<script setup>
import axios from "@axios"
import { returnedDraftNote } from '@/utils/requestApproval'
import { noteLikelihoodAnswer, placeholderWhile } from '@/utils/likelihoodVisibility'
import { SPEND_GUIDANCE, SPEND_GUIDANCE_PARTS, SPEND_GUIDANCE_QUESTION, showsSpendGuidance } from '@/utils/spendGuidance'
import { NOTES_GUIDANCE, menuSite, showsNotesGuidance } from '@/utils/notesGuidance'
import { showMedianSpend } from '@/utils/medianSpend'
import { mediaUrl } from '@core/utils/mediaUrl'
import lodash from "underscore"
import moment from 'moment-timezone'
import { useDesignSystem } from '@/composables/useDesignSystem'

import { ref, nextTick, computed, watch, onUnmounted } from "vue"
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useReservationsStore, useRestaurantsStore, useUsersStore, usePreferredStatusStore } from '@/stores'
import CmsText from '@/@core/components/CmsText.vue'
import RestaurantTitleBlock from '@core/components/RestaurantTitleBlock.vue'
import { useInstantBookingSlotsStore } from '@/stores/instantBookingSlots.store'
import {numberWithCommas, generateGeneralReservationUrlWithQueryString, addAndToString, buildProfileUrl, getFullUrl} from '@/plugins/misc'
import MemberVerifiedTags from '@core/components/MemberVerifiedTags.vue'
import MemberProfileCard from '@core/components/MemberProfileCard.vue'
import { requiredValidator } from '@validators'

import noImage from "@/assets/images/ui/no_restaurant_image_placeholder-360px.jpg"
import logoLargeLight from '@/assets/logo.svg'
import googleAttribution from "@/assets/images/ui/google_on_white.png"
import Tooltip from '@/layouts/components/Tooltip.vue'
import AppDateTimePicker from '@core/components/AppDateTimePicker.vue'
import GooglePlacesService from '@/services/GooglePlacesService'
import AddRestaurantDialog from '@core/components/AddRestaurantDialog.vue'
import PreferredRequestInfoDialog from '@core/components/preferredRequestInfoDialog.vue'
import PreferredTabsDialog from '@core/components/PreferredTabsDialog.vue'
import RestaurantsInfoDialog from '@core/components/RestaurantsInfoDialog.vue'
import MemberBookingCard from '@core/components/MemberBookingCard.vue'
import InstantBookingSlotsCompact from '@core/components/InstantBookingSlotsCompact.vue'
import InstantBookingCheckoutDialog from '@core/components/InstantBookingCheckoutDialog.vue'
import UpgradeProfileDialog from '@core/components/UpgradeProfileDialog.vue'
import EditReservationFavoriteDialog from '@core/components/EditReservationDialog/EditReservationFavoriteDialog.vue'
import EditReservationRequestPreviewDialog from '@core/components/EditReservationDialog/EditReservationRequestPreviewDialog.vue'
import EditReservationPaymentStep from '@core/components/EditReservationDialog/EditReservationPaymentStep.vue'
import RequestPaymentInfo from '@core/components/EditReservationDialog/RequestPaymentInfo.vue'
import EditReservationReviewStep from '@core/components/EditReservationDialog/EditReservationReviewStep.vue'
import { projectedRequestExpirationLabel as projectedRequestExpirationLabelUtil } from '@core/utils/requestExpiration'
import EditReservationPrivateDining3bStep from '@core/components/EditReservationDialog/EditReservationPrivateDining3bStep.vue'
import EditReservationPrivateDining4bStep from '@core/components/EditReservationDialog/EditReservationPrivateDining4bStep.vue'
import EditReservationPrivateDining5bStep from '@core/components/EditReservationDialog/EditReservationPrivateDining5bStep.vue'

// Card brand images for payment methods
import amexCard from '@/assets/images/payment_methods/amex.png'
import cirrusCard from '@/assets/images/payment_methods/cirrus.png'
import dinersCard from '@/assets/images/payment_methods/diners.png'
import discoverCard from '@/assets/images/payment_methods/discover.png'
import jcbCard from '@/assets/images/payment_methods/jcb.png'
import maestroCard from '@/assets/images/payment_methods/maestro.png'
import masterCard from '@/assets/images/payment_methods/mastercard.png'
import unionpayCard from '@/assets/images/payment_methods/unionpay.png'
import visaCard from '@/assets/images/payment_methods/visa.png'

const emit = defineEmits([
  'update:modelValue',
  'submit',

  // 'update:isDialogVisible'
])

const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth(450)

const cardBrandImages = {
  amex: amexCard,
  cirrus: cirrusCard,
  diners: dinersCard,
  discover: discoverCard,
  jcb: jcbCard,
  maestro: maestroCard,
  mastercard: masterCard,
  unionpay: unionpayCard,
  visa: visaCard
}

const router = useRouter()

// Initialize stores
const usersStore = useUsersStore()
const restaurantsStore = useRestaurantsStore()
const reservationsStore = useReservationsStore()

// Users Store
let myUser = null
let isRunningInIOSAppStore = null
let loginDialogMode = null
let loginDialogVerificationType = null
let isShowingPreferredRequestInfoDialog = null
let isShowingPreferredTabsDialog = null;
({myUser, isRunningInIOSAppStore, loginDialogMode, loginDialogVerificationType, isShowingPreferredRequestInfoDialog, isShowingPreferredTabsDialog} = storeToRefs(usersStore))

// Reservations Store
let editingReservation = null
let myReservations = null
let saving = null
let justBookedReservationsByRestaurant = null
let restaurantStatsByRestaurant = null;
({editingReservation, myReservations, saving, justBookedReservationsByRestaurant, restaurantStatsByRestaurant} = storeToRefs(reservationsStore))

// RM-150: when the actor is booking on behalf of someone else (group admin,
// personal admin), gate membership-dependent UI on the OWNER's status — not
// the actor's. editingReservation.owner is auto-populated server-side
// (autoOwner hooks in models/Reservation.js); fall back to myUser for
// self-bookings where the lean POST response doesn't carry an owner.
//
// RM-154 follow-up: the server sometimes returns a partial owner object
// (e.g. just `email`/`_id` without `isPlatinum`/`isPreferred`/etc), and the
// truthy `||` short-circuit was bypassing myUser even on self-bookings,
// leaving `reservationOwner.isPlatinum` undefined and rendering the
// non-Platinum "View Additional Request Insights" CTA at Platinum members.
// Repro: select non-Preferred restaurant → click a Preferred from the
// alt-list → auto-save fires → POST/PUT round trips return a lean owner
// stub from `reservation.toObject()` (autoOwner pre-save populates _id/email
// without membership fields), and a plain `{...me, ...owner}` spread lets
// the stub's missing/explicit-false fields overwrite myUser's truthy ones.
//
// Now:
// - No owner → myUser (fresh draft, no round trip yet)
// - Owner has no identity (no email, no _id) → treat as missing, use myUser
// - Owner identifies the actor (self-booking, with case/whitespace-normalized
//   email comparison and string-cast _id comparison so ObjectId vs string
//   doesn't trip us up) → merge BUT for boolean membership flags use a
//   truthy-wins union: if either myUser or owner says Platinum, the result
//   says Platinum. myUser is the freshest source for the actor's live
//   session state (just-renewed subscription, etc.), so we never gate
//   Platinum features off due to a stale owner snapshot.
// - Owner is a different person (genuine booking-on-behalf) → use owner
//   as-is. myUser would be the wrong subject and would leak the admin's
//   status into UI gates intended for the owner.
// RM-150 follow-up: cache a booking-on-behalf member's membership fields the first
// time we see a fully-populated owner (the GET in newDraftReservation / reopen),
// keyed by _id|email. Later POST/PUT round-trips return a lean owner stub that
// drops these fields; reservationOwner re-merges from this cache so Platinum-gated
// UI (Member Bookings, acceptance-rate stats) doesn't disappear on auto-save.
const ownerMembershipCache = ref({})

const ownerCacheKey = owner => String(owner?._id || owner?.email || '').toLowerCase().trim()

const isSelfBooking = (owner, me) => {
  if (!me) return false
  const ownerEmail = String(owner.email || '').toLowerCase().trim()
  const meEmail = String(me.email || '').toLowerCase().trim()

  return (!!ownerEmail && ownerEmail === meEmail)
    || (!!owner._id && !!me._id && String(owner._id) === String(me._id))
}

// Owner is a different person (genuine booking-on-behalf, e.g. a personal/group
// admin requesting for a member). We can't recover membership from myUser here —
// that's the actor, not the subject. But the auto-save PUT round-trip returns a
// lean owner stub (no isPlatinum/isPreferred — see the note on reservationOwner),
// which would make owner-gated UI like Member Bookings vanish after the first save.
// Backfill the membership fields from the cache captured on the fully-populated GET
// so the owner's Platinum status (and the bookings/stats it unlocks) stays stable.
const backfillOwnerMembership = owner => {
  const cached = ownerMembershipCache.value[ownerCacheKey(owner)]
  if (!cached) return owner

  return {
    ...cached,
    ...owner,
    isPlatinum: owner.isPlatinum ?? cached.isPlatinum,
    isPreferred: owner.isPreferred ?? cached.isPreferred,
    activeSubscription: owner.activeSubscription ?? cached.activeSubscription,
    platinumRenewalDate: owner.platinumRenewalDate ?? cached.platinumRenewalDate,
    premiumRenewalDate: owner.premiumRenewalDate ?? cached.premiumRenewalDate,
    membershipType: owner.membershipType ?? cached.membershipType,
  }
}

const reservationOwner = computed(() => {
  const owner = editingReservation.value?.owner
  const me = myUser.value
  if (!owner || (!owner.email && !owner._id)) return me || {}

  if (isSelfBooking(owner, me)) {
    return {
      ...me,
      ...owner,
      isPlatinum: !!(owner.isPlatinum || me.isPlatinum),
      isPreferred: !!(owner.isPreferred || me.isPreferred),
      activeSubscription: owner.activeSubscription || me.activeSubscription,
    }
  }

  return backfillOwnerMembership(owner)
})

watch(
  () => editingReservation.value?.owner,
  owner => {
    // Only cache once the populated owner actually carries membership data; a lean
    // stub (email/_id only) must not overwrite a previously-cached good record.
    if (!owner || (owner.isPlatinum === undefined && owner.isPreferred === undefined)) return

    const key = ownerCacheKey(owner)
    if (!key) return

    ownerMembershipCache.value = {
      ...ownerMembershipCache.value,
      [key]: {
        isPlatinum: owner.isPlatinum,
        isPreferred: owner.isPreferred,
        activeSubscription: owner.activeSubscription,
        platinumRenewalDate: owner.platinumRenewalDate,
        premiumRenewalDate: owner.premiumRenewalDate,
        membershipType: owner.membershipType,
      },
    }
  },
  { immediate: true },
)

// Restaurants Store
let restaurants = null
let restaurantSearchResults = null
let viewingDetailsRestaurant = null
let viewingDetailsRestaurantTab = null;
({restaurants, restaurantSearchResults, viewingDetailsRestaurant, viewingDetailsRestaurantTab} = storeToRefs(restaurantsStore))

// Instant Booking Slots Store
const instantBookingSlotsStore = useInstantBookingSlotsStore()

// Refs
const isShowingRequestPreviewDialog = ref(false)
const isShowingFavoriteRestaurantDialog = ref(false)
const onFavoriteDialogContinue = () => {
  isShowingFavoriteRestaurantDialog.value = false
  reservationsStore.saveEditReservation(true)
  changesMade.value = false
  tab.value = '5p' // land on spend/payment (spend moved off 6a)
}
const isRestaurantsInfoDialogOpen = ref(false)
const selectedRestaurantsObjectArray = ref([])
const reservationData = editingReservation
const tab = ref(null)
const isDialogVisible = ref(false)
const isConfirmDialogOpen = ref(false)
const changesMade = ref(false)
const platinumRequested = ref(false)
const availableInstantBookingSlots = ref(0)

// Upgrade Profile Dialog state
const showUpgradeDialog = ref(false)
const upgradeProductId = computed(() => {
  const isExtension = reservationOwner.value?.isPlatinum && reservationOwner.value?.platinumRenewalDate && moment(reservationOwner.value.platinumRenewalDate).isAfter(moment())

  return isExtension ? 'individual-1cycle-annual-extension' : 'individual-1cycle-annual'
})
const upgradePriceDollars = computed(() => {
  const isExtension = reservationOwner.value?.isPlatinum && reservationOwner.value?.platinumRenewalDate && moment(reservationOwner.value.platinumRenewalDate).isAfter(moment())

  return isExtension ? 200 : 300
})
const openUpgradeDialog = () => {
  reservationsStore.saveEditReservation()
  showUpgradeDialog.value = true
}
const openPlatinumMembersDialog = () => {
  if (reservationOwner.value && !reservationOwner.value.isPlatinum) {
    openUpgradeDialog()

    return
  }

  loginDialogMode.value = 'activate'
}
const onUpgradeDialogComplete = async () => {
  showUpgradeDialog.value = false
  await usersStore.getMyUser()
}
const onUpgradeDialogCancel = () => {
  showUpgradeDialog.value = false
}

const scrollableDiv = ref(null)
const addRestaurantButton = ref(null)
const scrollableDivScrollLeft = ref(0)
const buttonExtendedWidth = ref(0)
const rightMostScrollValue = ref(0)
const scrollableDivThreshold = ref(0)
const scrolledLeft = ref(false)
const scrolledRight = ref(false)

const recentBookingsTab = ref(0)

// Preferred restaurants section (shown when selected restaurant is not preferred)
const preferredRestaurantsTab = ref('trending') // 'trending' or 'alpha'
const preferredRestaurantsForDialog = ref([])
const loadingPreferredRestaurants = ref(false)
const loadingMorePreferredRestaurants = ref(false)
const preferredRestaurantsSkip = ref(0)
const hasMorePreferredRestaurants = ref(true)
const preferredRestaurantsEndRef = ref(null)
const PREFERRED_RESTAURANTS_PAGE_SIZE = 20
let preferredRestaurantsObserver = null

const checkingGeneralAvailability = ref(true)
const showNotesField = ref(false)

const gratuityArray = ref([])

const form = ref()
const valid = ref(true)

const comboBoxRef = ref(null)
const currentSearchTerm = ref('')


const gettingPlace = ref(false)
const fetchingAutocomplete = ref(false)

const searchMode = ref(false)
const loadingRestaurantData = ref(false)
const hasSearched = ref(false)
const isAddRestaurantDialogOpen = ref(false)
const searchQuery = ref('')

// Instant booking checkout state
const showInstantBookingCheckout = ref(false)
const selectedInstantBookingSlotId = ref(null)

// Instant booking slots by date (for general reservation flow)
const instantBookingSlotsByDate = ref([])
const loadingInstantBookingSlots = ref(false)

const paymentStepRef = ref(null)

const positionCalendarBelowInput = (instance, customPositionElement) => {
  const calendarContainer = instance?.calendarContainer
  const positionElement = customPositionElement || instance?._positionElement || instance?._input

  if (!calendarContainer || !positionElement)
    return

  const inputBounds = positionElement.getBoundingClientRect()
  const top = window.pageYOffset + inputBounds.top + positionElement.offsetHeight + 2
  const minLeft = window.pageXOffset + 8
  const maxLeft = window.pageXOffset + window.innerWidth - calendarContainer.offsetWidth - 8
  const left = Math.min(Math.max(window.pageXOffset + inputBounds.left, minLeft), Math.max(minLeft, maxLeft))

  calendarContainer.style.top = `${top}px`
  calendarContainer.style.left = `${left}px`
  calendarContainer.style.right = 'auto'
  calendarContainer.classList.remove('arrowBottom', 'arrowCenter', 'arrowRight')
  calendarContainer.classList.add('arrowTop', 'arrowLeft')
}

const editReservationDatePickerConfig = {
  disableMobile: true,
  altInput: true,
  altFormat: 'D n/j',
  dateFormat: 'Y-m-d',
  maxDate: moment().add(2, 'year').format('YYYY-MM-DD'),
  position: positionCalendarBelowInput,
  formatDate: (date, format) => {
    // Only customize the display (altFormat) — leave the data format (Y-m-d) untouched
    if (format === 'Y-m-d') {
      return moment(date).format('YYYY-MM-DD')
    }
    const m = moment(date)
    const today = moment().startOf('day')
    const tomorrow = moment().add(1, 'day').startOf('day')
    const dayLabel = m.format('ddd M/D')
    if (m.isSame(today, 'day')) return `Today ${dayLabel}`
    if (m.isSame(tomorrow, 'day')) return `Tomorrow ${dayLabel}`
    
    return dayLabel
  },
}

// Fetch instant booking slots for the selected date and market
const fetchInstantBookingSlotsForDate = async () => {
  if (!editingReservation.value?.date) {
    instantBookingSlotsByDate.value = []
    
    return
  }

  // Get market from selected restaurant or editing reservation
  const market = selectedRestaurantsObjectArray.value?.[0]?.market || editingReservation.value?.market

  loadingInstantBookingSlots.value = true
  try {
    const params = {
      date: editingReservation.value.date,
      status: 'available'
    }
    if (market) {
      params.city = market
    }

    const res = await axios.get('/instantBookingSlot/', { params })

    // Filter out expired slots
    const now = new Date()
    instantBookingSlotsByDate.value = (res.data.slots || []).filter(slot => {
      return slot.expirationTime ? new Date(slot.expirationTime) >= now : true
    })
  } catch (error) {
    console.error('Error fetching instant booking slots for date:', error)
    instantBookingSlotsByDate.value = []
  } finally {
    loadingInstantBookingSlots.value = false
  }
}

// Group instant booking slots by restaurant
const instantBookingSlotsByRestaurant = computed(() => {
  const grouped = {}

  instantBookingSlotsByDate.value.forEach(slot => {
    const restaurantId = slot.restaurantId
    if (!grouped[restaurantId]) {
      grouped[restaurantId] = {
        restaurantId,
        restaurantName: slot.restaurantName,
        restaurantMarket: slot.restaurantMarket,
        restaurant: slot.restaurant,
        slots: []
      }
    }
    grouped[restaurantId].slots.push(slot)
  })

  // Sort slots within each restaurant by time, then deduplicate by date/time/partySize
  Object.values(grouped).forEach(restaurant => {
    restaurant.slots.sort((a, b) => a.time.localeCompare(b.time))

    // Deduplicate slots with same date, time, and party size
    const seen = new Set()
    restaurant.slots = restaurant.slots.filter(slot => {
      const key = `${slot.date}_${slot.time}_${slot.partySize}`
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      
      return true
    })
  })

  return Object.values(grouped).sort((a, b) =>
    a.restaurantName.localeCompare(b.restaurantName)
  )
})

// Compute date context subtitle (Today/Tomorrow/This Weekend/Next Week/Month)
const dateContextSubtitle = computed(() => {
  if (!editingReservation.value?.date) return ''

  const reservationDate = moment(editingReservation.value.date).startOf('day')
  const today = moment().startOf('day')
  const tomorrow = moment().add(1, 'day').startOf('day')
  const endOfThisWeekend = moment().clone().endOf('week') // Sunday
  const endOfNextWeek = moment().clone().add(1, 'week').endOf('week')

  if (reservationDate.isSame(today, 'day')) {
    return 'Request for Today'
  }
  if (reservationDate.isSame(tomorrow, 'day')) {
    return 'Request for Tomorrow'
  }
  if (reservationDate.isAfter(today) && reservationDate.isSameOrBefore(endOfThisWeekend)) {
    return 'Request for This Weekend'
  }
  if (reservationDate.isAfter(endOfThisWeekend) && reservationDate.isSameOrBefore(endOfNextWeek)) {
    return 'Request for Next Week'
  }

  return 'Request for Weeks Out'
})

// Format time for instant booking slots
const formatInstantBookingTime = timeStr => {
  if (!timeStr) return ''
  const parsed = moment(timeStr, ['HH:mm', 'H:mm'], true)
  if (!parsed.isValid()) return timeStr

  return parsed.format('h:mm A')
}

// Handler for instant booking slot selection
const handleInstantBookingSlot = slotId => {
  console.log('🎫 handleInstantBookingSlot called with slotId:', slotId)
  console.log('🎫 Current editingReservation:', editingReservation.value?.id)
  console.log('🎫 Current selectedRestaurantsObjectArray length:', selectedRestaurantsObjectArray.value?.length)
  selectedInstantBookingSlotId.value = slotId
  showInstantBookingCheckout.value = true
  console.log('🎫 Set showInstantBookingCheckout to true')
}

// Handler for instant booking success
const handleInstantBookingSuccess = () => {
  // Dialog handles navigation to /bookings
}

// Handler for instant booking slots loaded
const handleInstantBookingSlotsLoaded = count => {
  availableInstantBookingSlots.value = count
}

// Reset instant booking slots count when selected restaurant changes
watch(
  () => selectedRestaurantsObjectArray.value[0]?._id,
  () => {
    availableInstantBookingSlots.value = 0
    fetchBookingDataForRestaurants()
  }
)

// Reactive flag to control watcher execution and prevent circular updates while in loop below
const isUpdating = ref(false)

// Flag to prevent handleRestaurantSelection from overwriting handleRestaurantItemClick
const justHandledItemClick = ref(false)

// Flag to skip the preferredTime watcher's reset of earliestTime/latestTime on initial dialog open
// (e.g. when opening an agent-created draft that already has these values set)
const skipNextTimeReset = ref(false)

// Flag: dialog was opened via agent handoff (openAtStep). When true, always load payment
// methods at step 8a even if restaurant data hasn't finished loading yet.
const openedViaAgentHandoff = ref(false)

// ========== Preferred Member Status ==========
// Use the shared preferredStatus store (loaded on app start in App.vue).
// `loadPreferredStatus()` short-circuits when the cache is fresh (5-min TTL),
// so calling it on dialog open / restaurant-selection change is cheap and keeps
// the spend-optional UI in sync with server-authoritative bypass decisions.
const preferredStatusStore = usePreferredStatusStore()

const loadMyPreferredStatus = () => {
  if (!myUser.value?._id) return
  preferredStatusStore.loadPreferredStatus()
}

const isPreferredAtSelectedRestaurant = computed(() =>
  selectedRestaurantsObjectArray.value.some(
    r => r._id && preferredStatusStore.isPreferredAt(r._id)
  )
)

const preferredRestaurantNames = computed(() =>
  selectedRestaurantsObjectArray.value
    .filter(r => r._id && preferredStatusStore.isPreferredAt(r._id))
    .map(r => r.name)
)

// ========== End Preferred Member Status ==========

//update selectedRestaurantsObjectArray when editingReservationDialog is opened
watch(
  () => editingReservation?.value,
  async (currentValue, previousValue) => {
    // Check if the previous value was null and the current value is not
    // Also handle switching directly from one reservation to another (both truthy but different)
    const isNewReservation = (previousValue === null && currentValue) ||
      (previousValue && currentValue && previousValue._id !== currentValue._id)

    if(isNewReservation) {
      // If the reservation already has earliestTime/latestTime (e.g. agent-created draft),
      // prevent the preferredTime watcher from clearing them on initial load
      if (currentValue.earliestTime && currentValue.latestTime) {
        skipNextTimeReset.value = true
      }

      // IMPORTANT: Use currentValue (the value that triggered the watcher) instead of editingReservation.value
      // to avoid race conditions where the reactive ref hasn't fully synchronized yet
      if (!currentValue.preferredRestaurant || !currentValue.market) {
        selectedRestaurantsObjectArray.value = []
        searchMode.value = true

        return // Early exit for new draft without restaurant
      }

      // Immediately set searchMode to false when we have a restaurant to load
      // This prevents the search combobox from showing while we fetch the restaurant
      searchMode.value = false

      // Quick cache check without blocking.
      // Try by ID first (most reliable, especially for agent-created drafts),
      // then fall back to name + market matching.
      const prefId = currentValue.preferredRestaurantId
      const cachedRestaurant = restaurants.value?.find(restaurant =>
        (prefId && (restaurant._id === prefId || restaurant.id === prefId)) ||
        (restaurant.name == currentValue.preferredRestaurant &&
           restaurant.market == currentValue.market)
      )

      if (restaurantHasBookingFields(cachedRestaurant)) {
        // Fast path: use cached data immediately
        console.log(`✅ Using cached restaurant: ${currentValue.preferredRestaurant}`)
        selectedRestaurantsObjectArray.value = [cachedRestaurant]
        loadingRestaurantData.value = false
        if (cachedRestaurant._id || cachedRestaurant.id) {
          restaurantsStore.recordProfileView(cachedRestaurant._id || cachedRestaurant.id)
        }
      } else {
        // Slow path: need to fetch
        // Create a placeholder restaurant object immediately so selectedRestaurantsObjectArray.length > 0
        // This prevents the search combobox from showing while loading
        const placeholderRestaurant = {
          name: currentValue.preferredRestaurant,
          market: currentValue.market,
          timeZoneIdentifier: currentValue.timeZoneIdentifier, // Include timezone for search filtering
          _loading: true // Flag to indicate this is a placeholder
        }
        selectedRestaurantsObjectArray.value = [placeholderRestaurant]
        loadingRestaurantData.value = true
        console.log(`⏳ Fetching restaurant: ${currentValue.preferredRestaurant} (id: ${prefId || 'none'})`)

        try {
          // Prefer ID-based lookup (reliable for agent drafts and reopened reservations),
          // then fall back to name + market lookup.
          let fetchedRestaurant = null
          if (prefId) {
            fetchedRestaurant = await restaurantsStore.getRestaurantById(prefId)
          }
          if (!fetchedRestaurant) {
            fetchedRestaurant = await restaurantsStore.getSingleRestaurant(
              currentValue.preferredRestaurant,
              currentValue.market
            )
          }

          if (fetchedRestaurant) {
            // Replace placeholder with actual restaurant
            selectedRestaurantsObjectArray.value = [fetchedRestaurant]
            if (fetchedRestaurant._id || fetchedRestaurant.id) {
              restaurantsStore.recordProfileView(fetchedRestaurant._id || fetchedRestaurant.id)
            }
          } else {
            // If fetch failed, remove placeholder and show search
            selectedRestaurantsObjectArray.value = []
            searchMode.value = true
          }
        } catch (error) {
          console.error('Failed to fetch restaurant:', error)

          // On error, remove placeholder and show search
          selectedRestaurantsObjectArray.value = []
          searchMode.value = true
        } finally {
          loadingRestaurantData.value = false
        }
      }

      // Handle alternate restaurants if present
      if (currentValue.alternateRestaurants && currentValue.market) {
        for (let i = 0; i < currentValue.alternateRestaurants.length; i++) {
          let theRestaurant = restaurants.value?.find(restaurant =>
            (restaurant.name == currentValue.alternateRestaurants[i] &&
               restaurant.market == currentValue.market)
          )

          if (!theRestaurant) {
            theRestaurant = await restaurantsStore.getSingleRestaurant(
              currentValue.alternateRestaurants[i],
              currentValue.market
            )
          }

          if (theRestaurant) {
            selectedRestaurantsObjectArray.value.push(theRestaurant)
          }
        }
      }
    }
    else if(previousValue && currentValue === null) {
      selectedRestaurantsObjectArray.value = []
      searchMode.value = false
      loadingRestaurantData.value = false
      openedViaAgentHandoff.value = false
    }
  },
)

// Helper function to fetch booking data for restaurants
// Optimized to be truly non-blocking and defer heavy work
const fetchBookingDataForRestaurants = () => {
  // Tabs that show restaurant cards with member bookings:
  // - tab '1': Restaurant selection (shows member bookings when no instant booking slots)
  // - tab '2': Private dining restaurant selection (shows cards)
  // - tab '3a': General reservation restaurant selection (shows card after selection)
  // - tab '6a': Review screen with recent bookings section
  const tabsNeedingBookingData = ['1', '2', '3a', '6a']

  if (tabsNeedingBookingData.includes(tab.value) && selectedRestaurantsObjectArray.value.length > 0) {
    // Defer fetching to avoid blocking initial render
    // Use requestIdleCallback for better performance, fallback to setTimeout
    const fetchData = () => {
      // Fetch booking data for selected restaurants in background
      for (const restaurant of selectedRestaurantsObjectArray.value) {
        if (restaurant) {
          // Refresh in the background so stats stay aligned with current server-side logic.
          reservationsStore.getJustBookedReservationsByRestaurant(
            restaurant.name,
            restaurant.market,
            { forceRefresh: true },
          ).catch(err => {
            console.error('Background booking fetch failed:', err)
          })
        }
      }
    }

    if (window.requestIdleCallback) {
      requestIdleCallback(fetchData, { timeout: 1000 })
    } else {
      setTimeout(fetchData, 100)
    }
  }
}

// Track the market for which preferred restaurants were fetched
const preferredRestaurantsFetchedMarket = ref(null)

// Fetch preferred restaurants for non-preferred restaurant section
const fetchPreferredRestaurantsForDialog = async (market = null, loadMore = false) => {
  // Prevent concurrent requests
  if (loadingPreferredRestaurants.value || loadingMorePreferredRestaurants.value) return

  // If not loading more, check if we already have data for this market
  if (!loadMore && preferredRestaurantsForDialog.value.length > 0 && preferredRestaurantsFetchedMarket.value === market) {
    return // Already fetched for this market
  }

  // Reset state for new market or initial load
  if (!loadMore) {
    preferredRestaurantsSkip.value = 0
    hasMorePreferredRestaurants.value = true
    loadingPreferredRestaurants.value = true
  } else {
    // Don't load more if there's no more data
    if (!hasMorePreferredRestaurants.value) return
    loadingMorePreferredRestaurants.value = true
  }

  try {
    // Fetch preferred restaurants filtered by market, sorted by trending (popular)
    // We fetch in trending order and sort client-side for A-Z
    const params = {
      limit: PREFERRED_RESTAURANTS_PAGE_SIZE,
      skip: preferredRestaurantsSkip.value,
      sort: 'popular'
    }
    if (market) {
      params.market = market
    }
    const response = await axios.get('/restaurant/preferred', {
      params
    })
    const newRestaurants = response.data || []

    if (loadMore) {
      // Append to existing list
      preferredRestaurantsForDialog.value = [...preferredRestaurantsForDialog.value, ...newRestaurants]
    } else {
      // Replace list
      preferredRestaurantsForDialog.value = newRestaurants
    }

    preferredRestaurantsFetchedMarket.value = market
    preferredRestaurantsSkip.value += newRestaurants.length

    // Check if there are more results to load
    hasMorePreferredRestaurants.value = newRestaurants.length === PREFERRED_RESTAURANTS_PAGE_SIZE
  } catch (error) {
    console.error('Error fetching preferred restaurants:', error)
    if (!loadMore) {
      preferredRestaurantsForDialog.value = []
    }
  } finally {
    loadingPreferredRestaurants.value = false
    loadingMorePreferredRestaurants.value = false
  }
}

// Setup IntersectionObserver for infinite loading of preferred restaurants
const setupPreferredRestaurantsObserver = () => {
  if (preferredRestaurantsObserver) {
    preferredRestaurantsObserver.disconnect()
  }

  if (!preferredRestaurantsEndRef.value) return

  preferredRestaurantsObserver = new IntersectionObserver(
    entries => {
      const entry = entries[0]
      if (entry.isIntersecting && !loadingMorePreferredRestaurants.value && hasMorePreferredRestaurants.value) {
        const market = selectedRestaurantsObjectArray.value[0]?.market || null
        fetchPreferredRestaurantsForDialog(market, true)
      }
    },
    { rootMargin: '100px' }
  )

  preferredRestaurantsObserver.observe(preferredRestaurantsEndRef.value)
}

// Watch for changes to the end ref element
watch(preferredRestaurantsEndRef, newVal => {
  if (newVal) {
    setupPreferredRestaurantsObserver()
  }
})

// Cleanup observer on unmount
onUnmounted(() => {
  if (preferredRestaurantsObserver) {
    preferredRestaurantsObserver.disconnect()
  }
})

// Whether a restaurant object carries the reservation-specific fields needed to
// enforce the spend minimum (min-spend-per-person cutoffs) and to filter/display
// operating hours (full googlePlaceDetails.opening_hours). The Preferred/Favorited
// list endpoints use RESTAURANT_LIST_FIELDS, which omit both — so objects coming
// from those lists must be hydrated via the single/by-id endpoint before use.
const restaurantHasReservationFields = restaurant => {
  if (!restaurant || typeof restaurant !== 'object' || restaurant._loading) {
    return false
  }

  const hasMinSpend = Object.prototype.hasOwnProperty.call(restaurant, 'dinnerSunThruThursMinSpendPerPerson')
  const hasOpeningHours = !!restaurant.googlePlaceDetails?.opening_hours

  return hasMinSpend && hasOpeningHours
}

// Select a preferred restaurant - replaces current selection with the preferred one
const selectPreferredRestaurant = async restaurant => {
  if (!restaurant) return

  // Update the editingReservation with the new restaurant immediately (these
  // fields don't depend on the heavier reservation-specific data).
  if (editingReservation.value) {
    editingReservation.value.preferredRestaurant = restaurant.name
    editingReservation.value.preferredRestaurantId = restaurant._id || restaurant.id
    editingReservation.value.market = restaurant.market
    editingReservation.value.alternateRestaurants = []
    editingReservation.value.alternateRestaurantIds = []
    editingReservation.value.timeZoneIdentifier = restaurant.timeZoneIdentifier
  }

  // Exit search mode if active
  searchMode.value = false

  // RM-154: when a Preferred restaurant is selected from the intermediary
  // guidance screen's recommendations, return the member to the reservation
  // type selection screen. reservationType is preserved.
  if (tab.value === '1b') {
    tab.value = '1'
  }

  // The object from the Preferred list is lean (RESTAURANT_LIST_FIELDS) and is
  // missing min-spend cutoffs and full opening hours. Without hydrating, the
  // spend-minimum dropdown would offer sub-threshold options and the operating
  // hours would disappear. Hydrate to the full single record before using it.
  if (restaurantHasReservationFields(restaurant)) {
    selectedRestaurantsObjectArray.value = [restaurant]
  } else {
    // Show the chosen restaurant right away via a loading placeholder so the
    // search combobox stays closed while we fetch the full record.
    selectedRestaurantsObjectArray.value = [{ ...restaurant, _loading: true }]
    loadingRestaurantData.value = true

    try {
      const prefId = restaurant._id || restaurant.id
      let fetchedRestaurant = null
      if (prefId) {
        fetchedRestaurant = await restaurantsStore.getRestaurantById(prefId)
      }
      if (!restaurantHasReservationFields(fetchedRestaurant)) {
        fetchedRestaurant = await restaurantsStore.getSingleRestaurant(
          restaurant.name,
          restaurant.market,
        )
      }

      // Fall back to the lean object if the fetch failed, so the user still has
      // a selection (degraded, but better than an empty/stuck dialog).
      selectedRestaurantsObjectArray.value = [fetchedRestaurant || restaurant]
    } catch (error) {
      console.error('Failed to hydrate preferred restaurant:', error)
      selectedRestaurantsObjectArray.value = [restaurant]
    } finally {
      loadingRestaurantData.value = false
    }
  }

  // Refresh bookings and stats for the newly selected restaurant.
  reservationsStore.getJustBookedReservationsByRestaurant(
    restaurant.name,
    restaurant.market,
    { forceRefresh: true },
  ).catch(err => {
    console.error('Failed to fetch member bookings for preferred restaurant:', err)
  })
}

// Fetch booking data when user navigates to tabs where it's displayed
// Defer execution to avoid blocking tab change
watch(() => tab.value, (newTab, oldTab) => {
  console.log('🕐 tab watcher - changing from', oldTab, 'to', newTab, '- earliestTime:', editingReservation.value?.earliestTime, 'latestTime:', editingReservation.value?.latestTime)

  // Only fetch if actually changing tabs (not initial mount)
  if (oldTab !== undefined) {
    // Defer to allow UI to update first
    nextTick(() => {
      console.log('🕐 tab watcher nextTick - tab:', newTab, '- earliestTime:', editingReservation.value?.earliestTime, 'latestTime:', editingReservation.value?.latestTime)
      fetchBookingDataForRestaurants()
    })
  }

  // RM-154: on entering the intermediary request-guidance screen,
  // auto-expand Member Bookings (Preferred restaurants) and the
  // Preferred Restaurants alt-list (non-Preferred restaurants).
  // Also fetch the YTD Preferred acceptance rate aggregate (cached).
  if (newTab === '1b') {
    showBookingsList.value = true
    memberFavoriteStatsTab.value = 'preferred'
    reservationsStore.getPreferredAcceptanceRateYtd()
  }
})


// Debug watcher to track earliestTime changes
watch(() => editingReservation.value?.earliestTime, (newVal, oldVal) => {
  console.log('🕐 earliestTime CHANGED from', oldVal, 'to', newVal, '- tab:', tab.value)
  if (newVal === null && oldVal !== null) {
    console.trace('🕐 earliestTime set to null - STACK TRACE:')
  }
})

// Debug watcher to track latestTime changes
watch(() => editingReservation.value?.latestTime, (newVal, oldVal) => {
  console.log('🕐 latestTime CHANGED from', oldVal, 'to', newVal, '- tab:', tab.value)
  if (newVal === null && oldVal !== null) {
    console.trace('🕐 latestTime set to null - STACK TRACE:')
  }
})

// Fetch booking data when restaurants are selected (on relevant tabs)
watch(
  () => selectedRestaurantsObjectArray.value.length,
  () => {
    // Small delay to ensure restaurant data is fully loaded
    setTimeout(fetchBookingDataForRestaurants, 50)
  }
)

// Fetch booking data when instant booking slots finish loading and there are none
// This ensures Member Bookings data is ready when the section would be shown
watch(
  () => availableInstantBookingSlots.value,
  newCount => {
    if (newCount === 0 && tab.value === '1' && selectedRestaurantsObjectArray.value.length > 0) {
      fetchBookingDataForRestaurants()
    }
  }
)

const isScrolledIntoButton = computed(() => {
  return rightMostScrollValue.value > buttonExtendedWidth.value/3
})

const isScrollToLeft = computed(() => {

  return scrolledLeft.value
})

const isScrollToRight = computed(() => {
  return scrolledRight.value
})

const showRestaurantDetails = restaurant => {
  console.log("showRestaurantDetails", restaurant)
  viewingDetailsRestaurantTab.value = "Details"

  // Signal that bookings should be auto-expanded if there are any
  const hasBookings = justBookedReservationsByRestaurant?.value?.[restaurant.market]?.[restaurant.name]?.length > 0
  console.log("Has bookings:", hasBookings, "for", restaurant.name)
  if (hasBookings) {
    console.log("Setting viewingDetailsRestaurantShowBookings to true")
    restaurantsStore.viewingDetailsRestaurantShowBookings = true
  }
  viewingDetailsRestaurant.value = restaurant
}

const getPlaceAsync = async placeId => {
  if (!placeId) {
    console.error(`[GooglePlaces]: placeId is required.`)

    // we return null to use either the fallback image or the user uploaded images
    return null
  }

  gettingPlace.value = true

  try {
    const placesService = new GooglePlacesService()
    
    return await placesService.getPlaceDetails(placeId)
  } catch (error) {
    console.error(`[GoogleMap]: Failed to get place details`, error)

    // we return null to use either the fallback image or the user uploaded images
    return null
  } finally {
    gettingPlace.value = false
  }
}

const fetchAutocomplete = lodash.throttle(async input => {
  // Only update searchQuery for the combobox, but preserve currentSearchTerm
  // if we have valid search results. This prevents results from disappearing
  // when Vuetify's combobox clears its internal search state after a delay.
  searchQuery.value = input
  
  // Only clear currentSearchTerm if input is explicitly empty AND we don't have results
  // This prevents the dropdown from closing when combobox clears its internal state
  if (!input || input.length < 2) {
    // Only clear if we don't have existing results, or if input is truly empty
    if (!restaurantSearchResults.value || restaurantSearchResults.value.length === 0) {
      currentSearchTerm.value = ''
      restaurantSearchResults.value = []
      hasSearched.value = false
    }
    fetchingAutocomplete.value = false
    
    return
  }
  
  // Update currentSearchTerm when we have valid input
  currentSearchTerm.value = input
  hasSearched.value = true
  fetchingAutocomplete.value = true
  
  // Determine if we should filter by market/timezone at database level
  // Only filter when on type selection screen AND restaurant is already selected
  const isOnTypeSelectionScreen = (tab.value == '2' || tab.value == '3a')
  let market = null
  let timeZoneIdentifier = null
  
  if (isOnTypeSelectionScreen && selectedRestaurantsObjectArray.value.length > 0) {
    const reservationType = editingReservation.value?.reservationType
    const firstRestaurant = selectedRestaurantsObjectArray.value[0]
    
    if (reservationType == 'private') {
      market = firstRestaurant?.market ||
               editingReservation.value?.market ||
               null

      // For private dining, get timezone from first selected restaurant directly
      // This is more reliable than using editingReservation.value.timeZoneIdentifier
      // which might not be set yet if the restaurant was just selected
      timeZoneIdentifier = firstRestaurant?.timeZoneIdentifier || 
                          editingReservation.value?.timeZoneIdentifier ||
                          null
      
      // Debug logging to help troubleshoot timezone filtering
      if (!timeZoneIdentifier && firstRestaurant) {
        console.warn('⚠️ Private dining search: First restaurant missing timeZoneIdentifier', {
          restaurant: firstRestaurant.name,
          restaurantData: firstRestaurant
        })
      }
    } else if (editingReservation.value?.market) {
      // For general reservations, filter by market
      market = editingReservation.value.market
    }
  }
  
  // Search internal restaurants only (no more Google Places API)
  // Pass market/timezone filters to backend for database-level filtering
  try {
    // Debug logging to help troubleshoot timezone filtering
    if (timeZoneIdentifier) {
      console.log(`🔍 Searching restaurants with timezone filter: ${timeZoneIdentifier}`, {
        firstRestaurant: selectedRestaurantsObjectArray.value[0]?.name,
        timeZoneFromRestaurant: selectedRestaurantsObjectArray.value[0]?.timeZoneIdentifier,
        timeZoneFromEditingReservation: editingReservation.value?.timeZoneIdentifier
      })
    }
    
    await restaurantsStore.searchRestaurants(input, market, timeZoneIdentifier)
    
    // Log search results for debugging
    if (timeZoneIdentifier && restaurantSearchResults.value?.length > 0) {
      console.log(`✅ Found ${restaurantSearchResults.value.length} restaurants in timezone ${timeZoneIdentifier}`)
    } else if (timeZoneIdentifier && restaurantSearchResults.value?.length === 0) {
      console.warn(`⚠️ No restaurants found in timezone ${timeZoneIdentifier}. Check if restaurants have timeZoneIdentifier set.`)
    }
  } catch (error) {
    console.error("Error searching restaurants:", error)
  } finally {
    fetchingAutocomplete.value = false
  }
}, 400)  // Increased to 400ms to reduce API calls and improve performance


const filteredSearchResults = computed(() => {
  if (!currentSearchTerm.value || currentSearchTerm.value.length < 2) {
    return []
  }
  
  // Early return if no search results
  // Filtering is now done at database level, so we can return results directly
  if (!restaurantSearchResults.value || restaurantSearchResults.value.length === 0) {
    return []
  }
  
  // Results are already filtered by market/timezone at database level when applicable
  // No need for additional JavaScript filtering - this improves performance
  return restaurantSearchResults.value
})


const fuzzyFilter =  (searchString, query) => {
  if (!query) return true

  const normalize = s => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  const searchNormalized = normalize(searchString || '')
  const queryNormalized = normalize(query)

  // Check if the search string contains the query (diacritics-insensitive)
  return searchNormalized.includes(queryNormalized)
}

const requestedRestaurants = computed(() => {
  let restaurantsArray = []
  if(editingReservation.value.preferredRestaurant) {
    restaurantsArray.push(editingReservation.value.preferredRestaurant)
  }
  if(editingReservation.value.alternateRestaurants) {
    restaurantsArray = restaurantsArray.concat(editingReservation.value.alternateRestaurants)
  }
  restaurantsArray.sort( function(a,b){
    if ( a.toLowerCase() < b.toLowerCase() ){
      return -1
    }
    if ( a.toLowerCase() > b.toLowerCase() ){
      return 1
    }
    
    return 0
  } )
  
  return restaurantsArray.join(', ')
})


const restaurantsForSelectedCity = computed(() => {
  let returnArray = []
  restaurants.value.forEach(function (restaurant) {
    if (!editingReservation.value.market || (restaurant.market && (restaurant.market == editingReservation.value.market))) {
      if (!restaurant.acceptingReferrals) {
        returnArray.push({text: restaurant.name + "**", value: restaurant.name, status: restaurant.status, timeZone: restaurant.timeZone, acceptingReferrals: restaurant.acceptingReferrals, market: restaurant.market})
      }
      else if (restaurant.status == "Member Favorite") {
        returnArray.push({text: restaurant.name + "*", value: restaurant.name, status: restaurant.status, timeZone: restaurant.timeZone, acceptingReferrals: restaurant.acceptingReferrals, market: restaurant.market})
      }
      else if (restaurant.status == "Preferred") {
        returnArray.push({text: restaurant.name, value: restaurant.name, status: restaurant.status, timeZone: restaurant.timeZone, acceptingReferrals: restaurant.acceptingReferrals, market: restaurant.market})
      }
    }
  })
  
  return returnArray
})

const selectedRestaurantsNotPreferred = computed(() => {
  const nonPreferredRestaurants = selectedRestaurantsObjectArray.value
    .filter(restaurant => restaurant.status !== "Preferred")
    .map(restaurant => restaurant.name)

  return nonPreferredRestaurants.length ? nonPreferredRestaurants : false
})

const selectedRestaurantsNotAcceptingReferrals = computed(() => {
  const notAcceptingReferrals = selectedRestaurantsObjectArray.value
    .filter(restaurant => !restaurant.acceptingReferrals)
    .map(restaurant => restaurant.name)

  return notAcceptingReferrals.length ? notAcceptingReferrals : false
})

// RM-154: also fetch the YTD aggregate when the member is stuck on step 1
// with a not-accepting restaurant — the inline fallback block on step 1
// renders the same Preferred Restaurants alt-list intro and needs the value.
// Declared here (immediately after the computed it watches) to avoid the
// temporal-dead-zone trap when the watcher captures its initial source value.
// Also default-expand the "Preferred Restaurants" toggle pill so the
// suggestions are visible immediately; member can collapse to dismiss.
watch(() => !!selectedRestaurantsNotAcceptingReferrals.value, isBlocked => {
  if (isBlocked) {
    reservationsStore.getPreferredAcceptanceRateYtd()
    memberFavoriteStatsTab.value = 'preferred'
  }
}, { immediate: true })

const recentBookingsForSelectedRestaurant = computed(() => {
  if (selectedRestaurantsObjectArray.value.length === 0) return []
  const restaurant = selectedRestaurantsObjectArray.value[0]
  console.log("CALCULATING MEMBER BOOKINGS: ",justBookedReservationsByRestaurant.value)
  
  return justBookedReservationsByRestaurant?.value?.[restaurant.market]?.[restaurant.name] || []
})

const selectedRestaurantPerformanceStats = computed(() => {
  if (selectedRestaurantsObjectArray.value.length === 0) return null
  const restaurant = selectedRestaurantsObjectArray.value[0]

  return restaurantStatsByRestaurant?.value?.[restaurant.market]?.[restaurant.name]
})

const showBookingsList = ref(false)
const showNonPreferredPreferredList = ref(false)
const memberFavoriteStatsTab = ref(null)

// RM-359 (Kapil 10/30): "ask RM" in the spend step's guidance opens Ask RM for feedback
const spendAskRm = ref(null)

// RM-359 (Kapil 10/30): the restaurant's site at the end of the notes guidance's first item
const notesMenuSite = computed(() => menuSite(selectedRestaurantsObjectArray.value[0]?.website))

// Restaurant key stats for selected restaurant
const selectedRestaurantStats = computed(() => {
  if (selectedRestaurantsObjectArray.value.length === 0) return null
  const restaurant = selectedRestaurantsObjectArray.value[0]

  // Response time label (falls back to 24 hours when no recent data)
  const performanceStats = selectedRestaurantPerformanceStats.value
  const medianResponseTimeHours = performanceStats?.medianResponseTimeHours ?? null
  // Expose the hours figure ("12 hours") separately from the label so the
  // template can bold just the number.
  let responseTimeLabel = null
  let responseTimeValue = null
  if (medianResponseTimeHours != null) {
    const h = Number(medianResponseTimeHours)
    responseTimeValue = h <= 2 ? '2 hours' : (h <= 6 ? '6 hours' : '12 hours')
  } else if (performanceStats !== undefined) {
    responseTimeValue = '24 hours'
  }
  if (responseTimeValue) responseTimeLabel = `Response time*: generally within ${responseTimeValue}`

  const bookings3m = restaurant.last3MonthsReservationCount || 0
  const acceptanceRate = performanceStats?.acceptanceRate ?? null
  const acceptanceRateDisplay = acceptanceRate != null
    ? Math.round(Number(acceptanceRate))
    : null
  const avgResponseTimeHours = performanceStats?.avgResponseTimeHours ?? null
  const avgSpendPerPerson = performanceStats?.avgSpendPerPerson ?? null
  const medianSpendPerPerson = performanceStats?.medianSpendPerPerson ?? null
  const avgLeadTimeDays = restaurant.avgLeadTimeDays

  const hasStats = !!(
    responseTimeLabel
    || bookings3m > 0
    || acceptanceRateDisplay != null
    || avgSpendPerPerson != null
    || medianSpendPerPerson != null
    || avgLeadTimeDays != null
  )

  return {
    name: restaurant.name,
    responseTimeLabel,
    responseTimeValue,
    bookings3m,
    acceptanceRate: acceptanceRateDisplay,
    avgResponseTimeHours,
    medianResponseTimeHours,
    avgSpendPerPerson,
    medianSpendPerPerson,
    avgLeadTimeDays,
    hasStats,
  }
})

const showRestaurantKeyStats = computed(() => {
  return selectedRestaurantStats.value?.hasStats === true
})

const showPreferredRestaurantNoBookingsMessage = computed(() => {
  const selectedRestaurant = selectedRestaurantsObjectArray.value[0]

  return selectedRestaurant?.status === 'Preferred'
    && Number(selectedRestaurantStats.value?.acceptanceRate) === 0
})

const showViewBookings = computed(() => {
  // RM-150: gate on the OWNER's membership (not the actor's) so the bookings
  // toggle shows correctly when a group/personal admin books on behalf of a member.
  return recentBookingsForSelectedRestaurant.value.length > 0 && reservationOwner.value?.isPlatinum
})

// Preferred restaurants sorted by trend score (highest first)
const preferredRestaurantsByTrendScore = computed(() => {
  if (!preferredRestaurantsForDialog.value || preferredRestaurantsForDialog.value.length === 0) return []
  
  // Tiebreak chain mirrors Discover Trending: score → favoriteCount → newest-added.
  // (These are all Preferred, so the Preferred-status step is a no-op here.)
  return [...preferredRestaurantsForDialog.value].sort((a, b) => {
    const trendScoreDiff = (b.trendingScore || 0) - (a.trendingScore || 0)
    if (trendScoreDiff !== 0) return trendScoreDiff

    const favDiff = (b.favoriteCount || 0) - (a.favoriteCount || 0)
    if (favDiff !== 0) return favDiff

    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0

    return bTime - aTime
  })
})

// Displayed preferred restaurants based on tab selection
const displayedPreferredRestaurants = computed(() => {
  if (!preferredRestaurantsForDialog.value || preferredRestaurantsForDialog.value.length === 0) {
    return []
  }

  let restaurants = [...preferredRestaurantsForDialog.value]

  if (preferredRestaurantsTab.value === 'alpha') {
    // Sort A-Z by name
    restaurants.sort((a, b) => a.name.localeCompare(b.name))
  }

  // For 'trending', we keep the default order from the API (sorted by reservation count)

  return restaurants
})

// Check if selected restaurant should show preferred restaurant alternatives
const showPreferredRestaurantsSection = computed(() => {
  if (selectedRestaurantsObjectArray.value.length === 0) return false
  const restaurant = selectedRestaurantsObjectArray.value[0]

  return restaurant.status !== 'Preferred' || restaurant.acceptingReferrals === false
})

const showPreferredRestaurantsInsightsPanel = computed(() => {
  return (
    !!selectedRestaurantsObjectArray?.value?.[0]?.name
    && editingReservation.value?.reservationType !== 'private'
    && (
      !!selectedRestaurantsNotAcceptingReferrals.value
      || (!myUser?.value && !!selectedRestaurantsNotPreferred.value)

      // RM-150: gate on the OWNER's Platinum status, not the actor's. When a
      // group/personal admin books on behalf of a Platinum member while the
      // actor is non-Platinum, this panel (and its Next CTA) must still render
      // on the non-Preferred step 1b — otherwise the sibling fallback panel
      // (also owner-gated) is hidden too and the member is dead-ended with no
      // way to proceed. Owner-gating keeps the whole 1b step consistent.
      || (!!reservationOwner.value?.isPlatinum && !!selectedRestaurantsNotPreferred.value)
    )
  )
})

// Fetch preferred restaurants when a restaurant should show preferred alternatives
// Watch for changes in the selected restaurant to get the market
watch(
  [() => showPreferredRestaurantsSection.value, () => selectedRestaurantsObjectArray.value[0]?.market],
  ([shouldShow, market]) => {
    if (shouldShow && market) {
      // Fetch preferred restaurants for the same market as the selected restaurant
      fetchPreferredRestaurantsForDialog(market)
    }
  },
  { immediate: true }
)

const selectedRestaurantsWithUnverifiedContactInfo = computed(() => {
  const unverifiedRestaurants = selectedRestaurantsObjectArray.value
    .filter(restaurant => restaurant.contactInfoNotVerified)
    .map(restaurant => restaurant.name)

  return unverifiedRestaurants.length ? unverifiedRestaurants : false
})

const hasFavoriteNotPreferredWithLowSpend = computed(() => {
  // Check if any selected restaurant is a "Member Favorite" (favorite but not Preferred)
  const hasMemberFavorite = selectedRestaurantsObjectArray.value.some(
    restaurant => restaurant.status === "Member Favorite"
  )
  
  // Only validate if minimum spend is set
  if (!editingReservation.value.minSpendPerPerson) {
    return false
  }
  
  // Check if minimum spend is less than $200
  const hasLowSpend = editingReservation.value.minSpendPerPerson < 200
  
  return hasMemberFavorite && hasLowSpend
})

const restaurantsWithLowSpend = computed(() => {
  // Only validate if minimum spend is set
  if (!editingReservation.value.minSpendPerPerson) {
    return []
  }

  // Check if minimum spend is less than $200
  const hasLowSpend = editingReservation.value.minSpendPerPerson < 200

  if (!hasLowSpend) {
    return []
  }

  // Get restaurants that are "Member Favorite" (not Preferred)
  return selectedRestaurantsObjectArray.value
    .filter(restaurant => restaurant.status === "Member Favorite")
    .map(restaurant => restaurant.name)
})

// Pricing breakdown computed for guaranteed spend display
const pricingBreakdown = computed(() => {
  if (!editingReservation.value?.minSpendPerPerson || !editingReservation.value?.partySize) {
    return null
  }

  const minSpendPerPerson = editingReservation.value.minSpendPerPerson
  const partySize = editingReservation.value.partySize
  const baseSpend = minSpendPerPerson * partySize
  const taxPercentage = 12
  const tipPercentage = 20
  const tax = baseSpend * (taxPercentage / 100)
  const tip = baseSpend * (tipPercentage / 100)
  const total = baseSpend + tax + tip

  return {
    minSpendPerPerson,
    partySize,
    baseSpend,
    tax,
    taxPercentage,
    tip,
    tipPercentage,
    total
  }
})

// Get the restaurant name(s) for display in guaranteed spend section
const selectedRestaurantNames = computed(() => {
  if (!selectedRestaurantsObjectArray.value?.length) {
    return 'the restaurant'
  }

  return selectedRestaurantsObjectArray.value.map(r => r.name).join(' / ')
})

// Get first restaurant name for payment info display
const firstRestaurantName = computed(() => {
  return selectedRestaurantsObjectArray.value?.[0]?.name || 'the restaurant'
})

//keeps restaurant.holdData updated for all the selected restaurants
watchEffect(async () => {
  if(editingReservation.value){
    selectedRestaurantsObjectArray.value.forEach(function (restaurant, i) {

      //if gratuityArray.value[i] doesn't exist, create it
      if(!gratuityArray.value?.[i]){
        gratuityArray.value[i] = 20
      }

      let holdData = {
        restaurantName: restaurant.name,
        cancellationLimitHours: restaurant.generalReservationHoldCancellationLimitHours || 24,
      }
      if(restaurant.generalReservationHoldRequired){
        holdData.type = restaurant.generalReservationHoldType
        holdData.foodAndBeverageSpend = editingReservation.value.partySize * editingReservation.value.minSpendPerPerson
        holdData.tax = (holdData.foodAndBeverageSpend * (restaurant.generalReservationHoldTaxPercentage ?? 12) / 100)
        holdData.perPersonHoldAmount = parseInt(restaurant.generalReservationHoldType.includes("pp") ? restaurant.generalReservationHoldType.replace("pp", "") : 0)
        holdData.gratuityAmount = holdData.foodAndBeverageSpend*gratuityArray.value?.[i]/100


        if(holdData.type.includes('full')){
          holdData.holdAmountDollars = ((holdData.foodAndBeverageSpend || 0) + (holdData.gratuityAmount || 0) + (holdData.tax || 0))
          let ccFee = (restaurant?.generalReservationCCFeePercentage ? (holdData.holdAmountDollars || 0) * restaurant.generalReservationCCFeePercentage/100 : 0)
          holdData.holdAmountDollars = (holdData.holdAmountDollars + ccFee).toFixed(2)
        } else if(holdData.type.includes("pp")) {
          holdData.holdAmountDollars = (editingReservation.value.partySize * holdData.perPersonHoldAmount).toFixed(2)
        }

      }
      console.log("holdData", holdData)
      restaurant.holdData = holdData


    })
  }

})

const dateSelectedPastPreferredMembershipTerm = computed(() => {
  if(reservationOwner.value?.premiumRenewalDate){
    let newRequestPreferredMoment = moment.tz(editingReservation.value.date + ' ' + editingReservation.value.preferredTime, 'YYYY-MM-DD h:mma', timeZone.value.identifier)

    //this might not be right... need look at how timezone works with premiumRenewalDate and paidThroughDate etx
    let premiumRenewalDateMoment = moment.tz(reservationOwner.value?.premiumRenewalDate, 'YYYY-MM-DD', 'America/Chicago')

    return newRequestPreferredMoment.isSameOrAfter(premiumRenewalDateMoment)
  }

  return null
})

const dateSelectedPastPlatinumMembershipTerm = computed(() => {
  if(reservationOwner.value?.platinumRenewalDate){
    let newRequestPreferredMoment = moment.tz(editingReservation.value.date + ' ' + editingReservation.value.preferredTime, 'YYYY-MM-DD h:mma', timeZone.value.identifier)

    //this might not be right... need look at how timezone works with premiumRenewalDate and paidThroughDate etx
    let platinumRenewalDateMoment = moment.tz(reservationOwner.value?.platinumRenewalDate, 'YYYY-MM-DD', 'America/Chicago')

    return newRequestPreferredMoment.isSameOrAfter(platinumRenewalDateMoment)
  }

  return null
})

const isTrialAndAlreadyHasAReservation = computed(() => {
  let qualifyingReservations = []

  //only show if main dialog is open and user is on trial
  if (isDialogVisible.value && myUser?.value?.platinumSubscriptionsByState?.current?.planId.includes('-1free') ||
    isDialogVisible.value && !myUser?.value?.platinumSubscriptionsByState?.current && !myUser?.value?.platinumSubscriptionsByState?.current && myUser?.value?.platinumSubscriptionsByState?.lastActive?.planId.includes('-1free') ) {
    qualifyingReservations = myReservations.value.filter(function (el) {
      return el.status == "Awaiting Response from Restaurant" || el.status == "Pending RM Review" || el.status == "Submitted" || el.status == "Accepted" || el.status == "Completed" || el.status == "Canceled"
    })
  }
  if (qualifyingReservations.length > 0) {
    return true
  } else {
    return false
  }
})

// RM-281/RM-182 — the early conflict warning is answered by the server, not
// computed locally. The old client-side filter read the cached myReservations
// list (stale after account switches, empty until a bookings page loaded) and
// its matching had drifted from the enforced rule, so members could sail past
// this step and only hit the conflict at submit. GET /reservation/conflict-check
// runs the SAME shared matcher as the submit backstop (lib/reservationConflicts),
// including the allowConcurrentRequests exemption for the reservation owner.
const serverConflicts = ref([])
let conflictCheckTimer = null
let conflictCheckSeq = 0
const refreshConflictCheck = () => {
  clearTimeout(conflictCheckTimer)
  const date = editingReservation.value?.date
  const time = editingReservation.value?.preferredTime
  if (!date || !time) {
    serverConflicts.value = []

    return
  }
  conflictCheckTimer = setTimeout(async () => {
    const seq = ++conflictCheckSeq
    try {
      const res = await axios.get('/reservation/conflict-check', {
        params: { date, time, excludeId: editingReservation.value?.id || editingReservation.value?._id },
      })

      if (seq === conflictCheckSeq) serverConflicts.value = res.data?.conflicts || []
    } catch {
      // Advisory only — the submit backstop still enforces the rule.
      if (seq === conflictCheckSeq) serverConflicts.value = []
    }
  }, 250)
}

watch(
  () => [editingReservation.value?.date, editingReservation.value?.preferredTime, editingReservation.value?.id],
  refreshConflictCheck,
  { immediate: true },
)

const conflictingPendingRequest = computed(() => serverConflicts.value.find(c => c.type === 'pending') || false)

const conflictingConfirmedRequest = computed(() => serverConflicts.value.find(c => c.type === 'confirmed') || false)

const missingProfileFields = computed(() => {
  const missing = []
  if (!myUser.value) return missing
  
  if (!myUser.value.phoneNumber) missing.push('Phone number')
  if (!myUser.value.profileImageId) missing.push('Profile Pic')
  
  if (!myUser.value.verified) {
    if (!myUser.value.company) missing.push('Company')
    if (!myUser.value.title) missing.push('Position/Title')
  }
  
  return missing
})

const isProfileIncomplete = computed(() => {
  return missingProfileFields.value.length > 0
})

const profileValidationMessage = computed(() => {
  if (missingProfileFields.value.length === 0) return ''
  
  return `Additional details required to submit request: ${missingProfileFields.value.join(', ')}`
})

const selectedRestaurants = computed(() => {
  let returnArray = []
  if (restaurants.value && editingReservation.value) {
    if (selectedRestaurantsObjectArray?.value?.length > 0)
      returnArray = returnArray.concat(selectedRestaurantsObjectArray.value)
  }
  
  return returnArray

})

const selectedRestaurantsObjectArrayAlphabetical = computed(() => {
  let returnArray = []
  if (selectedRestaurantsObjectArray?.value?.length > 0) {
    returnArray = selectedRestaurantsObjectArray.value.sort((a, b) => a.name.localeCompare(b.name))
  }
  
  return returnArray
})

const timeZone = computed(() => {
  return selectedRestaurants?.value?.[0]?.timeZone || restaurantsForSelectedCity?.value?.[0]?.timeZone || editingReservation?.value?.timeZone || {identifier: 'America/Chicago', abbreviation: 'CT'}

})


let seatingPreferenceOptionsBasedOnReservationType = computed(() => {
  if (editingReservation.value.reservationType == "private") {
    // RM-401 (Kapil, 10/14): multiple choice — every type that works; nothing selected = no preference
    return ['One Large Table', 'Multiple Round', 'Cocktail Style']
  } else if (editingReservation.value.reservationType == "general") {
    return ['Indoor only', 'Indoor preferred but patio works too', 'Patio only', 'Patio preferred but indoor works too']
  }
})

//reverse order of above array
let roomPreferenceOptions = computed(() => {
  // Multiple choice since 10/21 (the product agent and Kapil): every room type that works, joined ("Private, Semi-Private"); older values still read (client/src/utils/inquiryPreferences.js).
  return ["Private", "Semi-Private", "Main Dining"]
})

let partySizeOptions = computed(() => {
  let returnArray = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]
  if (editingReservation.value.reservationType == "private") {
    returnArray = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100, 150, 200, 250, 500, 750, 1000]
  } else {
    let smallestMaxPartySize = 14
    selectedRestaurants.value.forEach(function (restaurant) {
      if (restaurant.maxGeneralReservationPartySize < smallestMaxPartySize)
        smallestMaxPartySize = restaurant.maxGeneralReservationPartySize
    })

    returnArray = returnArray.filter((x, i) => x <= smallestMaxPartySize)
  }

  if (!returnArray.includes(editingReservation.value.partySize)) {
    editingReservation.value.partySize = null
  }
  
  return returnArray
})

let spendOptionsPlaceholder = computed(() => {
  return 'Guaranteed F&B Spend'
})
let minSpendOptions = computed(() => {
  let returnArray = [
    {text: '$3,000pp', value: 3000},
    {text: '$2,500pp', value: 2500},
    {text: '$2,000pp',value: 2000},
    {text: '$1,500pp', value: 1500},
    {text: '$1,000pp', value: 1000},
    {text: '$900pp', value: 900},
    {text: '$800pp',value: 800},
    {text: '$750pp', value: 750},
    {text: '$700pp', value: 700},
    {text: '$650pp', value: 650},
    {text: '$600pp',value: 600},
    {text: '$550pp', value: 550},
    {text: '$500pp', value: 500},
    {text: '$450pp', value: 450},
    {text: '$400pp',value: 400},
    {text: '$375pp', value: 375},
    {text: '$350pp', value: 350},
    {text: '$300pp', value: 300},
    {text: '$250pp',value: 250},
    {text: '$200pp', value: 200},
    {text: '$175pp', value: 175},
    {text: '$150pp', value: 150},
    {text: '$125pp',value: 125},
    {text: '$100pp', value: 100},
    {text: '$75pp', value: 75},
    {text: '$50pp', value: 50}
  ]

  //reverse order of returnArray fully written out
  returnArray = returnArray.reverse()

  let highestBrunchLunchMinSpendPerPerson = 0
  let highestDinnerSunThruThursMinSpendPerPerson = 0
  let highestDinnerFriAndSatMinSpendPerPerson = 0
  let highestDinnerMinSpendPerPerson = 0

  if (editingReservation.value.reservationType == "private") {
    selectedRestaurants.value.forEach(function (restaurant) {
      if (restaurant.privateBrunchLunchMinSpendPerPerson > highestBrunchLunchMinSpendPerPerson)
        highestBrunchLunchMinSpendPerPerson = restaurant.privateBrunchLunchMinSpendPerPerson
      if (restaurant.privateDinnerSunThruThursMinSpendPerPerson > highestDinnerSunThruThursMinSpendPerPerson)
        highestDinnerSunThruThursMinSpendPerPerson = restaurant.privateDinnerSunThruThursMinSpendPerPerson
      if (restaurant.privateDinnerFriAndSatMinSpendPerPerson > highestDinnerFriAndSatMinSpendPerPerson)
        highestDinnerFriAndSatMinSpendPerPerson = restaurant.privateDinnerFriAndSatMinSpendPerPerson
    })
  } else {
    selectedRestaurants.value.forEach(function (restaurant) {
      if (restaurant.brunchLunchMinSpendPerPerson > highestBrunchLunchMinSpendPerPerson)
        highestBrunchLunchMinSpendPerPerson = restaurant.brunchLunchMinSpendPerPerson
      if (restaurant.dinnerSunThruThursMinSpendPerPerson > highestDinnerSunThruThursMinSpendPerPerson)
        highestDinnerSunThruThursMinSpendPerPerson = restaurant.dinnerSunThruThursMinSpendPerPerson
      if (restaurant.dinnerFriAndSatMinSpendPerPerson > highestDinnerFriAndSatMinSpendPerPerson)
        highestDinnerFriAndSatMinSpendPerPerson = restaurant.dinnerFriAndSatMinSpendPerPerson
    })
  }

  if (selectedRestaurants.value.length < 1) {
    highestBrunchLunchMinSpendPerPerson = 50
    highestDinnerSunThruThursMinSpendPerPerson = 100
    highestDinnerFriAndSatMinSpendPerPerson = 100
  }

  if (moment(editingReservation.value.date).day() >= 5)
    highestDinnerMinSpendPerPerson = highestDinnerFriAndSatMinSpendPerPerson
  else
    highestDinnerMinSpendPerPerson = highestDinnerSunThruThursMinSpendPerPerson

  let cutoff = 0
  if (moment(editingReservation.value.preferredTime, "h:mma").isSameOrBefore(moment('3:00pm', "h:mma"))) {
    cutoff = highestBrunchLunchMinSpendPerPerson
  } else {
    cutoff = highestDinnerMinSpendPerPerson
  }
  returnArray = returnArray.filter((x, i) => x.value >= cutoff)

  let match = returnArray.filter((x, i) => x.value == editingReservation.value.minSpendPerPerson)

  if (match.length == 0 && editingReservation.value.minSpendPerPerson) {
    editingReservation.value.minSpendPerPerson = null
    if(editingReservation.value.reservationType == "general")
      tab.value="6a"
    else if(editingReservation.value.reservationType == "private")
      tab.value="4b"
  }

  if(editingReservation.value.partySize > 1){
    returnArray = returnArray.map(option => {
      let totalSpend = option.value * (editingReservation.value.partySize || 1) // Default party size to 1 if not set
      
      return {
        ...option,
        text: `$${option.value.toLocaleString()}pp | $${totalSpend.toLocaleString()} total`
      }
    })
  }

  return returnArray
})

let selectedRestaurantsMinSpend = computed(() => {
  let returnArray = []

  if (!editingReservation.value || !editingReservation.value.date || !editingReservation.value.preferredTime) {
    return returnArray
  }

  selectedRestaurants.value.forEach(function (restaurant) {
    let cutoff = null

    // Determine which fields to use based on reservation type
    if (editingReservation.value.reservationType == "private") {
      // Private dining: use private* fields
      if (moment(editingReservation.value.preferredTime, "h:mma").isSameOrBefore(moment('3:00pm', "h:mma"))) {
        cutoff = restaurant.privateBrunchLunchMinSpendPerPerson
      } else {
        const dayOfWeek = moment(editingReservation.value.date).day()
        if (dayOfWeek >= 5) {
          // Friday (5) or Saturday (6)
          cutoff = restaurant.privateDinnerFriAndSatMinSpendPerPerson
        } else {
          // Sunday (0) through Thursday (4)
          cutoff = restaurant.privateDinnerSunThruThursMinSpendPerPerson
        }
      }
    } else {
      // General/group dining: use general fields (brunchLunchMinSpendPerPerson, dinnerFriAndSatMinSpendPerPerson, dinnerSunThruThursMinSpendPerPerson)
      if (moment(editingReservation.value.preferredTime, "h:mma").isSameOrBefore(moment('3:00pm', "h:mma"))) {
        cutoff = restaurant.brunchLunchMinSpendPerPerson
      } else {
        const dayOfWeek = moment(editingReservation.value.date).day()
        if (dayOfWeek >= 5) {
          // Friday (5) or Saturday (6)
          cutoff = restaurant.dinnerFriAndSatMinSpendPerPerson
        } else {
          // Sunday (0) through Thursday (4)
          cutoff = restaurant.dinnerSunThruThursMinSpendPerPerson
        }
      }
    }

    // Handle undefined/null values - use 0 as default if the field doesn't exist
    // Check for both null and undefined explicitly
    const minSpendValue = (cutoff !== null && cutoff !== undefined && !isNaN(cutoff)) ? cutoff : 0
    
    // Debug logging to help troubleshoot
    if (minSpendValue === 0 && restaurant.name) {
      console.log(`⚠️ Min spend is 0 for ${restaurant.name}:`, {
        reservationType: editingReservation.value.reservationType,
        date: editingReservation.value.date,
        preferredTime: editingReservation.value.preferredTime,
        dayOfWeek: moment(editingReservation.value.date).day(),
        restaurantFields: {
          privateBrunchLunchMinSpendPerPerson: restaurant.privateBrunchLunchMinSpendPerPerson,
          privateDinnerFriAndSatMinSpendPerPerson: restaurant.privateDinnerFriAndSatMinSpendPerPerson,
          privateDinnerSunThruThursMinSpendPerPerson: restaurant.privateDinnerSunThruThursMinSpendPerPerson,
          brunchLunchMinSpendPerPerson: restaurant.brunchLunchMinSpendPerPerson,
          dinnerFriAndSatMinSpendPerPerson: restaurant.dinnerFriAndSatMinSpendPerPerson,
          dinnerSunThruThursMinSpendPerPerson: restaurant.dinnerSunThruThursMinSpendPerPerson,
        },
        cutoff
      })
    }
    
    returnArray.push({
      name: restaurant.name, 
      minSpend: minSpendValue, 
      generalReservationHoldType: (restaurant.generalReservationHoldRequired ? restaurant.generalReservationHoldType : null)
    })
  })

  //sort array by minSpend
  returnArray.sort((a, b) => {
    if (a.minSpend === b.minSpend) return 0
    
    return (a.minSpend < b.minSpend) ? -1 : 1
  })

  return returnArray
})

let selectedRestaurantsPartySizeLimits = computed(() => {
  let returnArray = []

  selectedRestaurants.value.forEach(function (restaurant) {
    let cutoff = 0

    if (editingReservation.value.reservationType == "private") {
      cutoff = restaurant.privateMaxPartySize
    } else{
      cutoff = restaurant.maxGeneralReservationPartySize
    }

    returnArray.push({name: restaurant.name, maxPartySize: cutoff})

  })

  //sort array by maxPartySize
  returnArray.sort((a, b) => (a.maxPartySize > b.maxPartySize) ? -1 : 1)

  return returnArray
})

let earliestBookingMoment = computed(() => {
  //limit to not allow reservations in the past or very near future

  //if not preferred we need 48hrs
  // if(preferredRestaurantNotPreferred.value || alternateRestaurantsNotPreferred.value){
  //   return moment.tz(timeZone.value.identifier).add(15, 'minutes').add(48, 'hours')
  // }
  // //else must be preferred restaurant selected
  // else{
  //   return moment.tz(timeZone.value.identifier).add(15, 'minutes').add(2, 'hours')
  // }
  return moment.tz(timeZone.value.identifier).add(30, 'minutes')//.add(1, 'hours');
})

let preferredTimeOptionsBasedOnReservationType = computed(() => {
  editingReservation.value.date

  let startTimeMoment
  let endTimeMoment
  const dayStart = moment.tz(editingReservation.value.date, timeZone.value.identifier).startOf('day')

  if (editingReservation.value.reservationType == "private") {
    // Private dining is multi-restaurant and not tied to any single restaurant's hours — allow any time (24hrs).
    startTimeMoment = moment(dayStart)
    endTimeMoment = moment(dayStart).add(23, 'hours').add(45, 'minutes')
  } else if (editingReservation.value.reservationType == "general") {
    // General is single-restaurant — honor that restaurant's operating hours for the selected date's day-of-week.
    const dayOfWeek = moment(editingReservation.value.date, 'YYYY-MM-DD').day()
    const restaurant = selectedRestaurantsObjectArray.value?.[0]
    const operatingWindow = restaurant ? getRestaurantOperatingWindow(restaurant, dayOfWeek) : null

    if (operatingWindow) {
      startTimeMoment = moment(dayStart).add(operatingWindow.openMinutes, 'minutes')
      endTimeMoment = moment(dayStart).add(operatingWindow.closeMinutes, 'minutes')
    } else {
      // Fallback when Google hours are unavailable — keep legacy 9am → 11:30pm window.
      startTimeMoment = moment(dayStart).set({ hour: 9, minute: 0 })
      endTimeMoment = moment(startTimeMoment).add(14, 'hours').add(30, 'minutes')
    }
  }

  if (
    moment.tz(editingReservation.value.date, timeZone.value.identifier)
      .isSame(moment.tz(minSelectableDate.value, timeZone.value.identifier), "day") &&
      startTimeMoment.isBefore(earliestBookingMoment.value)
  ) {
    startTimeMoment = earliestBookingMoment.value.minutes(Math.floor(earliestBookingMoment.value.minutes() / 15) * 15).seconds(0).milliseconds(0)
  }

  let newRange = generateTimeRange(startTimeMoment, endTimeMoment)

  // Filter by operating hours only for general — keeps multi-period gaps (e.g. lunch/dinner split) out of the list.
  // Private explicitly allows any time, so skip the filter.
  if (
    editingReservation.value.reservationType === 'general' &&
    editingReservation.value.date &&
    selectedRestaurantsObjectArray.value?.length
  ) {
    const dayOfWeek = moment(editingReservation.value.date, 'YYYY-MM-DD').day()
    const anyHasHours = selectedRestaurantsObjectArray.value.some(r => r.googlePlaceDetails?.opening_hours?.periods)
    if (anyHasHours) {
      newRange = newRange.filter(time => isTimeValidForAllRestaurants(time, dayOfWeek))
    }
  }

  if (!newRange.includes(editingReservation.value.preferredTime)) {
    editingReservation.value.preferredTime = null
  }

  return newRange
})

let preferredEndTimeOptions = computed(() => {

  if (editingReservation.value.reservationType == "private") {
    let startTimeMoment
    if (editingReservation.value.preferredTime) {
      // Start from 15 minutes after preferred start time to ensure end time is after start time
      startTimeMoment = moment(editingReservation.value.preferredTime, "h:mma").add(15, 'minutes')
    } else {
      startTimeMoment = moment('10:00am', "h:mma")
    }

    let endTimeMoment = moment(startTimeMoment).add(8, 'hours')

    let newRange = generateTimeRange(startTimeMoment, endTimeMoment)
    
    // Filter out any times that are before or equal to preferred start time
    if (editingReservation.value.preferredTime) {
      const preferredStartMoment = moment(editingReservation.value.preferredTime, "h:mma")
      newRange = newRange.filter(time => {
        const timeMoment = moment(time, "h:mma")
        
        return timeMoment.isAfter(preferredStartMoment)
      })
    }
    
    // Convert to objects with text and value properties for VSelect
    const options = newRange.map(time => ({
      text: time,
      value: time
    }))
    
    // If preferredEndTime is set and is less than or equal to preferredTime, reset it
    if (editingReservation.value.preferredEndTime && editingReservation.value.preferredTime) {
      const endTime = moment(editingReservation.value.preferredEndTime, "h:mma")
      const startTime = moment(editingReservation.value.preferredTime, "h:mma")
      if (endTime.isSameOrBefore(startTime)) {
        editingReservation.value.preferredEndTime = null
      }
    }
    
    // Check if current preferredEndTime is in the valid options
    const validValues = options.map(opt => opt.value)
    if (editingReservation.value.preferredEndTime && !validValues.includes(editingReservation.value.preferredEndTime)) {
      editingReservation.value.preferredEndTime = null
    }

    return options
  } else
    return []
})

let allTimeOptions = computed(() => {
  let startTimeMoment = moment('0:00am', "h:mma")
  let endTimeMoment = moment(startTimeMoment).add(23, 'hours').add(45, 'minutes')

  return generateTimeRange(startTimeMoment, endTimeMoment)
})

let earliestTimeOptionsBasedOnReservationType = computed(() => {
  console.log('🕐 earliestTimeOptionsBasedOnReservationType computed - reservationType:', editingReservation.value?.reservationType, 'current earliestTime:', editingReservation.value?.earliestTime)

  // Return empty array if date or preferredTime is not set to avoid "Invalid date"
  if (!editingReservation.value?.date || !editingReservation.value?.preferredTime) {
    return []
  }

  let startTimeMoment = moment.tz(editingReservation.value.date + ' ' + editingReservation.value.preferredTime, 'YYYY-MM-DD h:mma', timeZone.value.identifier).subtract(90, "minutes")
  if(startTimeMoment.isBefore(earliestBookingMoment.value)){
    startTimeMoment = earliestBookingMoment.value.minutes(Math.floor(earliestBookingMoment.value.minutes() / 15) * 15).seconds(0).milliseconds(0)
  }
  let endTimeMoment = moment.tz(editingReservation.value.date + ' ' + editingReservation.value.preferredTime, 'YYYY-MM-DD h:mma', timeZone.value.identifier)

  if(endTimeMoment.isBefore(earliestBookingMoment.value)){
    endTimeMoment = earliestBookingMoment.value.minutes(Math.floor(earliestBookingMoment.value.minutes() / 15) * 15).seconds(0).milliseconds(0)
  }

  let newRange = generateTimeRange(startTimeMoment, endTimeMoment)
  console.log('🕐 earliestTimeOptionsBasedOnReservationType - newRange length:', newRange.length, 'range:', newRange.slice(0, 3), '...', 'includes current:', newRange.includes(editingReservation.value.earliestTime))

  // Filter out times outside operating hours for all selected restaurants
  if (editingReservation.value.date && selectedRestaurantsObjectArray.value?.length) {
    const dayOfWeek = moment(editingReservation.value.date, 'YYYY-MM-DD').day()
    const anyHasHours = selectedRestaurantsObjectArray.value.some(r => r.googlePlaceDetails?.opening_hours?.periods)
    if (anyHasHours) {
      newRange = newRange.filter(time => isTimeValidForAllRestaurants(time, dayOfWeek))
    }
  }

  // Only clear value for general reservations, not private dining
  if (editingReservation.value.reservationType !== 'private' && !newRange.includes(editingReservation.value.earliestTime)) {
    console.log('🕐 earliestTimeOptionsBasedOnReservationType - CLEARING earliestTime (general reservation, not in range)')
    editingReservation.value.earliestTime = null
  }

  return newRange
})

let latestTimeOptionsBasedOnReservationType = computed(() => {
  console.log('🕐 latestTimeOptionsBasedOnReservationType computed - reservationType:', editingReservation.value?.reservationType, 'current latestTime:', editingReservation.value?.latestTime)

  // Return empty array if preferredTime is not set to avoid "Invalid date"
  if (!editingReservation.value?.preferredTime) {
    return []
  }

  let startTimeMoment = moment.tz(editingReservation.value.preferredTime, "h:mma", timeZone.value.identifier).add(15, 'minutes')
  let endTimeMoment = moment.tz(editingReservation.value.preferredTime, "h:mma", timeZone.value.identifier).add(90, "minutes")

  let newRange = generateTimeRange(startTimeMoment, endTimeMoment)
  console.log('🕐 latestTimeOptionsBasedOnReservationType - newRange length:', newRange.length, 'range:', newRange.slice(0, 3), '...', 'includes current:', newRange.includes(editingReservation.value.latestTime))

  // Filter out times outside operating hours for all selected restaurants
  if (editingReservation.value.date && selectedRestaurantsObjectArray.value?.length) {
    const dayOfWeek = moment(editingReservation.value.date, 'YYYY-MM-DD').day()
    const anyHasHours = selectedRestaurantsObjectArray.value.some(r => r.googlePlaceDetails?.opening_hours?.periods)
    if (anyHasHours) {
      newRange = newRange.filter(time => isTimeValidForAllRestaurants(time, dayOfWeek))
    }
  }

  // Only clear value for general reservations, not private dining
  if (editingReservation.value.reservationType !== 'private' && !newRange.includes(editingReservation.value.latestTime)) {
    console.log('🕐 latestTimeOptionsBasedOnReservationType - CLEARING latestTime (general reservation, not in range)')
    editingReservation.value.latestTime = null
  }

  return newRange
})

let restaurantIsInDifferentTimezone = computed(() => {

  // Get client's local time
  let localTime = moment()

  // Get the offset in minutes for local timezone and "America/Chicago"
  let localOffset = localTime.utcOffset()
  let chicagoOffset = moment.tz(timeZone.value.identifier).utcOffset()

  // Check if the timezone offset is different
  if(localOffset !== chicagoOffset) {
    return true
  } else {
    return false
  }
})

let isNotHost = computed(() => {
  if(!myUser.value)
    return false

  const ownerEmail = editingReservation?.value?.userPermissions?.[0]?.email
  if (!ownerEmail || myUser.value.email == ownerEmail)
    return false // booking for self — the host is me

  const isAdminActor = myUser.value.userLevel == 'admin'
    || myUser.value.primaryGroupPermission == 'admin'
    || myUser.value.primaryGroupPermission == 'adminStandard'
    || myUser.value.primaryGroupPermission == 'owner'

  // RM-159: a Platinum spouse submitting on behalf of their linked partner is also
  // "not the host" — the request is for the partner, so the confirmation copy and
  // profile-completion prompts must attribute to the partner, not the spouse.
  // The link is bidirectional: a primary holder points to their secondary via
  // secondaryAccountEmail; a secondary resolves the primary via the primaryAccount virtual.
  const spouseEmails = [
    myUser.value.secondaryAccountEmail,
    myUser.value.secondaryAccount?.email,
    myUser.value.primaryAccount?.email,
  ].filter(Boolean)
  const isSpouseActor = spouseEmails.includes(ownerEmail)

  return isAdminActor || isSpouseActor
})

let minSelectableDate = computed(() => {
  let returnDate

  // if(editingReservation?.value.status == 'Draft'){
  //   if(preferredRestaurantNotPreferred.value || alternateRestaurantsNotPreferred.value){
  //     returnDate = moment.tz(timeZone.value.identifier).add(2, 'days').format('YYYY-MM-DD')
  //   }
  //   else{
  returnDate = moment.tz(timeZone.value.identifier).format('YYYY-MM-DD')

  //   }
  // }
  return returnDate
})

// RM-162 — hours from now until the requested reservation time, in the restaurant timezone.
// Used to gate the 24h/48h request-expiration selector (only shown when > 24h out).
let hoursUntilReservation = computed(() => {
  const r = editingReservation?.value
  if (!r?.date || !r?.preferredTime) return null
  const tz = timeZone.value?.identifier || 'America/Chicago'
  const reservationMoment = moment.tz(`${r.date} ${r.preferredTime}`, 'YYYY-MM-DD h:mma', tz)
  if (!reservationMoment.isValid()) return null
  
  return reservationMoment.diff(moment(), 'hours', true)
})

// RM-162 — the expiration choice is offered for Preferred RESTAURANTS to all
// Platinum members. It is NOT gated on the member's per-restaurant Preferred
// status (that was the original bug). Matches the backend, which applies the
// 24h/48h logic when every selected restaurant has status 'Preferred'.
const allSelectedRestaurantsArePreferred = computed(() =>
  selectedRestaurantsObjectArray.value.length > 0
  && selectedRestaurantsObjectArray.value.every(r => r?.status === 'Preferred'),
)

const showRequestExpirationChoice = computed(() =>
  allSelectedRestaurantsArePreferred.value
  && reservationOwner.value?.isPlatinum === true
  && hoursUntilReservation.value != null
  && hoursUntilReservation.value > 24,
)

// Second ("longer window") option. When the reservation is 24-48h out, a 48h
// window would pass the reservation time, so the request actually lives until
// the time the member is seeking — so label it with that time instead of "48 hours".
// The stored value stays 48 either way; the backend caps it to the reservation time.
const requestExpirationLongOptionLabel = computed(() => {
  const h = hoursUntilReservation.value
  const r = editingReservation?.value
  if (h != null && h <= 48 && r?.date && r?.preferredTime) {
    const tz = timeZone.value?.identifier || 'America/Chicago'
    // Normalize the date — r.date may be a full datetime string, not YYYY-MM-DD.
    const dateOnly = moment(r.date).format('YYYY-MM-DD')
    const m = moment.tz(`${dateOnly} ${r.preferredTime}`, 'YYYY-MM-DD h:mma', tz)
    if (m.isValid()) return m.format('ddd, MMM D · h:mma')
  }

  return 'In 48 hours'
})

// RM-162 — projected request-expiration time, shown ALWAYS (even when the
// 24h/48h selector is hidden: Preferred-within-24h, or non-Preferred restaurants
// which run a fixed 48h window). Logic lives in the shared util (mirrors
// lib/reservationExpiration.js). The review step computes its own copy from the
// store so it never depends on a prop round-trip.
const projectedRequestExpirationLabel = computed(() =>
  projectedRequestExpirationLabelUtil(
    editingReservation?.value,
    selectedRestaurantsObjectArray.value,
    timeZone.value?.identifier,
  ),
)

// RM-161 — per-restaurant booking window (general reservations only).
// Most restrictive positive window among the selected restaurants; null = no limit.
let bookingWindowDays = computed(() => {
  if (editingReservation?.value?.reservationType !== 'general') return null
  const windows = selectedRestaurants.value
    .map(r => r?.reservationBookingWindowDays)
    .filter(n => typeof n === 'number' && n > 0)
  if (windows.length === 0) return null
  
  return Math.min(...windows)
})

// Latest selectable date = today + (N-1) in the restaurant timezone (inclusive of today).
let maxSelectableDate = computed(() => {
  const n = bookingWindowDays.value
  if (!n) return undefined
  const tz = timeZone.value?.identifier || 'America/Chicago'
  
  return moment.tz(tz).add(n - 1, 'days').format('YYYY-MM-DD')
})

let bookingWindowMessage = computed(() => {
  if (!maxSelectableDate.value) return ''
  const name = selectedRestaurants.value?.[0]?.name || 'This restaurant'
  
  return `${name} is accepting requests through ${moment(maxSelectableDate.value).format('ddd, MMM D')}.`
})

// Exact spec message surfaced when an out-of-window date is auto-cleared.
let bookingWindowClearedMessage = ref('')

// If the picked date falls outside the window (restaurant change or day rollover),
// clear it so the member must re-pick within the allowed range, and surface the
// exact "<restaurant> is not accepting requests for this date yet." message.
watch(maxSelectableDate, newMax => {
  if (!newMax) return
  const picked = editingReservation?.value?.date
  if (picked && moment(picked).isAfter(moment(newMax))) {
    editingReservation.value.date = null
    const name = selectedRestaurants.value?.[0]?.name || 'This restaurant'
    bookingWindowClearedMessage.value = `${name} is not accepting requests for this date yet.`
  }
})

// Clear the warning once the member picks a new (in-window) date.
watch(() => editingReservation?.value?.date, newDate => {
  if (newDate) bookingWindowClearedMessage.value = ''
})

const requestClose = async () => {
  if (myUser.value && editingReservation.value.status == 'Draft' && tab.value != '2c') {
    isConfirmDialogOpen.value = true
  } else {
    await usersStore.showLoadingOverlay()
    await reservationsStore.cancelEditReservation()
    await usersStore.hideLoadingOverlay()
  }
}

const closeWithSave = async () => {
  await usersStore.showLoadingOverlay()
  await reservationsStore.saveEditReservation()
  await usersStore.hideLoadingOverlay()
}

const closeWithDelete = async () => {
  await usersStore.showLoadingOverlay()
  await reservationsStore.deleteEditReservation()
  await usersStore.hideLoadingOverlay()
}

//limits to 1 when editingReservation.value.reservationType == 'general' and 10 when editingReservation.value.reservationType == 'private'
const generalRestaurantLimit = 1
const privateRestaurantLimit = 10

const restaurantSelectorLimit = computed(() => {
  if(editingReservation.value.reservationType == 'general'){
    return generalRestaurantLimit
  } else if(editingReservation.value.reservationType == 'private'){
    return privateRestaurantLimit
  }
})

// Computed property for remaining restaurants count in private dining
const remainingRestaurantsCount = computed(() => {
  if(editingReservation.value?.reservationType == 'private'){
    const selected = selectedRestaurantsObjectArray.value?.length || 0
    
    return Math.max(0, privateRestaurantLimit - selected)
  }
  
  return 0
})

// Computed property for placeholder text in private dining searchbar
const privateDiningPlaceholderText = computed(() => {
  if(tab.value == '2' && editingReservation.value?.reservationType == 'private'){
    const selected = selectedRestaurantsObjectArray.value?.length || 0
    if(selected === 0){
      return 'Select up to 10 favorites'
    } else {
      const remaining = remainingRestaurantsCount.value
      
      return `Select up to ${remaining} more`
    }
  }
  
  return undefined
})

// Computed property for message below restaurant cards
const privateDiningMessageText = computed(() => {
  if(tab.value == '2' && editingReservation.value?.reservationType == 'private'){
    const selected = selectedRestaurantsObjectArray.value?.length || 0
    if(selected === 0){
      return 'Select up to 10 favorites in the search box'
    } else {
      const remaining = remainingRestaurantsCount.value
      
      return `Select up to ${remaining} more favorites in the search box`
    }
  }
  
  return undefined
})

// Helper function to validate if an item is a valid restaurant object
const isValidRestaurant = item => {
  return item && typeof item === 'object' && (item.name || item._id || item.id)
}

const restaurantHasBookingFields = restaurant => {
  if (!restaurant || typeof restaurant !== 'object' || restaurant._loading) {
    return false
  }

  const requiredFields = [
    'generalReservationUrl',
    'generalReservationPlatform',
    'generalReservationHoldRequired',
    'generalReservationHoldType',
    'generalReservationHoldCancellationLimitHours',
    'maxGeneralReservationPartySize'
  ]

  return requiredFields.every(field => Object.prototype.hasOwnProperty.call(restaurant, field))
}

const limitPreferredRestaurant = () => {
  // Use requestAnimationFrame to defer non-critical work and allow UI to update first
  requestAnimationFrame(() => {
    // Immediately filter out any invalid entries (strings, null, undefined)
    if (selectedRestaurantsObjectArray?.value?.length > 0) {
      const validRestaurants = selectedRestaurantsObjectArray.value.filter(isValidRestaurant)
      
      // If we filtered out invalid entries, update immediately
      if (validRestaurants.length !== selectedRestaurantsObjectArray.value.length) {
        selectedRestaurantsObjectArray.value = validRestaurants
        
        return // Exit early to avoid further processing
      }
    }

    // Tab '1' or general reservations: limit to 1 restaurant
    // Tab '2' with private dining: limit to 10 restaurants
    if (selectedRestaurantsObjectArray?.value?.length > generalRestaurantLimit && (tab.value == '1' || editingReservation.value.reservationType == 'general')) {
      nextTick(() => selectedRestaurantsObjectArray.value = selectedRestaurantsObjectArray.value.slice(0, generalRestaurantLimit))
    } else if(selectedRestaurantsObjectArray?.value?.length > privateRestaurantLimit && editingReservation.value.reservationType == 'private'){
      nextTick(() => selectedRestaurantsObjectArray.value = selectedRestaurantsObjectArray.value.slice(0, privateRestaurantLimit))
    } else if(selectedRestaurantsObjectArray?.value?.length > 0){
      //blur when new selection is made - to get rid of keyboard on mobile (tab '1' or general reservations)
      if (tab.value == '1' || editingReservation.value.reservationType == 'general') {
        // Defer blur to avoid blocking
        setTimeout(() => {
          const inputElement = comboBoxRef.value?.$el?.querySelector('input')
          if (inputElement) {
            inputElement.blur()
          }
        }, 0)
      }
    }
  })
}

// Handle restaurant selection from combobox - filters out invalid entries
// CRITICAL: Must synchronously filter out strings before updating the array
// to prevent race conditions with watchEffect that accesses restaurant.holdData
const handleRestaurantSelection = value => {
  // Skip if handleRestaurantItemClick just ran - it already handled the update
  if (justHandledItemClick.value) {
    return
  }

  // SYNCHRONOUSLY filter out any invalid entries (strings, null, undefined)
  // This must happen before the array is updated to prevent errors in watchEffect
  if (Array.isArray(value)) {
    const validRestaurants = value.filter(isValidRestaurant)

    // Only update if we have valid restaurants or if we're clearing the selection
    selectedRestaurantsObjectArray.value = validRestaurants

    // If we filtered out invalid entries, close the dropdown
    if (validRestaurants.length !== value.length) {
      if (comboBoxRef.value) {
        const inputElement = comboBoxRef.value?.$el?.querySelector('input')
        if (inputElement) {
          inputElement.blur()
        }
      }
    }
  }

  // Clear search input after selection
  searchQuery.value = ''
  currentSearchTerm.value = ''
  hasSearched.value = false
  restaurantSearchResults.value = []

  // Clear and close the combobox - defer this for UI smoothness
  nextTick(() => {
    if (comboBoxRef.value) {
      const inputElement = comboBoxRef.value?.$el?.querySelector('input')
      if (inputElement) {
        inputElement.value = ''
        inputElement.blur()
      }
    }
  })

  // Process valid selection - defer limitPreferredRestaurant to avoid blocking
  requestAnimationFrame(() => {
    limitPreferredRestaurant()
  })
  changesMade.value = true
}

// Handler for dropdown item clicks - must be synchronous to ensure selection works
const handleRestaurantItemClick = restaurant => {
  console.log('🍽️ handleRestaurantItemClick called with:', restaurant?.name, 'isUpdating:', isUpdating.value)

  // Validate restaurant is a proper object
  if (!restaurant || typeof restaurant !== 'object' || !restaurant.name) {
    console.warn('handleRestaurantItemClick: Invalid restaurant object', restaurant)
    
    return
  }

  // If isUpdating is true from a previous selection, we need to reset it first
  // This can happen if the watcher's async processing hasn't completed yet
  if (isUpdating.value) {
    console.warn('handleRestaurantItemClick: isUpdating was true, resetting')
    isUpdating.value = false
  }

  // Set flag to prevent handleRestaurantSelection from overwriting our update
  justHandledItemClick.value = true

  // SYNCHRONOUSLY update the selection - do not defer with requestAnimationFrame
  // as the menu closes immediately and deferred updates can be lost

  // Defensive check: ensure the array is actually an array
  if (!Array.isArray(selectedRestaurantsObjectArray.value)) {
    console.error('🚨 selectedRestaurantsObjectArray.value is not an array!', selectedRestaurantsObjectArray.value)
    selectedRestaurantsObjectArray.value = []
  }

  const previousLength = selectedRestaurantsObjectArray.value.length
  console.log('🍽️ Current reservationType:', editingReservation.value.reservationType, 'tab:', tab.value)

  // Tab '1' or general reservations: single select (replace)
  // Tab '2' with private dining: multi-select (add to array)
  if (tab.value == '1' || editingReservation.value.reservationType == 'general') {
    selectedRestaurantsObjectArray.value = [restaurant]
  } else {
    // For private dining on tab '2', add to array if not already selected
    const alreadySelected = selectedRestaurantsObjectArray.value.some(r => r && r.name === restaurant.name && r.market === restaurant.market)
    console.log('🍽️ Already selected?', alreadySelected)
    if (!alreadySelected) {
      selectedRestaurantsObjectArray.value = [...selectedRestaurantsObjectArray.value, restaurant]
    }
  }
  console.log('🍽️ Array updated from', previousLength, 'to', selectedRestaurantsObjectArray.value.length, 'restaurants')

  // Clear search input after selection
  searchQuery.value = ''
  currentSearchTerm.value = ''
  hasSearched.value = false
  restaurantSearchResults.value = []

  // Clear and close the combobox - defer only the UI cleanup
  nextTick(() => {
    if (comboBoxRef.value) {
      const inputElement = comboBoxRef.value?.$el?.querySelector('input')
      if (inputElement) {
        inputElement.value = ''
        inputElement.blur()
      }
    }

    // Reset the flag after the next tick (after handleRestaurantSelection would have run)
    justHandledItemClick.value = false
  })

  // Defer only the non-critical work
  requestAnimationFrame(() => {
    limitPreferredRestaurant()
  })
  changesMade.value = true
}

// Handle Enter key press - prevent adding search text as a restaurant
// Optimized for INP performance
const handleEnterKey = event => {
  // Prevent default Enter behavior that might add search text to selection
  event.preventDefault()
  
  // Use requestAnimationFrame for immediate UI feedback
  requestAnimationFrame(() => {
    // If there are search results and menu is open, select the first result
    if (filteredSearchResults.value && filteredSearchResults.value.length > 0) {
      const firstResult = filteredSearchResults.value[0]
      handleRestaurantItemClick(firstResult)
      
      // Close the combobox - defer to avoid blocking
      setTimeout(() => {
        if (comboBoxRef.value) {
          const inputElement = comboBoxRef.value?.$el?.querySelector('input')
          if (inputElement) {
            inputElement.blur()
          }
        }
      }, 0)
    } else {
      // No results, just close the combobox
      if (comboBoxRef.value) {
        const inputElement = comboBoxRef.value?.$el?.querySelector('input')
        if (inputElement) {
          inputElement.blur()
        }
      }
    }
  })
}

const handleAddFavoriteClick = () => {
  // Close the combobox menu first
  if (comboBoxRef.value) {
    const inputElement = comboBoxRef.value?.$el?.querySelector('input')
    if (inputElement) {
      inputElement.blur()
    }
  }
  
  // Use nextTick to ensure the menu closes before opening the dialog
  nextTick(() => {
    // Check if user is logged in (not a dummy user and not null)
    if (!myUser.value || myUser.value.userLevel === 'dummy') {
      // User is not logged in, open request to join dialog
      loginDialogMode.value = 'activate'
    } else {
      // User is logged in, open add restaurant dialog
      isAddRestaurantDialogOpen.value = true
    }
  })
}

const handleRestaurantAdded = () => {
  // Optionally handle when a restaurant is added
  // Could refresh the search results or close the dialog
  isAddRestaurantDialogOpen.value = false
}

const removeRestaurant = index => {
  selectedRestaurantsObjectArray.value.splice(index, 1)

  // Clear search query and results to prevent showing default restaurants
  searchQuery.value = ''
  currentSearchTerm.value = ''
  hasSearched.value = false
  restaurantSearchResults.value = []
  
  // Close and reset the combobox if it exists
  nextTick(() => {
    if (comboBoxRef.value) {
      const inputElement = comboBoxRef.value?.$el?.querySelector('input')
      if (inputElement) {
        inputElement.value = ''
        inputElement.blur()
      }
    }
  })
}

// Memoize image URLs to avoid recalculating on every render
const restaurantImageCache = new Map()

// Track image fallback state per restaurant so broken cached Google photos can retry with fresh place data.
const failedImageKeys = ref({})
const fetchedGooglePhotoUrls = ref({})
const fetchingGooglePhotoKeys = ref({})
const hasFetchedGooglePhotoKeys = ref({})
const googlePlacesService = new GooglePlacesService()

const getRestaurantImageKey = restaurant => {
  return `${restaurant.name}|${restaurant.market}`
}

// Helper to get Google Places photo URL
const getGooglePhotoUrl = (photoReference, maxWidth = 200, maxHeight = 200) => {
  const apiKey = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY
  
  return `https://d128sk95sboo22.cloudfront.net/maps/api/place/photo?maxwidth=${maxWidth}&maxheight=${maxHeight}&photo_reference=${photoReference}&key=${apiKey}`
}

// Helper function to safely get restaurant image URL with fallbacks
const getRestaurantImageUrl = restaurant => {
  if (!restaurant) return noImage

  // Create a cache key from restaurant identifier
  const cacheKey = getRestaurantImageKey(restaurant)

  // Check cache first
  if (restaurantImageCache.has(cacheKey)) {
    return restaurantImageCache.get(cacheKey)
  }

  let imageUrl = noImage

  // 1. Use uploaded images from database (imageIds)
  if (restaurant.imageIds && restaurant.imageIds.length > 0) {
    imageUrl = mediaUrl(restaurant.imageIds[0], 'small')
  }

  // 2. Fallback to event spaces images
  else if (restaurant.eventSpaces?.length > 0) {
    const spaceWithImage = restaurant.eventSpaces.find(space => space.imageId)
    if (spaceWithImage) {
      imageUrl = mediaUrl(spaceWithImage.imageId, 'small')
    }
  }

  // 3. Fallback to a freshly fetched Google Places photo (takes priority if cached photo failed)
  else if (fetchedGooglePhotoUrls.value[cacheKey]) {
    imageUrl = fetchedGooglePhotoUrls.value[cacheKey]
  }

  // 4. Fallback to cached Google Places photos (skip if previously failed to load)
  else if (!failedImageKeys.value[cacheKey] && restaurant.googlePlaceDetails?.photos?.length > 0) {
    const photoRef = restaurant.googlePlaceDetails.photos[0].photo_reference
    if (photoRef) {
      imageUrl = getGooglePhotoUrl(photoRef)
    }
  }

  // Only cache valid image URLs (not noImage) to allow re-evaluation when data loads
  if (imageUrl !== noImage) {
    // Limit cache size to prevent memory issues
    if (restaurantImageCache.size > 100) {
      // Clear oldest entries if cache gets too large
      const firstKey = restaurantImageCache.keys().next().value
      restaurantImageCache.delete(firstKey)
    }
    restaurantImageCache.set(cacheKey, imageUrl)
  }
  
  return imageUrl
}

const fetchGooglePlacePhoto = async restaurant => {
  if (!restaurant?.googlePlaceId) return

  const cacheKey = getRestaurantImageKey(restaurant)
  if (fetchingGooglePhotoKeys.value[cacheKey] || hasFetchedGooglePhotoKeys.value[cacheKey]) return

  fetchingGooglePhotoKeys.value[cacheKey] = true

  try {
    const placeDetails = await googlePlacesService.getPlaceDetails(restaurant.googlePlaceId, ['photos'])
    const photoRef = placeDetails?.photos?.[0]?.photo_reference

    if (photoRef) {
      fetchedGooglePhotoUrls.value[cacheKey] = getGooglePhotoUrl(photoRef)
    }
  } catch (error) {
    console.error('Failed to fetch Google Places photo:', error)
  } finally {
    restaurantImageCache.delete(cacheKey)
    fetchingGooglePhotoKeys.value[cacheKey] = false
    hasFetchedGooglePhotoKeys.value[cacheKey] = true
  }
}

// Handle image load failure - try a fresh Google Places fetch before falling back to noImage
const onImageError = async restaurant => {
  if (!restaurant) return
  const cacheKey = getRestaurantImageKey(restaurant)
  failedImageKeys.value[cacheKey] = true
  delete fetchedGooglePhotoUrls.value[cacheKey]
  restaurantImageCache.delete(cacheKey)

  if (restaurant.googlePlaceId && !hasFetchedGooglePhotoKeys.value[cacheKey]) {
    await fetchGooglePlacePhoto(restaurant)
  }
}

const generateTimeRange = (startTimeMoment, endTimeMoment) => {
  let returnArray = []
  let momentProcessing = moment(startTimeMoment)
  while (momentProcessing.isSameOrBefore(endTimeMoment)) {
    returnArray.push(momentProcessing.tz(timeZone.value.identifier).format("h:mma"))
    momentProcessing.add(15, "minutes")
  }
  
  return returnArray
}

const saveDraft = () => {
  if(myUser.value){
    reservationsStore.saveEditReservation(true)
  }
}

// RM-360: the member's preview link — exactly what the restaurant(s) will get
// (their response page and email). The draft is saved first so the preview
// shows it as it stands; the server works out the hold and expiration as the
// submit would.
const openingRequestPreview = ref(false)
const requestPreviewPath = ref(null)
const requestPreviewError = ref(false)

const requestPreviewLabel = computed(() => {
  const names = (selectedRestaurantsObjectArray.value || []).map(r => r?.name).filter(Boolean)
  if (names.length === 1) return `See what ${names[0]} will receive`
  if (names.length === 2) return `See what ${names[0]} and ${names[1]} will receive`

  return 'See what the restaurants will receive'
})

const showPreview = async () => {
  if (!editingReservation.value?._id) return
  openingRequestPreview.value = true
  requestPreviewError.value = false
  try {
    await reservationsStore.saveEditReservation(true)

    const { data } = await axios.post(`/reservation/${editingReservation.value._id}/preview-link`)

    requestPreviewPath.value = data.path
    isShowingRequestPreviewDialog.value = true
  } catch (error) {
    requestPreviewError.value = true
  } finally {
    openingRequestPreview.value = false
  }
}

// RM-359 PR 3 (Kapil 10/27): a step of its own right after the request review
// (7a → 7l → 7b): what the restaurant will receive, the Likelihood of
// Acceptance and Ask RM for feedback, so Review & Submit (7c) only submits.
// The likelihood is read each time the step opens and after each save, so it
// follows the member's edits; it never blocks anything.
const likelihood = ref(null)
const likelihoodLoading = ref(false)
const checkLikelihood = async () => {
  const id = editingReservation.value?._id
  if (!id || editingReservation.value?.status !== 'Draft') {
    likelihood.value = null

    return
  }
  likelihoodLoading.value = true
  try {
    const { data } = await axios.get(`/reservation/${id}/likelihood`)
    noteLikelihoodAnswer(data)
    likelihood.value = data?.likelihood || null
  } catch (error) {
    likelihood.value = null
  } finally {
    likelihoodLoading.value = false
  }
}
watch(() => [tab.value, reservationsStore.saving], ([step, saving]) => {
  if (step === '7l' && !saving) checkLikelihood()
})

const exitSearchModeIfNoFocus = isFocused => {
  console.log("exitSearchModeIfNoFocus", isFocused )
  
  // When the combobox gets focus again, clear the search term for private dining group requests
  // This allows users to start fresh when searching for additional restaurants
  if (isFocused && tab.value == '2' && editingReservation.value?.reservationType == 'private') {
    // Clear search-related state
    currentSearchTerm.value = ''
    searchQuery.value = ''
    restaurantSearchResults.value = []
    hasSearched.value = false
    
    // Clear the input element value if possible
    nextTick(() => {
      if (comboBoxRef.value) {
        const inputElement = comboBoxRef.value?.$el?.querySelector('input')
        if (inputElement) {
          inputElement.value = ''
        }
      }
    })
  }
}

const goBack = () => {
  if(tab.value == '2')
    tab.value = '1'
  else if(tab.value == '2b')
    tab.value = '2'
  else if(tab.value == '1b')
    tab.value = '1'
  else if(tab.value == '3a')
    tab.value = '1b'
  else if(tab.value == '2c')
    tab.value = '1'
  else if(tab.value == '4b')
    tab.value = '2b' // RM-400 (10/13): budget right after date/times
  else if(tab.value == '3b')
    tab.value = '4b' // optional details back to budget
  else if(tab.value == '5b')
    tab.value = '3b' // review back to optional details
  else if(tab.value == '5p')
    tab.value = '3a' // Spend/payment back to date/time (4a skipped — times on 3a)
  else if(tab.value == '6a')
    tab.value = '5p' // Occasion back to spend/payment
  else if(tab.value == '6b')
    tab.value = '6a' // Go back to occasion step
  else if(tab.value == '7a')
    tab.value = '6a' // Review back to occasion
  else if(tab.value == '7l')
    tab.value = '7a' // Before you submit back to Review
  else if(tab.value == '7b')
    tab.value = '7l' // Profile back to Before you submit
  else if(tab.value == '7c')
    tab.value = '7b' // Submit back to Profile
  else
    tab.value = (Number(tab.value[0]) - 1) + (tab.value[0] == '3' ? '' : tab.value[1])
}

// Optimized function to handle tab change to '2' with deferred heavy operations
const goToTab2 = () => {
  tab.value = '2'

  // Defer all heavy operations to allow UI to paint first
  if (window.requestIdleCallback) {
    requestIdleCallback(() => {
      nextTick(() => {
        limitPreferredRestaurant()
        searchMode.value = false
        loadingRestaurantData.value = false
      })
    }, { timeout: 200 })
  } else {
    setTimeout(() => {
      nextTick(() => {
        limitPreferredRestaurant()
        searchMode.value = false
        loadingRestaurantData.value = false
      })
    }, 50)
  }
}

const tabData = {
  '1': {
    title: ''
  },
  '2': {
    title: ''// 'Select up to 10 favorites'
  },
  '1b': {
    title: ''// Request guidance
  },
  '2b': {
    title: ''//'Event Details'
  },
  '3a': {
    title:''// 'Request Reservation'
  },
  '4a': {
    title:''// 'Start Time Range'
  },
  '6b': {
    title: ''//'Other Details'
  },
  '6a': {
    title:''// 'Guaranteed Spend'
  },
  '7a': {
    title: ''//'Review & Submit'
  },
  '7l': {
    title: ''//'Before you submit'
  },
  '8a': {
    title: ''// 'Payment'
  },
  '4b': {
    title: ''//'Budget'
  },
  '3b': {
    title: ''//'Optional details'
  },
  '5b': {
    title: ''//'Please Review'
  },
  '2c': {
    title:''// 'Happenings'
  },
}

const confirmEditingReservation = async () => {

  try {
    await reservationsStore.confirmEditingReservation({
      email: myUser.value.email,
      firstName: myUser.value.firstName,
      lastName: myUser.value.lastName
    })

    //navigate to bookings page
    router.push({path: '/bookings'})
  } catch (error) {
    console.error("Error during confirmEditingReservation: ",error)
    const message = error?.response?.data || error?.message || 'There was an error submitting your request. Please try again.'

    alert(message)
  }
}

//doing the querySelector on document is probably not the right way to do this
const scrollToSelected = (retries = 8) => {
  nextTick(() => {
    const el = document.querySelector(".v-list-item--active")
    if (el) {
      el.scrollIntoView({behavior: "instant", block: "center", inline: "nearest"})
    } else if (retries > 0) {
      // Dropdown menu (teleported overlay) may not have mounted yet, or v-model
      // was just assigned in the same click handler and the active item hasn't
      // rendered. Retry on next frame until it appears.
      requestAnimationFrame(() => scrollToSelected(retries - 1))
    }
  })
}

// Validator to ensure preferredEndTime is not less than preferredTime for private dining
const preferredEndTimeValidator = value => {
  if (!value) return true // requiredValidator will handle empty case
  if (editingReservation.value.reservationType !== 'private') return true // Only validate for private dining
  if (!editingReservation.value.preferredTime) return true // Can't validate without start time
  
  const endTime = moment(value, 'h:mma')
  const startTime = moment(editingReservation.value.preferredTime, 'h:mma')
  
  if (endTime.isBefore(startTime)) {
    return 'End time must be after start time'
  }
  
  return true
}

const checkPreferredTime = () => {
  if(!editingReservation.value.preferredTime){
    editingReservation.value.preferredTime = "7:00pm"
  }
}


const checkEarliestTime = () => {
  console.log('🕐 checkEarliestTime called - current earliestTime:', editingReservation.value.earliestTime)

  // Only set default if preferredTime is set to avoid "Invalid date"
  // if(!editingReservation.value.earliestTime && editingReservation.value.preferredTime){
  //   let defaultTime = moment.tz(editingReservation.value.preferredTime, "h:mma", timeZone.value.identifier).subtract(30, "minutes").format("h:mma")
  //   console.log('🕐 checkEarliestTime - setting default earliestTime to:', defaultTime)
  //   editingReservation.value.earliestTime = defaultTime
  // }
}

const checkLatestTime = () => {
  console.log('🕐 checkLatestTime called - current latestTime:', editingReservation.value.latestTime)

  // Only set default if preferredTime is set to avoid "Invalid date"
  // if(!editingReservation.value.latestTime && editingReservation.value.preferredTime){
  //   let defaultTime = moment.tz(editingReservation.value.preferredTime, "h:mma", timeZone.value.identifier).add(30, "minutes").format("h:mma")
  //   console.log('🕐 checkLatestTime - setting default latestTime to:', defaultTime)
  //   editingReservation.value.latestTime = defaultTime
  // }
}

// NOTE: requestExpirationTime and requestExpirationSendReminderAt are now calculated
// on the backend when the reservation is submitted (see routes/api/reservation.js)
// The setExpirationAndReminder function has been removed from the frontend.

const showFavoriteRestaurantDialogIfNeeded = async () => {
  // Validate form before proceeding
  const { valid: isValid } = await form.value.validate()
  if (!isValid) return

  // Always proceed to next tab - removed dialog for non-preferred restaurants
  // Skip 4a (earliest/latest time) - those inputs are now on 3a
  // Flow: 3a -> 5p (spend/payment) -> 6a (occasion/notes/expiration) -> 7a (review)
  reservationsStore.saveEditReservation(true)
  changesMade.value = false
  tab.value = '5p'
}

// True when the reservation's preferred time is within 48 hours — drives
// auto-expanding the cancellation policy so the member sees it.
const isReservationWithin48Hours = computed(() => {
  const er = editingReservation.value
  if (!er?.date) return false
  const timeStr = er.preferredTime || er.earliestTime
  if (!timeStr) return false
  // Normalize date first — er.date may be a full datetime string, not YYYY-MM-DD.
  const dateOnly = moment(er.date).format('YYYY-MM-DD')
  const m = moment(`${dateOnly} ${timeStr}`, 'YYYY-MM-DD h:mma')
  if (!m.isValid()) return false

  return m.diff(moment(), 'hours', true) <= 48
})

// Mirror EditReservationReviewStep's 'Next' button gating so the Submit we
// render alongside the Review (commit 4) only appears in the same ready state —
// not for dummy users, expired membership, incomplete profile, or non-accepting
// restaurants (those show the review's own edit-profile / upgrade / login CTA).
const reviewReady = computed(() => {
  const er = editingReservation.value
  if (!er || er.status !== 'Draft') return false
  if (myUser.value?.userLevel === 'dummy') return false
  const owner = reservationOwner.value
  const upgradeNeeded = (owner.isPlatinum && dateSelectedPastPlatinumMembershipTerm.value)
    || (owner.isPreferred && !er.requiresPlatinum && dateSelectedPastPreferredMembershipTerm.value)
  if (upgradeNeeded) return false
  if (!isNotHost.value && myUser.value?.activeSubscription && isProfileIncomplete.value) return false
  if (selectedRestaurantsNotAcceptingReferrals.value) return false

  return !!(owner.activeSubscription && !(selectedRestaurantsNotPreferred.value && !owner.isPlatinum))
})

const handleSpendNext = async () => {
  // Spend lives on its own 5p step now. Preferred members have optional spend.
  // The card + authorization moved to the final Confirm & Pay step (7c).
  const spendRequired = !isPreferredAtSelectedRestaurant.value
  if (spendRequired && !editingReservation.value.minSpendPerPerson) {
    await form.value.validate()

    return
  }

  reservationsStore.saveEditReservation(true)
  changesMade.value = false
  tab.value = '6a'
}

const expirationError = ref(false)
// The backend defaults requestExpirationHours to 24, so we can't detect "unset"
// from the value. Track an explicit pick instead — nothing is highlighted and
// Next is blocked until the member actually taps a window.
const expirationPicked = ref(false)

const handleOccasionNext = async () => {
  // Require the occasion, and an explicit expiration pick when that selector is
  // shown (no default — members choose deliberately).
  const needsExpiration = showRequestExpirationChoice.value && !expirationPicked.value
  expirationError.value = needsExpiration
  if (!editingReservation.value.occasion || needsExpiration) {
    await form.value.validate()

    return
  }

  reservationsStore.saveEditReservation(true)
  changesMade.value = false
  tab.value = '7a'
}

//watch editingReservation.value and set isDialogVisible.value to false when editingReservation.value is set to null
//and TODO set tab to proper value
watch(
  () => editingReservation?.value,
  () => {
    let newValue = (editingReservation?.value != null)

    //if the dialog is going from not visible to visible and the reservation is a draft then initialize stuff
    if (isDialogVisible.value == false && isDialogVisible.value != newValue ) {
      // Reset flags when dialog opens to ensure clean state
      isUpdating.value = false
      justHandledItemClick.value = false
      console.log('📋 Dialog opening - reset isUpdating and justHandledItemClick to false')

      // AI-flow drafts arrive with the member's chat-chosen expiration window —
      // count it as picked so they aren't forced to choose again (RM-230 live
      // find 7/23). UI-created drafts keep the explicit-pick requirement: their
      // schema-default 24 is not a member choice, so `source` must be 'agent'.
      expirationPicked.value = editingReservation?.value?.source === 'agent'
        && [24, 48].includes(editingReservation?.value?.requestExpirationHours)

      if(editingReservation?.value.status == 'Draft'){
        // Load preferred status so the spend step can be made optional for preferred members
        if (editingReservation?.value?.reservationType === 'general') {
          loadMyPreferredStatus()
        }

        // Defer heavy computations to allow dialog to render first
        nextTick(() => {
          // Note: requestExpirationTime and requestExpirationSendReminderAt are now calculated
          // on the backend when the reservation is submitted (see routes/api/reservation.js)
          limitPreferredRestaurant()

          if (reservationsStore.openAtStep) {
            const targetStep = reservationsStore.openAtStep
            reservationsStore.openAtStep = null
            reservationsStore.openAtPaymentStep = false
            openedViaAgentHandoff.value = true
            // The old combined submit step (8a) is now split into Review (7a) →
            // Host Profile (7b, presence sign-off) → Confirm & Pay (7c, card +
            // authorization). The AI handoff lands on Review so the member walks
            // that sequence — 7b sets confirmedPresence, which 7c's Submit gates on.
            tab.value = targetStep === '8a' ? '7a' : targetStep

            return
          } else if (reservationsStore.openAtPaymentStep) {
            reservationsStore.openAtPaymentStep = false
            openedViaAgentHandoff.value = true
            tab.value = '7a' // enter the Review → Host Profile → Confirm & Pay sequence

            return
          }

          if(editingReservation?.value?.date){
            if(editingReservation?.value?.reservationType == 'general'){
              tab.value = "3a"
            } else if(editingReservation?.value?.reservationType == 'private'){
              tab.value = "4b"
            }
          } else{
            // No defaults — let user choose date, party size, and time
            editingReservation.value.date = null
            editingReservation.value.partySize = null
            editingReservation.value.preferredTime = null
            tab.value = "1"
          }

          //reset the date and preferredTime if the preferredTime is too soon or in the past
          if(editingReservation?.value?.preferredTime){
            let newRequestPreferredMoment = moment(editingReservation.value.date + ' ' + editingReservation.value.preferredTime, 'YYYY-MM-DD h:mma')
            if(newRequestPreferredMoment.isBefore(earliestBookingMoment.value)){
              editingReservation.value.preferredTime = null
              editingReservation.value.date = null
            }
          }

          changesMade.value = false
        })
      }
      else if (myUser.value.userLevel == 'admin') {
        if(editingReservation.value.reservationType == "general"){
          tab.value = "7a"
        } else {
          tab.value = "4b"
        }
      } else {
        tab.value = "1"
      }

    } else if (editingReservation?.value == null) {
      tab.value = "1"

      // Reset flags when dialog closes
      isUpdating.value = false
      justHandledItemClick.value = false
      console.log('📋 Dialog closing - reset isUpdating and justHandledItemClick to false')

      // Reset payment state when dialog closes
      paymentStepRef.value?.reset()
      reservationsStore.resetPaymentState()
      expirationPicked.value = false
      expirationError.value = false
    }

    isDialogVisible.value = newValue
  },

  // {deep: true}
)

//this is part of a hacky fix for https://github.com/vuetifyjs/vuetify/issues/17017
// by setting scroll-strategy="none" below it fixes an issue with the position of the dialog
//then this watchEffect prevents the page from scrolling when the dialog is open
watchEffect(() => {
  document.documentElement.style.overflow = isDialogVisible.value ? 'hidden' : null
})

//watches for changes to the alternateRestaurants and sets changesMade to true
watch(
  () => editingReservation?.value?.specialRequests,
  () => {
    if(editingReservation?.value?.specialRequests?.length == 0){
      editingReservation.value.specialRequestFlexibility = undefined
    }
  },
  {deep: true}
)

//watches for changes to the requestExpirationTimeframe
watch(

  // () => editingReservation?.value?.requestExpirationTimeframe,
  () => editingReservation?.value?.preferredTime,
  () => {
    // Add null check to prevent errors
    if (!editingReservation?.value) return

    console.log('🕐 preferredTime watcher triggered - preferredTime:', editingReservation.value.preferredTime, 'reservationType:', editingReservation.value.reservationType)

    // Note: requestExpirationTime and requestExpirationSendReminderAt are now calculated
    // on the backend when the reservation is submitted (see routes/api/reservation.js)

    // Only reset earliestTime/latestTime when the user interactively changes preferredTime,
    // not when the dialog is first opening with saved values (e.g. from agent handoff).
    if (editingReservation.value.preferredTime) {
      if (skipNextTimeReset.value) {
        skipNextTimeReset.value = false
      } else {
        editingReservation.value.earliestTime = null
        editingReservation.value.latestTime = null
      }
    }

    // For private dining, ensure preferredEndTime is not less than preferredTime
    if (editingReservation.value.reservationType === 'private' && editingReservation.value.preferredEndTime && editingReservation.value.preferredTime) {
      const endTime = moment(editingReservation.value.preferredEndTime, 'h:mma')
      const startTime = moment(editingReservation.value.preferredTime, 'h:mma')
      if (endTime.isBefore(startTime)) {
        editingReservation.value.preferredEndTime = null
      }
    }
  },
  {deep: true}
)

// Watch preferredEndTime to ensure it's never less than preferredTime for private dining
watch(
  () => editingReservation?.value?.preferredEndTime,
  () => {
    if (!editingReservation?.value) return
    
    // Only validate for private dining
    if (editingReservation.value.reservationType === 'private' && editingReservation.value.preferredEndTime && editingReservation.value.preferredTime) {
      const endTime = moment(editingReservation.value.preferredEndTime, 'h:mma')
      const startTime = moment(editingReservation.value.preferredTime, 'h:mma')
      if (endTime.isBefore(startTime)) {
        editingReservation.value.preferredEndTime = null
      }
    }
  },
  {deep: true}
)

//same as above but watched both preferredTime AND date
watch(
  () => editingReservation?.value?.date,
  () => {
    // Note: requestExpirationTime and requestExpirationSendReminderAt are now calculated
    // on the backend when the reservation is submitted (see routes/api/reservation.js)
  },
  {deep: true}
)

// Fetch instant booking slots when date changes (for general reservation flow)
watch(
  () => editingReservation?.value?.date,
  newDate => {
    // Only fetch for general reservations when on the appropriate tab
    if (newDate && editingReservation?.value?.reservationType === 'general') {
      fetchInstantBookingSlotsForDate()
    } else {
      instantBookingSlotsByDate.value = []
    }
  }
)

// Also fetch instant booking slots when market changes (restaurant selection)
watch(
  () => selectedRestaurantsObjectArray?.value?.[0]?.market,
  (newMarket, oldMarket) => {
    // Refetch slots if market changed and we have a date
    if (newMarket !== oldMarket && editingReservation?.value?.date && editingReservation?.value?.reservationType === 'general') {
      fetchInstantBookingSlotsForDate()
    }
  }
)

//watches for changes and sets requiresPlatinum as appropriate
watch(
  () => selectedRestaurantsNotPreferred?.value,
  () => {
    if(editingReservation?.value){
      if(selectedRestaurantsNotPreferred?.value){
        editingReservation.value.requiresPlatinum = true
      }
      else{
        editingReservation.value.requiresPlatinum = false
      }
    }

    // Note: requestExpirationTime and requestExpirationSendReminderAt are now calculated
    // on the backend when the reservation is submitted (see routes/api/reservation.js)
  },
  {deep: true}
)

//selectedRestaurantsObjectArray items need to be fetched from the server and have googlePlaceData added to them as needed
//also syncs up important editingReservation items like market and preferredRestaurant
watch(
  () => selectedRestaurantsObjectArray?.value,
  async (value, oldValue, onCleanup) => {
    console.log('👀 selectedRestaurantsObjectArray watcher triggered, length:', value?.length, 'isUpdating:', isUpdating.value, 'justHandledItemClick:', justHandledItemClick.value)

    // Skip watcher logic if updates are being controlled
    if (isUpdating.value) {
      console.log('👀 Watcher skipped - isUpdating is true')
      
      return
    }

    // Add null check to prevent errors
    if (!editingReservation.value) {
      console.log('👀 Watcher skipped - editingReservation is null')
      
      return
    }

    // Reset bookings list and stats tab when restaurant changes
    showBookingsList.value = false
    memberFavoriteStatsTab.value = null

    // Refresh preferred status on selection change (spec §8). The cached value
    // from dialog open is usually sufficient, but revoke/grant can race with
    // long-running drafts. A per-change fetch keeps the UI in sync with the
    // server-authoritative bypass decision.
    if (editingReservation.value?.reservationType === 'general') {
      loadMyPreferredStatus()
    }

    // Immediate UI updates - set basic values synchronously for instant feedback
    editingReservation.value.restaurantSpendShareSettings = []
    if(selectedRestaurantsObjectArray?.value?.length > 0){
      isUpdating.value = true // Prevent watcher from running
      console.log('👀 Set isUpdating = true')
      searchMode.value = false
      editingReservation.value.alternateRestaurants = []
      editingReservation.value.alternateRestaurantIds = []

      // Set basic values immediately for instant UI feedback
      if(selectedRestaurantsObjectArray?.value?.[0]) {
        editingReservation.value.market = selectedRestaurantsObjectArray.value[0].market
        editingReservation.value.preferredRestaurant = selectedRestaurantsObjectArray.value[0].name
        editingReservation.value.preferredRestaurantId = selectedRestaurantsObjectArray.value[0]._id || selectedRestaurantsObjectArray.value[0].id
        editingReservation.value.timeZoneIdentifier = selectedRestaurantsObjectArray.value[0].timeZoneIdentifier
      }

      // Check if any restaurant actually needs data loading
      const needsLoading = selectedRestaurantsObjectArray.value.some(r => r && !r.googlePlaceDetails)

      if(needsLoading) {
        loadingRestaurantData.value = true // Show loading state only if needed (inside dialog)
      }

      // Defer heavy async operations to avoid blocking UI - use requestIdleCallback for best performance
      const processRestaurants = async () => {
        for(let i = 0; i < selectedRestaurantsObjectArray?.value?.length; i++){
          let restaurant = selectedRestaurantsObjectArray?.value?.[i]

          // Skip if restaurant is undefined, null, or not a valid restaurant object
          if(!restaurant || !isValidRestaurant(restaurant)) {
            // Remove invalid entries immediately
            selectedRestaurantsObjectArray.value.splice(i, 1)
            i-- // Adjust index after removal
            continue
          }

          editingReservation.value.restaurantSpendShareSettings[i] = {includeSpendRestaurantAndGroup: false}

          //nothing to do if googlePlaceData already there
          if(restaurant.googlePlaceDetails){
            if(i > 0){
              editingReservation.value.alternateRestaurants.push(restaurant.name)
              editingReservation.value.alternateRestaurantIds.push(restaurant._id || restaurant.id)
            }

            // Booking data will be fetched when user navigates to tab 6a
            continue
          }

          //check if restaurant is in our db already
          let existingRestaurant, newRestaurant
          
          // First check if we already have it in the store's restaurants array
          if(restaurant.name && restaurant.market){
            existingRestaurant = restaurants.value?.find(r => 
              r.name === restaurant.name && r.market === restaurant.market
            )
            
            // If restaurant is cached but missing booking/min spend fields, fetch fresh
            if(existingRestaurant && (
              !restaurantHasBookingFields(existingRestaurant) ||
              existingRestaurant.privateBrunchLunchMinSpendPerPerson === undefined &&
              existingRestaurant.privateDinnerFriAndSatMinSpendPerPerson === undefined &&
              existingRestaurant.privateDinnerSunThruThursMinSpendPerPerson === undefined &&
              existingRestaurant.brunchLunchMinSpendPerPerson === undefined &&
              existingRestaurant.dinnerFriAndSatMinSpendPerPerson === undefined &&
              existingRestaurant.dinnerSunThruThursMinSpendPerPerson === undefined
            )) {
              // Restaurant is cached but missing min spend fields, fetch fresh
              existingRestaurant = null
            }
          }
          
          // If not in cache or missing fields, fetch by ID, name/market, or googlePlaceId
          if(!existingRestaurant){
            if(restaurant.id || restaurant._id){
              existingRestaurant = await restaurantsStore.getRestaurantById(restaurant.id || restaurant._id)
            }
            else if(restaurant.name && restaurant.market){
              // Use getSingleRestaurant to fetch with min spend fields
              existingRestaurant = await restaurantsStore.getSingleRestaurant(restaurant.name, restaurant.market)
            }
            else if(restaurant.googlePlaceId){
              existingRestaurant = await restaurantsStore.getRestaurantByGooglePlaceId(restaurant.googlePlaceId)
            }
          }

          //restaurants are only created if we have a googlePlaceId
          if(!existingRestaurant && restaurant.googlePlaceId){
            //if we don't have the restaurant then create it
            let newPlace = await getPlaceAsync(restaurant.googlePlaceId)

            //make call to google timezone api and append timezone to place object
            const timeZone = await axios.get(`https://d128sk95sboo22.cloudfront.net/maps/api/timezone/json?location=${newPlace.geometry.location.lat},${newPlace.geometry.location.lng}&key=${import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY}`)
            newPlace.timeZone = timeZone.data
            newRestaurant = await restaurantsStore.newRestaurantFromGooglePlace(newPlace, myUser?.value?.email)
          }

          let theRestaurant = existingRestaurant || newRestaurant
          if(theRestaurant) {
            const resolvedRestaurantTimeZoneIdentifier = theRestaurant.timeZoneIdentifier
              || (theRestaurant.market == editingReservation.value.market
                ? editingReservation.value.timeZoneIdentifier
                : null)

            if(editingReservation.value.timeZoneIdentifier && (resolvedRestaurantTimeZoneIdentifier != editingReservation.value.timeZoneIdentifier)){
              alert(`Sorry, you can only select multiple restaurants if they are in the same timezone. You just selected a restaurant in "${theRestaurant.timeZone?.abbreviation || theRestaurant.timeZoneIdentifier}" timezone but your other selection(s) are in "${editingReservation.value.timeZone?.abbreviation || editingReservation.value.timeZoneIdentifier}" timezone.`)
              selectedRestaurantsObjectArray.value.pop()
              break
            }
            else{
              theRestaurant.timeZoneIdentifier = resolvedRestaurantTimeZoneIdentifier || theRestaurant.timeZoneIdentifier
              selectedRestaurantsObjectArray.value[i] = theRestaurant
            }
          } else{
            selectedRestaurantsObjectArray.value[i] = null
          }

          // Defer Google Place details fetching - not needed immediately
          // Only fetch when user actually tries to view restaurant details
          if(selectedRestaurantsObjectArray.value[i].googlePlaceId && !selectedRestaurantsObjectArray.value[i].googlePlaceDetails){
            // Fetch in background, non-blocking
            getPlaceAsync(selectedRestaurantsObjectArray.value[i].googlePlaceId).then(placeDetails => {
              if(placeDetails && selectedRestaurantsObjectArray.value[i]) {
                selectedRestaurantsObjectArray.value[i].googlePlaceDetails = placeDetails
              }
            }).catch(err => {
              console.error('Background Google Place fetch failed:', err)
            })
          }
          
          if(i > 0 && theRestaurant){
            editingReservation.value.alternateRestaurants.push(theRestaurant.name)
            editingReservation.value.alternateRestaurantIds.push(theRestaurant._id || theRestaurant.id)
          }

          // Booking data will be fetched when user navigates to tab 6a

        }

        // Filter out any undefined/null/invalid values from the array BEFORE resetting isUpdating
        const filteredArray = selectedRestaurantsObjectArray.value.filter(r => r && isValidRestaurant(r))

        // Only update if the filtered array is different to avoid triggering watcher
        if(filteredArray.length !== selectedRestaurantsObjectArray.value.length) {
          selectedRestaurantsObjectArray.value = filteredArray
        }

        // Always reset loading state after processing (even if no valid restaurants found)
        if(needsLoading) {
          loadingRestaurantData.value = false // Hide loading state
        }
        
        // If array is empty after filtering, also reset loading state
        if(selectedRestaurantsObjectArray.value.length === 0) {
          loadingRestaurantData.value = false
        }

        //set editingReservation values based on the first selected restaurant (update with full data)
        if(selectedRestaurantsObjectArray?.value?.[0]) {
          editingReservation.value.market = selectedRestaurantsObjectArray.value[0].market
          editingReservation.value.preferredRestaurant = selectedRestaurantsObjectArray.value[0].name
          editingReservation.value.preferredRestaurantId = selectedRestaurantsObjectArray.value[0]._id || selectedRestaurantsObjectArray.value[0].id
          editingReservation.value.timeZoneIdentifier = selectedRestaurantsObjectArray.value[0].timeZoneIdentifier
          editingReservation.value.stripeHoldType = selectedRestaurantsObjectArray.value[0].generalReservationHoldType
          editingReservation.value.stripeHoldCancellationLimitHours = selectedRestaurantsObjectArray.value[0].generalReservationHoldCancellationLimitHours
        }

        isUpdating.value = false // Allow watcher to run again LAST
      }

      // Use requestIdleCallback for best performance, fallback to setTimeout
      if (window.requestIdleCallback) {
        requestIdleCallback(processRestaurants, { timeout: 500 })
      } else {
        setTimeout(processRestaurants, 0)
      }

    }
    else{
      loadingRestaurantData.value = false
      if(editingReservation.value){
        editingReservation.value.market = null
        editingReservation.value.preferredRestaurant = null
        editingReservation.value.preferredRestaurantId = null
        editingReservation.value.alternateRestaurants = []
        editingReservation.value.alternateRestaurantIds = []
        editingReservation.value.timeZoneIdentifier = null
      }
    }

    if(selectedRestaurantsObjectArray?.value?.length-1 < recentBookingsTab?.value){
      recentBookingsTab.value = selectedRestaurantsObjectArray?.value?.length-1
    }

    //selectedRestaurantsObjectArray.value[0].forceUpdate = true
    scrollToEnd()
  },{deep: true}
)

const scrollToEnd = () => {
  //scroll to end of scrollable div
  handleScroll()
  setTimeout(function(){
    if (scrollableDiv.value) {
      scrollableDiv.value.style.scrollBehavior = "auto"
      scrollableDiv.value.scrollLeft = scrollableDiv.value.scrollWidth
      scrollableDiv.value.style.scrollBehavior = "smooth"
    }
  }, 5)
}

const scrollToLeft = () => {
  if (scrollableDiv.value) {
    let currentScroll = scrollableDiv.value.scrollLeft
    scrollableDiv.value.scrollLeft = Math.max(0, currentScroll - scrollableDiv.value.clientWidth/1.5)
  }
}

const scrollToRight = () => {
  if (scrollableDiv.value) {
    let currentScroll = scrollableDiv.value.scrollLeft
    scrollableDiv.value.scrollLeft = currentScroll + scrollableDiv.value.clientWidth/1.5
  }
}

const handleScroll = () => {
  if (scrollableDiv.value) {

    const scrollableDivWidth = scrollableDiv.value.clientWidth
    const scrollWidth = scrollableDiv.value.scrollWidth
    const scrollLeft = scrollableDiv.value.scrollLeft
    const numberOfRestaurants = selectedRestaurantsObjectArray.value.length
    buttonExtendedWidth.value = scrollableDivWidth / numberOfRestaurants

    rightMostScrollValue.value = scrollWidth -  scrollLeft - scrollableDivWidth

    scrolledLeft.value = scrollableDiv.value.scrollLeft > buttonExtendedWidth.value/3
    scrolledRight.value = rightMostScrollValue.value > buttonExtendedWidth.value/3

  }
}

const preSearchResults = computed(() => {
  let sortedRestaurants = [...restaurants.value].sort((a, b) => b.favoriteCount - a.favoriteCount)
  
  return sortedRestaurants.slice(0, 25)
})


// Check if a time string (h:mma) falls within operating hours for a restaurant on a given day
const isTimeWithinOperatingHours = (timeStr, restaurant, dayOfWeek) => {
  const periods = restaurant.googlePlaceDetails?.opening_hours?.periods
  if (!periods) return true // If no hours data, allow all times

  const timeMoment = moment(timeStr, 'h:mma')

  for (const period of periods) {
    const openDay = period.open.day
    const closeDay = period.close?.day
    const openTime = moment(period.open.time, 'HHmm')
    const closeTime = period.close ? moment(period.close.time, 'HHmm') : null

    // Handle 24-hour open (no close property)
    if (!closeTime) {
      if (openDay === dayOfWeek) return true
      continue
    }

    if (openDay === dayOfWeek) {
      if (closeDay === dayOfWeek) {
        if (timeMoment.isBetween(openTime, closeTime, null, '[]')) return true
      } else {
        // Closes next day — everything from open onward is valid
        if (timeMoment.isSameOrAfter(openTime)) return true
      }
    } else if (closeDay === dayOfWeek) {
      // Previous day's period extends into this day
      if (timeMoment.isBefore(closeTime)) return true
    }
  }

  return false
}

// Check if a time is within operating hours of ALL selected restaurants
const isTimeValidForAllRestaurants = (timeStr, dayOfWeek) => {
  if (!selectedRestaurantsObjectArray.value?.length) return true

  return selectedRestaurantsObjectArray.value.every(r => isTimeWithinOperatingHours(timeStr, r, dayOfWeek))
}

// For a given day-of-week, return { openMinutes, closeMinutes } representing the
// earliest open and latest close (in minutes from midnight) across all periods
// that start that day. Returns null when hours data is missing. Close is capped at
// 23:45 so overnight/next-day closes collapse onto the selected calendar day.
const getRestaurantOperatingWindow = (restaurant, dayOfWeek) => {
  const periods = restaurant?.googlePlaceDetails?.opening_hours?.periods
  if (!periods || periods.length === 0) return null

  const MAX_MINUTES = 24 * 60 - 15 // 23:45
  const toMinutes = hhmm => parseInt(hhmm.slice(0, 2), 10) * 60 + parseInt(hhmm.slice(2), 10)

  let earliestOpen = null
  let latestClose = null

  for (const period of periods) {
    if (period.open.day !== dayOfWeek) continue

    const openMin = toMinutes(period.open.time)
    if (earliestOpen === null || openMin < earliestOpen) earliestOpen = openMin

    let closeMin
    if (!period.close) {
      closeMin = MAX_MINUTES
    } else {
      const raw = toMinutes(period.close.time)
      closeMin = Math.min(period.close.day !== dayOfWeek ? raw + 24 * 60 : raw, MAX_MINUTES)
    }
    if (latestClose === null || closeMin > latestClose) latestClose = closeMin
  }

  if (earliestOpen === null) return null
  
  return { openMinutes: earliestOpen, closeMinutes: latestClose ?? MAX_MINUTES }
}

// Get operating hours display strings for each selected restaurant on the selected date
const selectedRestaurantsHoursDisplay = computed(() => {
  if (!editingReservation.value?.date || !selectedRestaurantsObjectArray.value?.length) return []

  const reservationDate = moment(editingReservation.value.date, 'YYYY-MM-DD')
  const dayOfWeek = reservationDate.day()
  const results = []

  for (const restaurant of selectedRestaurantsObjectArray.value) {
    const periods = restaurant.googlePlaceDetails?.opening_hours?.periods
    if (!periods) continue

    const hoursForDay = []
    for (const period of periods) {
      if (period.open.day === dayOfWeek) {
        const openTime = moment(period.open.time, 'HHmm').format('h:mm A')
        const closeTime = period.close ? moment(period.close.time, 'HHmm').format('h:mm A') : 'Late'
        const closesNextDay = period.close && period.close.day !== dayOfWeek
        hoursForDay.push(`${openTime} - ${closeTime}${closesNextDay ? ' (+1)' : ''}`)
      }
    }

    if (hoursForDay.length > 0) {
      results.push({ name: restaurant.name, hours: hoursForDay.join(', ') })
    } else {
      results.push({ name: restaurant.name, hours: 'Closed' })
    }
  }

  return results
})

const timeOutOfRangeMessage = computed(() => {
  if (editingReservation.value.preferredTime) {
    const reservationDate = moment(editingReservation.value.date, 'YYYY-MM-DD')
    const reservationDay = reservationDate.day()
    let messages = []

    for (const restaurant of selectedRestaurantsObjectArray.value) {
      const periods = restaurant.googlePlaceDetails?.opening_hours?.periods
      if (periods) {
        const withinHours = isTimeWithinOperatingHours(editingReservation.value.preferredTime, restaurant, reservationDay)
        if (!withinHours) {
          let operatingHoursMessage = ''
          for (const period of periods) {
            if (period.open.day === reservationDay) {
              const openTime = moment(period.open.time, 'HHmm').format('h:mm A')
              const closeTime = period.close ? moment(period.close.time, 'HHmm').format('h:mm A') : 'Late'
              operatingHoursMessage += `${openTime} - ${closeTime}, `
            }
          }
          if (operatingHoursMessage) {
            operatingHoursMessage = operatingHoursMessage.slice(0, -2)
            messages.push(`${restaurant.name} is open ${reservationDate.format('ddd, M/D')}: ${operatingHoursMessage}`)
          } else {
            messages.push(`${restaurant.name} appears to be closed on ${reservationDate.format('ddd, M/D')}`)
          }
        }
      }
    }

    if (messages.length > 0) {
      return messages
    }
  }

  return
})

// --- Restaurant Hours (RestaurantProfile-style) ---
const showAllHours = ref(false)

// Helper: get day name from weekday_text index (0=Monday … 6=Sunday)
const getDayName = index => {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  
  return days[index]
}

// Build formatted weekday_text hours for every selected restaurant
const selectedRestaurantsFullHours = computed(() => {
  if (!selectedRestaurantsObjectArray.value?.length) return []

  return selectedRestaurantsObjectArray.value.map(restaurant => {
    const weekdayText = restaurant.googlePlaceDetails?.opening_hours?.weekday_text
    const directHours = restaurant.openingHours

    let formattedHours = null

    if (weekdayText && Array.isArray(weekdayText) && weekdayText.length > 0) {
      const first = weekdayText[0]
      if (first && first.match(/^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday):/)) {
        formattedHours = weekdayText
      } else {
        formattedHours = weekdayText.map((hours, i) => `${getDayName(i)}: ${hours.trim()}`)
      }
    } else if (directHours && Array.isArray(directHours)) {
      formattedHours = directHours.map((hours, i) => `${getDayName(i)}: ${hours.trim()}`)
    }

    return { name: restaurant.name, hours: formattedHours }
  }).filter(r => r.hours)
})

// Get the hours for the selected reservation date (like "Today 11:00 AM – 10:00 PM" in RestaurantProfile)
const getSelectedDateHours = restaurantEntry => {
  if (!restaurantEntry?.hours || !editingReservation.value?.date) return null

  const reservationDate = moment(editingReservation.value.date, 'YYYY-MM-DD')
  const dayName = reservationDate.format('dddd')
  const shortDate = reservationDate.format('ddd M/D')

  const match = restaurantEntry.hours.find(h => h.startsWith(dayName + ':'))
  if (!match) return null

  return match.replace(dayName, shortDate).replace(':', '')
}
</script>

<template>
  <div>
    <AddRestaurantDialog 
      v-model:is-dialog-visible="isAddRestaurantDialogOpen" 
      :search-query="searchQuery"
      @on-restaurant-added="handleRestaurantAdded"
    />
    <RestaurantsInfoDialog v-model:is-dialog-visible="isRestaurantsInfoDialogOpen" />

    <EditReservationFavoriteDialog
      v-model="isShowingFavoriteRestaurantDialog"
      :is-platinum="!!reservationOwner.isPlatinum"
      :not-preferred-names="selectedRestaurantsNotPreferred || []"
      @continue="onFavoriteDialogContinue"
    />

    <EditReservationRequestPreviewDialog
      v-model="isShowingRequestPreviewDialog"
      :path="requestPreviewPath"
    />

    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDialogOpen"
      confirmation-msg="Save draft?"
      confirm-btn-text="Yes"
      cancel-btn-text="No"
      @confirm="closeWithSave"
      @cancel="closeWithDelete"
    />

    <VDialog
      v-if="editingReservation"
      v-model="isDialogVisible"
      :width="$vuetify.display.mdAndDown ? '100%' : 650"
      persistent
      fullscreen
      no-click-animation
      :retain-focus="false"
      scroll-strategy="none"
    >
      <VCard class="">
        <div class="toolbar-header">
          <VBtn
            v-if="tab!=1"
            icon
            size="small"
            variant="text"
            color="white"
            @click="goBack"
          >
            <VIcon size="20">
              mdi-chevron-left
            </VIcon>
          </VBtn>
          <div
            v-else
            style="width: 40px"
          />
          <span class="toolbar-header-title">
            {{ tabData[tab]?.title }}
          </span>
          <VBtn
            icon
            size="small"
            variant="text"
            color="white"
            @click="requestClose"
          >
            <VIcon size="20">
              mdi-close
            </VIcon>
          </VBtn>
        </div>
        <div
          v-if="tab=='5b'"
          class="ml-2 mr-2"
        >
          <div
            v-if="editingReservation && editingReservation.status == 'Draft'"
            class="text-center text-sm"
          />
          <VRow
            v-if="editingReservation"
            class="ma-1 mt-3"
          >
            <img
              class="referral-logo"
              :src="logoLargeLight"
            >
            <VCol class="align-self-center">
              <h1 class="confirm-title">
                Group/Private Dining Inquiry
              </h1>
            </VCol>
          </VRow>
        </div>
        <div
          v-if="tab != '8a' && tab != '2c'"
          class="toolbar-selected-restaurants ml-3 mr-3 mt-1 pt-0"
        >
          <VExpandTransition>
            <div
              v-if="loadingRestaurantData"
              class="text-center pa-4"
            >
              <VProgressCircular
                indeterminate
                color="primary"
                size="40"
              />
              <div class="text-sm mt-2">
                Loading restaurant details...
              </div>
            </div>
          </VExpandTransition>
          <!-- Searchbar visible on tab '1' when no restaurant selected, tab '2' for private dining, or tab '3a' for general when no restaurant selected -->
          <VExpandTransition>
            <div v-if="!loadingRestaurantData && ((tab == '1' && selectedRestaurantsObjectArray.length === 0) || tab == '2' || (tab == '3a' && editingReservation.reservationType == 'general' && selectedRestaurantsObjectArray.length === 0))">
              <VCombobox
                ref="comboBoxRef"
                :model-value="selectedRestaurantsObjectArray"
                label="Restaurant"
                :autofocus="false"
                :items="currentSearchTerm && currentSearchTerm.length >= 2 ? filteredSearchResults : []"
                :menu-props="{
                  closeOnContentClick: true,
                  density: 'compact',
                  maxHeight: '50vh',
                  openOnClick: false,
                  contentClass: 'restaurant-search-menu'
                }"
                :hide-no-data="!(currentSearchTerm && currentSearchTerm.length >= 2)"
                :custom-filter="fuzzyFilter"
                item-title="name"
                item-value="displayNameAndMarket"
                :return-object="true"
                chips
                closable-chips
                required
                hide-selected
                multiple
                append-icon=""
                class="mt-4"
                :class="[{ 'private-dining-search': tab == '2' && editingReservation.reservationType == 'private' }]"
                @update:model-value="handleRestaurantSelection"
                @update:search="fetchAutocomplete"
                @update:focused="exitSearchModeIfNoFocus"
                @keydown.enter.prevent="handleEnterKey"
              >
                <template #chip="{ item, props }">
                  <VChip
                    v-bind="props"
                    closable
                  >
                    {{ item.raw.name }} - {{ item.raw.market }}
                  </VChip>
                </template>
                <template #prepend-inner>
                  <VIcon>mdi-magnify</VIcon>
                </template>
                <template #no-data>
                  <VListItem v-if="currentSearchTerm && currentSearchTerm.length >= 2">
                    <VListItemTitle>
                      No matches found
                    </VListItemTitle>
                  </VListItem>
                  <VListItem v-else-if="currentSearchTerm && currentSearchTerm.length < 2">
                    <VListItemTitle class="text-center text-caption">
                      Type at least 2 characters to search
                    </VListItemTitle>
                  </VListItem>
                </template>
                <template #item="{ item }">
                  <div
                    class="search-dropdown-card"
                    @click.stop="handleRestaurantItemClick(item.raw)"
                  >
                    <div class="search-dropdown-content">
                      <!-- THE shared identity cluster — one component on every restaurant row -->
                      <RestaurantTitleBlock
                        :name="item.raw.name"
                        :market="item.raw.market"
                        :group="item.raw.group || ''"
                        :status="item.raw.status || ''"
                        compact
                      />
                      <div class="search-dropdown-subtitle-wrapper">
                        <p
                          v-if="item.raw.market"
                          class="search-dropdown-subtitle"
                        >
                          {{ item.raw.market }}
                        </p>
                      </div>
                    </div>

                    <div class="search-dropdown-image-container">
                      <VImg
                        :src="getRestaurantImageUrl(item.raw)"
                        class="restaurant-image rounded-lg"
                        cover
                        height="72"
                        width="72"
                        @error="onImageError(item.raw)"
                      />
                    </div>
                  </div>
                </template>
                <!-- Add favorite button - always shown when user types 2+ chars -->
                <template
                  v-if="searchQuery && searchQuery.length >= 2"
                  #append-item
                >
                  <div class="add-favorite-section">
                    <div
                      v-if="fetchingAutocomplete"
                      class="search-loading-state"
                    >
                      <VProgressCircular
                        indeterminate
                        color="primary"
                        size="40"
                      />
                      <p class="loading-text">
                        Searching restaurants...
                      </p>
                    </div>
                    <p class="empty-state-text">
                      Favorite not coming up?
                    </p>
                    <VBtn
                      color="primary"
                      variant="elevated"
                      class="add-restaurant-btn"
                      @click="handleAddFavoriteClick"
                    >
                      Add favorite
                    </VBtn>
                  </div>
                </template>
              </VCombobox>
            </div>
          </VExpandTransition>
          <!-- Read-only display for tabs after '2' or '3a' (includes '2b' for private dining) -->
          <VExpandTransition>
            <div
              v-if="!loadingRestaurantData && tab != '2' && tab != '3a' && selectedRestaurantsObjectArray.length > 0"
              class="mt-2"
            >
              <!-- For tabs '1' and '1b' (RM-154 intermediary), show full card with image -->
              <div v-if="tab == '1' || tab == '1b'">
                <div
                  v-for="(restaurant, index) in selectedRestaurantsObjectArray"
                  :key="index"
                  class="add-restaurant-item add-restaurant-item--summary"
                >
                  <div class="single-restaurant-card">
                    <div class="mini-restaurant-card-content">
                      <div
                        class="profile-card-horizontal"
                        @click="showRestaurantDetails(restaurant)"
                      >
                        <div class="profile-card-content">
                          <!-- THE shared identity cluster — one component on every restaurant row -->
                          <RestaurantTitleBlock
                            :name="restaurant.name"
                            :market="restaurant.market"
                            :group="restaurant.group || ''"
                            :status="restaurant.status || ''"
                            compact
                            @name-click="showRestaurantDetails(restaurant)"
                          />
                          <div class="profile-card-location">
                            {{ restaurant.market.split(",")[0] }}
                          </div>
                          <!-- Member Preferred chip for this restaurant -->
                          <div
                            v-if="preferredStatusStore.isPreferredAt(restaurant._id)"
                            class="profile-card-preferred-badge"
                          >
                            <VIcon
                              icon="mdi-star-check"
                              size="11"
                              style="margin-right: 3px; vertical-align: middle;"
                            />
                            <CmsText
                              k="reservation.preferred.badge"
                              tag="span"
                            />
                          </div>
                          <div
                            v-if="restaurant.acceptingReferrals === false"
                            class="profile-card-action"
                          >
                            🚫 requests via RM
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      class="mini-restaurant-image-holder"
                      :class="{'mini-restaurant-image-holder-tab1': tab == '1' || tab == '1b'}"
                    >
                      <VImg
                        v-if="getRestaurantImageUrl(restaurant) !== noImage"
                        :width="86"
                        :height="86"
                        :src="getRestaurantImageUrl(restaurant)"
                        cover
                        class="rounded-lg"
                        :lazy-src="noImage"
                        loading="lazy"
                        @error="onImageError(restaurant)"
                      >
                        <template #placeholder>
                          <VRow
                            class="fill-height ma-0"
                            align="center"
                            justify="center"
                          >
                            <VProgressCircular
                              indeterminate
                              color="grey-lighten-5"
                            />
                          </VRow>
                        </template>
                      </VImg>
                      <!--
                        Remove (X) button is step 1 only — the member should
                        commit their restaurant choice before progressing past
                        type-selection. To swap on the intermediary 1b screen
                        they use the Preferred Restaurants alt-list (which calls
                        selectPreferredRestaurant), and to fully clear they go
                        Back to step 1 via the header chevron. 
                      -->
                    </div>
                    <VBtn
                      v-if="tab == '1'"
                      size="x-small"
                      variant="text"
                      color="grey-darken-1"
                      class="remove-restaurant-button-summary"
                      icon="mdi-close"
                      @click="(e) => {e.stopPropagation(); removeRestaurant(index);}"
                    />
                  </div>
                </div>
              </div>
            </div>
          </VExpandTransition>
          <!-- Chip display for general reservations on tab '3a' when restaurant is selected -->
          <VExpandTransition>
            <div
              v-if="!loadingRestaurantData && tab == '3a' && editingReservation.reservationType == 'general' && selectedRestaurantsObjectArray.length > 0"
              class="mt-2 mb-4"
            >
              <div
                class="text-xs mb-2 ml-1"
                style="color: #666;font-size: 14px"
              >
                <strong>{{ $t('reservation.labels.preferredRestaurant') }}: </strong>
              
                <span
                  v-for="(restaurant, index) in selectedRestaurantsObjectArray"
                  :key="index"
                  size="default"
                  color="primary"
                  variant="outlined"
                >
                  {{ restaurant.name }} ({{ restaurant.market.split(",")[0] }})
                </span>
              </div>
            </div>
          </VExpandTransition>
          <VExpandTransition>
            <div
              v-if="!loadingRestaurantData && tab == '2' && editingReservation.reservationType == 'private'"
              class="position-relative"
            >
              <div
                ref="scrollableDiv"
                class="add-restaurant-scroller"
                :class="[{'scrolled-into-button': isScrolledIntoButton}, {'vertical-scroller': editingReservation.reservationType == 'private'}, {'private-dining-with-restaurants': editingReservation.reservationType == 'private' && selectedRestaurantsObjectArray.length > 0}]"
                style="--test: blue"
                @scroll="handleScroll"
              >
                <div
                  v-if="editingReservation.reservationType == 'private'"
                  class="add-restaurant-items-holder"
                  :class="{'vertical-layout': editingReservation.reservationType == 'private'}"
                >
                  <div
                    v-for="(restaurant, index) in selectedRestaurantsObjectArray"
                    :key="`restaurant-${index}-${restaurant.name}`"
                    class="add-restaurant-item"
                  >
                    <!-- Private Dining Layout (horizontal - text left, image right) -->
                    <div
                      v-if="editingReservation.reservationType == 'private'"
                      class="mini-restaurant-card-content-horizontal"
                    >
                      <div
                        class="profile-card-horizontal flex-1-1"
                        @click="showRestaurantDetails(restaurant)"
                      >
                        <div class="profile-card-content">
                          <!-- THE shared identity cluster — one component on every restaurant row -->
                          <RestaurantTitleBlock
                            :name="restaurant.name"
                            :market="restaurant.market"
                            :group="restaurant.group || ''"
                            :status="restaurant.status || ''"
                            compact
                            @name-click="showRestaurantDetails(restaurant)"
                          />
                          <div class="profile-card-location">
                            {{ restaurant.market.split(",")[0] }}
                          </div>
                          <div
                            v-if="restaurant.acceptingReferrals === false"
                            class="profile-card-action"
                          >
                            🚫 requests via RM
                          </div>
                        </div>
                      </div>
                      <div class="mini-restaurant-image-holder-right flex-shrink-0">
                        <VImg
                          v-if="getRestaurantImageUrl(restaurant) !== noImage"
                          :width="86"
                          :height="86"
                          :src="getRestaurantImageUrl(restaurant)"
                          cover
                          class="rounded-lg"
                          :lazy-src="noImage"
                          loading="lazy"
                          @error="onImageError(restaurant)"
                        >
                          <template #placeholder>
                            <VRow
                              class="fill-height ma-0"
                              align="center"
                              justify="center"
                            >
                              <VProgressCircular
                                indeterminate
                                color="grey-lighten-5"
                              />
                            </VRow>
                          </template>
                        </VImg>
                      </div>
                      <VBtn
                        v-if="tab == '2' || tab == '3a'"
                        size="small"
                        variant="text"
                        color="#eee"
                        class="remove-restaurant-button"
                        :class="{'remove-restaurant-button-no-image': getRestaurantImageUrl(restaurant) === noImage}"
                        icon="mdi-close"
                        @click="(e) => {e.stopPropagation(); removeRestaurant(index);}"
                      />
                    </div>

                    <!-- General Reservation Layout (horizontal profile card) -->
                    <div
                      v-else
                      class="single-restaurant-card"
                    >
                      <div class="mini-restaurant-card-content">
                        <div
                          class="profile-card-horizontal"
                          @click="showRestaurantDetails(restaurant)"
                        >
                          <div class="profile-card-content">
                            <!-- THE shared identity cluster — one component on every restaurant row -->
                            <RestaurantTitleBlock
                              :name="restaurant.name"
                              :market="restaurant.market"
                              :group="restaurant.group || ''"
                              :status="restaurant.status || ''"
                              compact
                              @name-click="showRestaurantDetails(restaurant)"
                            />
                            <div class="profile-card-location">
                              {{ restaurant.market.split(",")[0] }}
                            </div>
                            <div
                              v-if="restaurant.acceptingReferrals === false"
                              class="profile-card-action"
                            >
                              🚫 requests via RM
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="mini-restaurant-image-holder">
                        <VImg
                          v-if="getRestaurantImageUrl(restaurant) !== noImage"
                          height="100%"
                          width="100%"
                          :src="getRestaurantImageUrl(restaurant)"
                          cover
                          :lazy-src="noImage"
                          loading="lazy"
                          @error="onImageError(restaurant)"
                        >
                          <template #placeholder>
                            <VRow
                              class="fill-height ma-0"
                              align="center"
                              justify="center"
                            >
                              <VProgressCircular
                                indeterminate
                                color="grey-lighten-5"
                              />
                            </VRow>
                          </template>
                        </VImg>
                      </div>
                      <VBtn
                        v-if="tab == '2' || tab == '3a'"
                        size="small"
                        variant="text"
                        color="#fff"
                        class="remove-restaurant-button"
                        :class="{'remove-restaurant-button-no-image': getRestaurantImageUrl(restaurant) === noImage}"
                        icon="mdi-close"
                        @click="(e) => {e.stopPropagation(); removeRestaurant(index);}"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <VBtn
                v-if="isScrollToLeft && (tab == '2' || tab == '3a')"
                density="compact"
                class="add-restaurant-scroll-button add-restaurant-scroll-button--left"
                icon="mdi-chevron-left"
                @click="scrollToLeft"
              />
              <VBtn
                v-if="isScrollToRight && (tab == '2' || tab == '3a')"
                density="compact"
                class="add-restaurant-scroll-button add-restaurant-scroll-button--right"
                icon="mdi-chevron-right"
                @click="scrollToRight"
              />
              <!-- Message below restaurant cards for private dining -->
              <div
                v-if="tab == '2' && editingReservation.reservationType == 'private' && privateDiningMessageText"
                class="restaurant-selection-hint text-body-1  mt-2 text-center"
                style="color: #666;"
              >
                {{ privateDiningMessageText }}
              </div>
            </div>
          </VExpandTransition>
          <template v-if="tab == '3a'">
            <div class="mt-0 text-xs text-center mb-6" />
          </template>
        </div>
        <VDefaultsProvider
          :defaults="{
            VSelect: { variant: 'solo-filled', flat: true, rounded: 'lg', bgColor: 'grey-lighten-4', density: 'comfortable' },
            VTextField: { variant: 'solo-filled', flat: true, rounded: 'lg', bgColor: 'grey-lighten-4', density: 'comfortable' },
            VTextarea: { variant: 'solo-filled', flat: true, rounded: 'lg', bgColor: 'grey-lighten-4', density: 'comfortable' },
            VCombobox: { variant: 'solo-filled', flat: true, rounded: 'lg', bgColor: 'grey-lighten-4', density: 'comfortable' },
            VAutocomplete: { variant: 'solo-filled', flat: true, rounded: 'lg', bgColor: 'grey-lighten-4', density: 'comfortable' },
          }"
        >
          <!-- RM-359: RM returned this request to Draft — why, on every step while they edit -->
          <div
            v-if="returnedDraftNote(editingReservation)"
            class="approval-feedback-note mx-4 mt-3 mb-4"
          >
            <span class="approval-feedback-note-label">{{ returnedDraftNote(editingReservation).label }}</span>
            <span
              v-if="returnedDraftNote(editingReservation).text"
              class="approval-feedback-note-text"
            >{{ returnedDraftNote(editingReservation).text }}</span>
          </div>
          <VWindow
            v-model="tab"
            :touch="{ left: () => null, right: () => null }"
            class=""
          >
            <VWindowItem value="1">
              <div class="tab-content">
                <VCardText class="step1-content">
                  <!-- 👉 Form -->
                  <VForm
                    ref="form"
                    v-model="valid"
                  >
                    <div
                      class="step1-flow"
                      @click="changesMade = true"
                      @keyup="changesMade = true"
                    >
                      <VExpandTransition>
                        <div
                          v-if="checkingGeneralAvailability && selectedRestaurantsObjectArray?.length > 0"
                          class="general-availability-holder text-center mb-6"
                        >
                          <template
                            v-for="(restaurant, index) in selectedRestaurantsObjectArray"
                            :key="index"
                          >
                            <div v-if="restaurant.generalReservationUrl">
                              <a
                                :href="generateGeneralReservationUrlWithQueryString(restaurant, editingReservation)"
                                target="_blank"
                                style="text-decoration: none;"
                                @click="saveDraft(restaurant)"
                              >
                                <span style="font-size: 16px">
                                  Check availability on {{ restaurant.generalReservationPlatform }}<VIcon
                                    size="16"
                                    class="ml-1"
                                    style="margin-bottom: 2px;"
                                    icon="mdi-open-in-new"
                                  />
                                </span>
                              </a>
                            </div>
                            <div v-else-if="restaurant.website">
                              <a
                                :href="restaurant.website"
                                target="_blank"
                                @click="saveDraft(restaurant)"
                              >
                                <span>
                                  {{ restaurant.name }} (website)<VIcon
                                    size="16"
                                    class="ml-1"
                                    style="margin-bottom: 2px;"
                                    icon="mdi-open-in-new"
                                  />
                                </span>
                              </a>
                            </div>
                          </template>
                        </div>
                      </VExpandTransition>

                      <div class="reservation-type-cards">
                        <div
                          class="reservation-type-card"
                          :class="{ 'reservation-type-card--active': editingReservation.reservationType === 'general', 'reservation-type-card--disabled': !!selectedRestaurantsNotAcceptingReferrals }"
                          @click="!selectedRestaurantsNotAcceptingReferrals && (editingReservation.reservationType = 'general')"
                        >
                          <div class="reservation-type-card-radio">
                            <div
                              class="reservation-type-radio-dot"
                              :class="{ 'reservation-type-radio-dot--active': editingReservation.reservationType === 'general' }"
                            />
                          </div>
                          <div class="reservation-type-card-text">
                            <span class="reservation-type-card-label">Request reservation for preferred time</span>
                            <span class="reservation-type-card-desc" />
                          </div>
                        </div>
                        <div
                          class="reservation-type-card"
                          :class="{ 'reservation-type-card--active': editingReservation.reservationType === 'private', 'reservation-type-card--disabled': !!selectedRestaurantsNotAcceptingReferrals }"
                          @click="!selectedRestaurantsNotAcceptingReferrals && (editingReservation.reservationType = 'private')"
                        >
                          <div class="reservation-type-card-radio">
                            <div
                              class="reservation-type-radio-dot"
                              :class="{ 'reservation-type-radio-dot--active': editingReservation.reservationType === 'private' }"
                            />
                          </div>
                          <div class="reservation-type-card-text">
                            <span class="reservation-type-card-label">Submit group/private dining inquiry</span>
                            <span class="reservation-type-card-desc">send 1 inquiry to up to 10 places</span>
                          </div>
                        </div>
                      </div>
                      <!-- Hidden radio for validation -->
                      <VRadioGroup
                        v-model="editingReservation.reservationType"
                        :rules="[requiredValidator]"
                        :disabled="!!selectedRestaurantsNotAcceptingReferrals"
                        required
                        class="d-none"
                      >
                        <VRadio value="general" />
                        <VRadio value="private" />
                      </VRadioGroup>

                      <div class="step1-next-holder">
                        <VBtn
                          v-if="editingReservation.reservationType == 'private'"
                          :disabled="!!selectedRestaurantsNotAcceptingReferrals"
                          color="primary-dark"
                          size="large"
                          block
                          class="step-next-btn"
                          @click="goToTab2"
                        >
                          Next
                        </VBtn>
                        <VBtn
                          v-if="editingReservation.reservationType == 'general'"
                          :disabled="!!selectedRestaurantsNotAcceptingReferrals"
                          color="primary-dark"
                          size="large"
                          block
                          class="step-next-btn"
                          @click="tab = '1b';"
                        >
                          Next
                        </VBtn>
                        <VBtn
                          v-if="editingReservation.reservationType == 'happenings'"
                          :disabled="!!selectedRestaurantsNotAcceptingReferrals"
                          color="primary-dark"
                          size="large"
                          block
                          class="step-next-btn"
                          @click="tab = '2c'; limitPreferredRestaurant();"
                        >
                          Next
                        </VBtn>
                      </div>
                    </div>

                    <!--
                      RM-154: not-accepting fallback on step 1. Step 1 is otherwise
                      minimal, but when the selected restaurant isn't accepting
                      requests the type-selection cards + Next are disabled, so we
                      surface the explanation, FAQs link, and Preferred Restaurants
                      alt-list inline here so the member has a path forward without
                      the screen feeling like a dead end.
                      Strict `=== false` check (not the truthy
                      `selectedRestaurantsNotAcceptingReferrals` computed) so the
                      block doesn't flash while `acceptingReferrals` is still
                      undefined during the initial restaurant data load — only
                      renders once the field has resolved to a concrete `false`. 
                    -->
                    <VCol
                      v-if="selectedRestaurantsObjectArray[0]?.acceptingReferrals === false && editingReservation.reservationType !== 'private'"
                      cols="12"
                      class="pt-2"
                    >
                      <div
                        class="text-center restaurant-key-stats"
                        style="font-size: 13px; line-height: 1.6;"
                      >
                        <p
                          class="mb-0"
                          style="font-weight: 600; color: #1a1a1a;"
                        >
                          {{ selectedRestaurantsObjectArray[0].name }} is not accepting requests via RM
                        </p>
                        <p
                          class="mt-3 mb-3"
                          style="line-height: 1.5;"
                        >
                          <span
                            class="text-primary cursor-pointer"
                            @click="isShowingPreferredRequestInfoDialog = true"
                          >How RM Works &amp; FAQs</span>
                        </p>

                        <div
                          v-if="preferredRestaurantsByTrendScore.length > 0"
                          class="pt-2"
                        >
                          <!--
                            RM-154: restore the "Preferred Restaurants" toggle pill at
                            the top of the alt-list (matches the original UX before
                            the step-1/1b split). Auto-expanded by the watcher on
                            selectedRestaurantsNotAcceptingReferrals below; the member
                            can collapse to dismiss the suggestions if they're set on
                            the not-accepting restaurant they originally chose. 
                          -->
                          <div class="d-flex justify-center mb-3">
                            <VBtn
                              size="small"
                              :variant="memberFavoriteStatsTab === 'preferred' ? 'flat' : 'outlined'"
                              :color="memberFavoriteStatsTab === 'preferred' ? 'primary-dark' : 'grey'"
                              rounded="pill"
                              style="text-transform: none; font-size: 13px; font-weight: 600;"
                              @click="memberFavoriteStatsTab = memberFavoriteStatsTab === 'preferred' ? null : 'preferred'"
                            >
                              Preferred Restaurants
                            </VBtn>
                          </div>

                          <div v-if="memberFavoriteStatsTab === 'preferred'">
                            <p
                              class="mb-3 text-center"
                              style="font-size: 16px; line-height: 1.5; color: rgba(0, 0, 0, 0.55);"
                            >
                              Restaurants that respond to requests within 24 hours are highlighted as Preferred.
                            </p>
                            <p
                              v-if="reservationsStore.preferredAcceptanceRateYtd?.acceptanceRate != null"
                              class="mb-4 text-center"
                              style="font-size: 16px; line-height: 1.4; color: rgba(0, 0, 0, 0.7);"
                            >
                              YTD acceptance rate at Preferred =
                              <strong style="color: #1a1a1a; font-weight: 700;">{{ reservationsStore.preferredAcceptanceRateYtd.acceptanceRate }}%</strong>
                            </p>
                            <p
                              v-if="selectedRestaurantsObjectArray[0]?.market"
                              class="mb-2 text-center"
                              style="font-size: 16px; line-height: 1.4; color: #1a1a1a; font-weight: 700;"
                            >
                              Preferred Restaurants in {{ selectedRestaurantsObjectArray[0].market.split(',')[0] }}
                            </p>
                            <div
                              v-for="(restaurant, index) in preferredRestaurantsByTrendScore"
                              :key="index"
                              class="preferred-restaurant-row cursor-pointer"
                              @click="selectPreferredRestaurant(restaurant)"
                            >
                              <span
                                class="profile-card-name"
                                style="font-size: 16px;"
                              >{{ restaurant.name }}</span>
                              <span
                                v-if="restaurant.group"
                                style="font-size: 14px; color: #666; margin-left: 6px;"
                              >{{ restaurant.group }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </VCol>

                    <!-- RM-154: Key Stats / Member Bookings / Preferred Restaurants alt-list moved to VWindowItem value="1b" -->

                    <!-- Instant Booking Slots when restaurant is selected and slots are available -->
                    <VCol
                      v-if="selectedRestaurantsObjectArray.length > 0"
                      v-show="availableInstantBookingSlots > 0"
                      cols="12"
                      class="pt-0"
                    >
                      <VDivider class="mb-4" />
                      <div class="instant-booking-section">
                        <div class="instant-booking-header">
                          <span class="instant-booking-title">⚡ Instant Bookings - {{ selectedRestaurantsObjectArray[0]?.name }}<span v-if="selectedRestaurantsObjectArray[0]?.market"> ({{ selectedRestaurantsObjectArray[0]?.market.split(",")[0] }})</span></span>
                        </div>

                        <InstantBookingSlotsCompact
                          :restaurant="selectedRestaurantsObjectArray[0]"
                          external-checkout
                          :show-submit-request="false"
                          @book-slot="handleInstantBookingSlot"
                          @slots-loaded="handleInstantBookingSlotsLoaded"
                        />
                      </div>
                    </VCol>
                  </VForm>
                </VCardText>
              </div>
            </VWindowItem>
            <!-- RM-154: Intermediary request-guidance screen (general reservationType only) -->
            <VWindowItem value="1b">
              <div class="tab-content">
                <VCardText class="pt-2">
                  <VForm
                    ref="form"
                    v-model="valid"
                  >
                    <!-- Key Stats + response time (Preferred restaurants) -->
                    <VCol
                      v-if="!selectedRestaurantsNotPreferred && selectedRestaurantsObjectArray?.[0]?.name"
                      cols="12"
                      class="pt-0"
                    >
                      <div
                        class="mt-3 text-center restaurant-key-stats"
                        style="font-size: 14px; line-height: 1.6;"
                      >
                        <!-- PLATINUM MEMBERS: full stats -->
                        <template v-if="reservationOwner.isPlatinum">
                          <p
                            class="mb-0"
                            style="font-size: 16px; font-weight: 600; color: #1a1a1a;"
                          >
                            {{ selectedRestaurantsObjectArray[0].name }} is a
                            <button
                              type="button"
                              class="preferred-restaurant-link text-primary"
                              @click="isRestaurantsInfoDialogOpen = true"
                            >
                              Preferred Restaurant
                            </button>
                          </p>

                          <template v-if="editingReservation.reservationType !== 'private'">
                            <p
                              v-if="selectedRestaurantStats?.responseTimeValue"
                              class="mt-2 mb-0"
                              style="font-size: 16px;"
                            >
                              Response time*: generally within <strong>{{ selectedRestaurantStats.responseTimeValue }}</strong>
                            </p>

                            <p
                              v-if="selectedRestaurantStats?.acceptanceRate != null"
                              class="mb-0"
                              style="font-size: 16px;"
                            >
                              <template v-if="showPreferredRestaurantNoBookingsMessage">
                                No bookings within the last 12 months
                              </template>
                              <template v-else>
                                Acceptance rate*: <strong>{{ selectedRestaurantStats.acceptanceRate }}%</strong>
                              </template>
                            </p>
                            <p
                              v-if="selectedRestaurantStats?.acceptanceRate != null"
                              class="mb-0"
                              style="font-size: 13px; color: rgba(0, 0, 0, 0.5);"
                            >
                              (last 12 months)
                            </p>
                            <p
                              v-if="selectedRestaurantStats?.acceptanceRate != null"
                              class="mb-0"
                              style="font-size: 13px; color: rgba(0, 0, 0, 0.5);"
                            >
                              <i>*varies by member profile, spend &amp; availability</i>
                            </p>
                            <p
                              class="mt-3 mb-3"
                              style="line-height: 1.5;"
                            >
                              <span
                                class="text-primary cursor-pointer"
                                @click="isShowingPreferredRequestInfoDialog = true"
                              >How RM Works &amp; FAQs</span>
                            </p>

                            <!-- RM-154: Continue CTA placed below FAQs, above Member Bookings -->
                            <div class="rm154-continue-holder mt-2 mb-4">
                              <VBtn
                                :disabled="!!selectedRestaurantsNotAcceptingReferrals"
                                color="primary-dark"
                                size="large"
                                block
                                class="step-next-btn"
                                @click="tab = '3a'; limitPreferredRestaurant();"
                              >
                                Next
                              </VBtn>
                            </div>

                            <!-- Member Bookings toggle -->
                            <div
                              v-if="showViewBookings"
                              class="mb-2 cursor-pointer d-flex align-center justify-center"
                              style="gap: 4px;"
                              @click="showBookingsList = !showBookingsList"
                            >
                              <span style="font-weight: 700; color: #000; font-size: 14px;">Member Bookings</span>
                              <VIcon
                                :icon="showBookingsList ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                                size="18"
                                color="#000"
                              />
                            </div>

                            <!-- Expandable bookings list -->
                            <div
                              v-if="showBookingsList && showViewBookings"
                              class="pt-2"
                            >
                              <MemberBookingCard
                                v-for="(item, index) in recentBookingsForSelectedRestaurant"
                                :key="index"
                                :booking="item"
                              />
                            </div>
                          </template>
                        </template>

                        <!-- NON-PLATINUM / NOT LOGGED IN: simplified view -->
                        <template v-else>
                          <p
                            class="mb-0"
                            style="font-weight: 600; color: #1a1a1a;"
                          >
                            {{ selectedRestaurantsObjectArray[0].name }} is a
                            <button
                              type="button"
                              class="preferred-restaurant-link text-primary"
                              @click="isRestaurantsInfoDialogOpen = true"
                            >
                              Preferred Restaurant
                            </button>
                          </p>

                          <template v-if="editingReservation.reservationType !== 'private'">
                            <p
                              v-if="selectedRestaurantStats?.responseTimeLabel"
                              class="mb-3"
                            >
                              {{ selectedRestaurantStats.responseTimeLabel }}
                            </p>

                            <p class="mb-2">
                              View Additional Request Insights<br>
                              <span style="font-size: 12px; color: #999;">
                                available to
                                <span
                                  class="text-primary cursor-pointer"
                                  style="text-decoration: underline;"
                                  @click="openPlatinumMembersDialog"
                                >Platinum members</span>
                              </span>
                            </p>

                            <!-- RM-154: Continue CTA (non-Platinum Preferred path) -->
                            <div class="rm154-continue-holder mt-2 mb-4">
                              <VBtn
                                :disabled="!!selectedRestaurantsNotAcceptingReferrals"
                                color="primary-dark"
                                size="large"
                                block
                                class="step-next-btn"
                                @click="tab = '3a'; limitPreferredRestaurant();"
                              >
                                Next
                              </VBtn>
                            </div>
                          </template>
                        </template>
                      </div>
                    </VCol>
                    <!-- MEMBER FAVORITE: Platinum stats panel -->
                    <VCol
                      v-if="showPreferredRestaurantsInsightsPanel"
                      cols="12"
                      class="pt-0"
                    >
                      <div
                        class="mt-3 text-center restaurant-key-stats"
                        style="font-size: 14px; line-height: 1.6;"
                      >
                        <p
                          class="mb-0"
                          style="font-size: 16px; font-weight: 600; color: #1a1a1a;"
                        >
                          <template v-if="selectedRestaurantsNotAcceptingReferrals">
                            {{ selectedRestaurantsObjectArray[0].name }} is not accepting requests via RM
                          </template>
                          <template v-else>
                            Request will be sent to {{ selectedRestaurantsObjectArray[0].name }}
                          </template>
                        </p>
                        <p
                          class="mt-2 mb-0"
                          style="font-size: 16px; color: #666;"
                        >
                          <template v-if="!selectedRestaurantsNotAcceptingReferrals">
                            Response &amp; timing not guaranteed
                          </template>
                        </p>
                        <p
                          v-if="reservationOwner.isPlatinum && selectedRestaurantStats?.acceptanceRate != null"
                          class="mb-0"
                          style="font-size: 16px;"
                        >
                          Acceptance rate*: <strong>{{ selectedRestaurantStats.acceptanceRate }}%</strong>
                        </p>
                        <p
                          v-if="reservationOwner.isPlatinum && selectedRestaurantStats?.acceptanceRate != null"
                          class="mb-0"
                          style="font-size: 13px; line-height: 1.5; margin-top: 8px; color: rgba(0, 0, 0, 0.5);"
                        >
                          (last 12 months)
                        </p>
                        <p
                          v-if="reservationOwner.isPlatinum && selectedRestaurantStats?.acceptanceRate != null"
                          class="mb-0"
                          style="font-size: 13px; line-height: 1.5; margin-top: 8px; color: rgba(0, 0, 0, 0.5);"
                        >
                          <i>*varies by member profile, spend &amp; availability</i>
                        </p>
                        <p
                          v-if="reservationOwner.isPlatinum"
                          class="mt-3 mb-3"
                          style="font-size: 13px; line-height: 1.5;"
                        >
                          <span
                            class="text-primary cursor-pointer"
                            @click="isShowingPreferredRequestInfoDialog = true"
                          >How RM Works &amp; FAQs</span>
                        </p>

                        <!-- RM-154: Continue CTA placed below FAQs, above Preferred Restaurants list -->
                        <div
                          v-if="!selectedRestaurantsNotAcceptingReferrals"
                          class="rm154-continue-holder mt-2 mb-4"
                        >
                          <VBtn
                            color="primary-dark"
                            size="large"
                            block
                            class="step-next-btn"
                            @click="tab = '3a'; limitPreferredRestaurant();"
                          >
                            Next
                          </VBtn>
                        </div>
                        <div
                          v-if="selectedRestaurantsNotAcceptingReferrals"
                          class="mt-2 mb-4 text-caption"
                          style="color: #b00020;"
                        >
                          {{ $t('reservation.preferred.removeToProceeed') }}
                        </div>

                        <div
                          v-if="preferredRestaurantsByTrendScore.length > 0 || (!selectedRestaurantsNotAcceptingReferrals && showViewBookings)"
                          class="d-flex justify-center"
                          style="gap: 8px;"
                        >
                          <VBtn
                            v-if="preferredRestaurantsByTrendScore.length > 0"
                            size="small"
                            :variant="memberFavoriteStatsTab === 'preferred' ? 'flat' : 'outlined'"
                            :color="memberFavoriteStatsTab === 'preferred' ? 'primary-dark' : 'grey'"
                            rounded="pill"
                            style="text-transform: none; font-size: 13px; font-weight: 600;"
                            @click="memberFavoriteStatsTab = memberFavoriteStatsTab === 'preferred' ? null : 'preferred'"
                          >
                            Preferred Restaurants
                          </VBtn>
                          <VBtn
                            v-if="!selectedRestaurantsNotAcceptingReferrals && showViewBookings"
                            size="small"
                            :variant="memberFavoriteStatsTab === 'bookings' ? 'flat' : 'outlined'"
                            :color="memberFavoriteStatsTab === 'bookings' ? 'primary-dark' : 'grey'"
                            rounded="pill"
                            style="text-transform: none; font-size: 13px; font-weight: 600;"
                            @click="memberFavoriteStatsTab = memberFavoriteStatsTab === 'bookings' ? null : 'bookings'"
                          >
                            Member Bookings
                          </VBtn>
                        </div>

                        <div
                          v-if="memberFavoriteStatsTab === 'bookings' && !selectedRestaurantsNotAcceptingReferrals && showViewBookings"
                          class="pt-2 text-left"
                        >
                          <MemberBookingCard
                            v-for="(item, index) in recentBookingsForSelectedRestaurant"
                            :key="index"
                            :booking="item"
                          />
                        </div>

                        <div
                          v-if="memberFavoriteStatsTab === 'preferred' && preferredRestaurantsByTrendScore.length > 0"
                          class="pt-2"
                        >
                          <p
                            class="mb-3 text-center"
                            style="font-size: 16px; line-height: 1.5; color: rgba(0, 0, 0, 0.55);"
                          >
                            Restaurants that respond to requests within 24 hours are highlighted as Preferred.
                          </p>
                          <p
                            v-if="reservationsStore.preferredAcceptanceRateYtd?.acceptanceRate != null"
                            class="mb-4 text-center"
                            style="font-size: 16px; line-height: 1.4; color: rgba(0, 0, 0, 0.7);"
                          >
                            YTD acceptance rate at Preferred =
                            <strong style="color: #1a1a1a; font-weight: 700;">{{ reservationsStore.preferredAcceptanceRateYtd.acceptanceRate }}%</strong>
                          </p>
                          <p
                            v-if="selectedRestaurantsObjectArray[0]?.market"
                            class="mb-2 text-center"
                            style="font-size: 16px; line-height: 1.4; color: #1a1a1a; font-weight: 700;"
                          >
                            Preferred Restaurants in {{ selectedRestaurantsObjectArray[0].market.split(',')[0] }}
                          </p>
                          <div
                            v-for="(restaurant, index) in preferredRestaurantsByTrendScore"
                            :key="index"
                            class="preferred-restaurant-row cursor-pointer"
                            @click="selectPreferredRestaurant(restaurant)"
                          >
                            <span
                              class="profile-card-name"
                              style="font-size: 16px;"
                            >{{ restaurant.name }}</span>
                            <span
                              v-if="restaurant.group"
                              style="font-size: 14px; color: #666; margin-left: 6px;"
                            >{{ restaurant.group }}</span>
                          </div>
                        </div>
                      </div>
                    </VCol>

                    <!-- MEMBER FAVORITE: non-Platinum / not accepting referrals -->
                    <VCol>
                      <div
                        v-if="selectedRestaurantsNotPreferred && !showPreferredRestaurantsInsightsPanel && (!reservationOwner.isPlatinum || selectedRestaurantsNotAcceptingReferrals)"
                        id="disclamer-not-preferred"
                        class="mt-2 justify-center text-center"
                      >
                        <template v-if="!selectedRestaurantsNotAcceptingReferrals">
                          Request will be sent to
                          <span
                            class="text-primary cursor-pointer"
                            @click="viewingDetailsRestaurant = selectedRestaurantsObjectArray[0]"
                          >{{ selectedRestaurantsObjectArray[0].name }}</span><br>
                          Response &amp; timing not guaranteed
                          <p class="mb-0 mt-2">
                            View Additional Request Insights<br>
                            <span style="font-size: 12px; color: #999;">
                              available to
                              <span
                                class="text-primary cursor-pointer"
                                style="text-decoration: underline;"
                                @click="openPlatinumMembersDialog"
                              >Platinum members</span>
                            </span>
                          </p>

                          <!-- RM-154: Continue CTA (non-Platinum non-Preferred path) -->
                          <div class="rm154-continue-holder mt-2 mb-4">
                            <VBtn
                              color="primary-dark"
                              size="large"
                              block
                              class="step-next-btn"
                              @click="tab = '3a'; limitPreferredRestaurant();"
                            >
                              Next
                            </VBtn>
                          </div>
                        </template>
                      </div>
                    </VCol>
                  </VForm>
                </VCardText>
              </div>
            </VWindowItem>
            <VWindowItem value="2">
              <div class="tab-content">
                <VCardText class="pt-2">
                  <!-- 👉 Form -->
                  <VForm
                    ref="form"
                    v-model="valid"
                  >
                    <VCol
                      cols="12"
                      class="d-flex flex-wrap justify-center gap-4 bottom-buttons"
                    >
                      <VSpacer />
                      <div class="action-buttons d-flex flex-wrap justify-center gap-4">
                        <VBtn
                          v-if="editingReservation?.reservationType == 'private'"
                          :disabled="selectedRestaurantsObjectArray.length == 0"
                          @click="tab = '2b';"
                        >
                          Next
                        </VBtn>
                        <VBtn
                          v-if="editingReservation?.reservationType == 'general'"
                          :disabled="selectedRestaurantsObjectArray.length == 0"
                          @click="tab = '3a';"
                        >
                          {{ $t('reservation.actions.next') }}
                        </VBtn>
                        <div
                          v-if="selectedRestaurantsNotAcceptingReferrals"
                          class="text-center mt-1 text-caption"
                        >
                          {{ selectedRestaurantsNotAcceptingReferrals.join(", ") }} 🚫 {{ $t('reservation.preferred.notAcceptingReferrals') }}
                        </div>
                      </div>
                      <VSpacer />
                    </VCol>
                  </VForm>
                </VCardText>
              </div>
            </VWindowItem>
            <VWindowItem value="2b">
              <div class="tab-content">
                <VCardText class="pt-2">
                  <!-- 👉 Form -->
                  <VForm
                    ref="form"
                    v-model="valid"
                  >
                    <VRow
                      @click="changesMade = true"
                      @keyup="changesMade = true"
                    >
                      <!-- Date / Party Size row -->
                      <div class="w-100 mx-3 mb-4">
                        <div class="d-flex datetime-picker-group">
                          <div class="datetime-picker-field datetime-field-date">
                            <span class="datetime-label">{{ $t('reservation.labels.date') }}*</span>
                            <AppDateTimePicker
                              v-model="editingReservation.date"
                              :config="editReservationDatePickerConfig"
                              :min-selectable-date="minSelectableDate"
                              :max-selectable-date="maxSelectableDate"
                              :rules="[requiredValidator]"
                              required
                              hide-details
                            />
                          </div>
                          <div
                            class="datetime-picker-field datetime-field-partysize"
                            @click="scrollToSelected"
                          >
                            <span class="datetime-label">{{ $t('reservation.labels.partySize') }}*</span>
                            <VSelect
                              v-model="editingReservation.partySize"
                              item-title="text"
                              item-value="value"
                              :items="partySizeOptions"
                              :rules="[requiredValidator]"
                              required
                              hide-details
                            />
                          </div>
                        </div>
                        <!-- RM-161: booking window helper -->
                        <div
                          v-if="bookingWindowMessage"
                          class="text-xs text-center mb-1 text-medium-emphasis"
                        >
                          {{ bookingWindowMessage }}
                        </div>
                        <!-- RM-161: out-of-window auto-clear message -->
                        <div
                          v-if="bookingWindowClearedMessage"
                          class="text-xs text-center mb-1 text-error"
                        >
                          {{ bookingWindowClearedMessage }}
                        </div>
                      </div>

                      <!-- Time row -->
                      <div class="w-100 mx-3 mb-4">
                        <div class="d-flex datetime-picker-group">
                          <div
                            class="datetime-picker-field datetime-field-time"
                            @click="checkPreferredTime(); scrollToSelected();"
                          >
                            <span class="datetime-label">{{ $t('reservation.labels.time') }}*</span>
                            <VSelect
                              v-model="editingReservation.preferredTime"
                              :items="preferredTimeOptionsBasedOnReservationType"
                              no-data-text="No times available"
                              :rules="[requiredValidator]"
                              required
                              hide-details
                            />
                          </div>
                        </div>
                      </div>

                      <!-- Earliest / Latest time range -->
                      <div class="w-100 mx-3 mb-4">
                        <div class="d-flex datetime-picker-group">
                          <div
                            class="datetime-picker-field"
                            @click="checkEarliestTime(); scrollToSelected();"
                          >
                            <span class="datetime-label">{{ $t('reservation.labels.earliestStartTime') }}*</span>
                            <VSelect
                              v-model="editingReservation.earliestTime"
                              :items="earliestTimeOptionsBasedOnReservationType"
                              no-data-text="No times available"
                              :rules="[requiredValidator]"
                              required
                              hide-details
                            />
                          </div>
                          <div
                            class="datetime-picker-field"
                            @click="checkLatestTime(); scrollToSelected();"
                          >
                            <span class="datetime-label">{{ $t('reservation.labels.latestStartTime') }}*</span>
                            <VSelect
                              v-model="editingReservation.latestTime"
                              :items="latestTimeOptionsBasedOnReservationType"
                              no-data-text="No times available"
                              :rules="[requiredValidator]"
                              required
                              hide-details
                            />
                          </div>
                        </div>
                      </div>

                      <!-- Restaurant Hours (collapsible, RestaurantProfile-style) -->
                      <div
                        v-if="selectedRestaurantsFullHours.length > 0 && editingReservation.date"
                        class="w-100 mx-3 mb-2 d-flex flex-column align-center"
                      >
                        <div
                          v-for="rh in selectedRestaurantsFullHours"
                          :key="rh.name"
                        >
                          <div
                            class="restaurant-hours-holder"
                            @click="showAllHours = !showAllHours"
                          >
                            <span
                              v-if="getSelectedDateHours(rh) && !showAllHours"
                              class="text-caption text-medium-emphasis"
                            >
                              <VIcon
                                size="12"
                                icon="mdi-clock-outline"
                                class="mr-1"
                              />
                              {{ rh.name }} hours: {{ getSelectedDateHours(rh) }}
                            </span>
                            <div v-else-if="showAllHours">
                              <span
                                class="text-caption text-medium-emphasis d-block mb-1"
                              >{{ rh.name }} hours:</span>
                              <span
                                v-for="day in rh.hours"
                                :key="day"
                                class="text-caption text-medium-emphasis d-block"
                              >{{ day }}</span>
                            </div>
                            <VBtn
                              density="compact"
                              size="small"
                              class="restaurant-hours-toggle"
                              variant="text"
                              color="secondary"
                              :icon="!showAllHours ? 'mdi-chevron-down' : 'mdi-chevron-up'"
                              @click.stop="showAllHours = !showAllHours"
                            />
                          </div>
                        </div>
                      </div>

                      <div
                        v-if="editingReservation.reservationType == 'private'"
                        class="w-100 mx-3 mt-2"
                      >
                        <div class="text-center mb-6">
                          <!--
                            RM-194: same-time conflict gate for group/private inquiries (mirrors the
                            general flow at tab 3a). Only a pending/accepted REGULAR reservation at the
                            same date/time blocks — group/private inquiries are excluded as conflict
                            candidates (so multiple are allowed). Surfaced here so the member can't
                            continue past the date/time step. 
                          -->
                          <VAlert
                            v-if="conflictingConfirmedRequest"
                            class="text-center mt-1 mb-2"
                            type="warning"
                          >
                            <div>
                              Request conflicts with existing reservation at
                              {{ conflictingConfirmedRequest.restaurant }}, please revise preferred time. Must be 75
                              minutes before or after existing reservation start time.
                            </div>
                            <div class="mt-2">
                              questions? email <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
                            </div>
                          </VAlert>
                          <VAlert
                            v-if="conflictingPendingRequest"
                            class="text-center mt-1 mb-2"
                            type="warning"
                          >
                            <div>
                              Request conflicts with pending reservation request for
                              {{ conflictingPendingRequest.restaurant }}, please revise preferred time
                              (needs to be 75 minutes before or after current request time range) or rescind pending request
                              first.
                            </div>
                            <div class="mt-2">
                              questions? email <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
                            </div>
                          </VAlert>
                          <VBtn
                            :disabled="selectedRestaurantsNotAcceptingReferrals || !editingReservation.date || !editingReservation.preferredTime || !editingReservation.earliestTime || !editingReservation.latestTime || !editingReservation.partySize || (conflictingPendingRequest != false) || (conflictingConfirmedRequest != false)"
                            color="primary-dark"
                            size="large"
                            block
                            class="occasion-next-btn"
                            @click="limitPreferredRestaurant(); tab = '4b'"
                          >
                            Next
                          </VBtn>
                          <div
                            v-if="selectedRestaurantsNotAcceptingReferrals"
                            class="mt-2 text-caption"
                          >
                            {{ $t('reservation.preferred.removeToProceeed') }}
                          </div>
                        </div>
                      </div>
                      <div
                        v-else-if="selectedRestaurantsNotPreferred && !reservationOwner.isPlatinum && !platinumRequested"
                        class="w-100 mx-3"
                      >
                        <div class="text-center">
                          <h3>
                            <RouterLink
                              :to="{ name: 'plans' }"
                              @click="reservationsStore.saveEditReservation()"
                            >
                              {{ $t('reservation.preferred.upgradeToPlatinum') }}
                            </RouterLink>
                            {{ $t('reservation.preferred.orSelectPreferred') }}
                          </h3>
                        </div>
                      </div>

                      <div
                        v-else-if="selectedRestaurantsNotPreferred && platinumRequested"
                        class="w-100 mx-3"
                      >
                        <div class="text-center">
                          {{ $t('reservation.preferred.upgradeRequestReceived') }}<br>
                          {{ $t('reservation.preferred.followUpAsap') }}<br>
                          {{ $t('reservation.preferred.interimPreferred') }}
                        </div>
                      </div>
                    </VRow>
                  </VForm>
                </VCardText>
              </div>
            </VWindowItem>
            <VWindowItem value="2c">
              <div class="w-100 pa-2">
                <HappeningViewerAndEditor />
              </div>
            </VWindowItem>
            <VWindowItem value="3a">
              <div class="tab-content">
                <VCardText class="pt-0">
                  <!-- 👉 Form -->
                  <VForm
                    ref="form"
                    v-model="valid"
                  >
                    <VRow
                      @click="changesMade = true"
                      @keyup="changesMade = true"
                    >
                      <!-- Date / Party Size picker -->
                      <div class="w-100 mx-3 mb-1">
                        <div class="d-flex datetime-picker-group">
                          <div class="datetime-picker-field datetime-field-date">
                            <span class="datetime-label">{{ $t('reservation.labels.date') }}</span>
                            <AppDateTimePicker
                              v-model="editingReservation.date"
                              :config="{ disableMobile: true, altInput: true, altFormat: 'D n/j', dateFormat: 'Y-m-d', maxDate: moment().add(1,'year').format('YYYY-MM-DD'), position: 'below' }"
                              :min-selectable-date="minSelectableDate"
                              :max-selectable-date="maxSelectableDate"
                              :rules="[requiredValidator]"
                              required
                              hide-details
                            />
                          </div>
                          <div
                            class="datetime-picker-field datetime-field-partysize"
                            @click="scrollToSelected"
                          >
                            <span class="datetime-label">{{ $t('reservation.labels.partySize') }}</span>
                            <VSelect
                              v-model="editingReservation.partySize"
                              item-title="text"
                              item-value="value"
                              :items="partySizeOptions"
                              :rules="[requiredValidator]"
                              required
                              hide-details
                            />
                          </div>
                        </div>
                        <div
                          v-if="selectedRestaurantsPartySizeLimits.length > 0"
                          class="text-center mb-1 text-medium-emphasis"
                          style="font-size: 14px;"
                        >
                          Restaurant's max party size for reservations is {{ selectedRestaurantsPartySizeLimits[selectedRestaurantsPartySizeLimits.length-1].maxPartySize }}
                        </div>
                        <!-- RM-161: booking window helper -->
                        <div
                          v-if="bookingWindowMessage"
                          class="text-xs text-center mb-1 text-medium-emphasis"
                        >
                          {{ bookingWindowMessage }}
                        </div>
                        <!-- RM-161: out-of-window auto-clear message -->
                        <div
                          v-if="bookingWindowClearedMessage"
                          class="text-xs text-center mb-1 text-error"
                        >
                          {{ bookingWindowClearedMessage }}
                        </div>
                      </div>
                      <!-- Time picker (separate row) -->
                      <div class="w-100 mx-3 mb-1">
                        <div class="d-flex datetime-picker-group">
                          <div
                            class="datetime-picker-field datetime-field-time-full"
                            @click="checkPreferredTime(); scrollToSelected();"
                          >
                            <span class="datetime-label">{{ $t('reservation.labels.preferredStartTime') }}</span>
                            <VSelect
                              v-model="editingReservation.preferredTime"
                              :items="preferredTimeOptionsBasedOnReservationType"
                              no-data-text="No times available"
                              :rules="[requiredValidator]"
                              required
                              content-class="v-menu__content--fixed"
                              :error-messages="timeOutOfRangeMessage"
                              :hide-details="!timeOutOfRangeMessage"
                            />
                          </div>
                        </div>
                      </div>


                      <!-- Restaurant Hours (collapsible, RestaurantProfile-style) -->
                      <div
                        v-if="selectedRestaurantsFullHours.length > 0 && editingReservation.date"
                        class="w-100 mx-3 mb-2 d-flex flex-column align-center"
                      >
                        <div
                          v-for="rh in selectedRestaurantsFullHours"
                          :key="rh.name"
                        >
                          <div
                            class="restaurant-hours-holder"
                            @click="showAllHours = !showAllHours"
                          >
                            <span
                              v-if="getSelectedDateHours(rh) && !showAllHours"
                              class="text-caption text-medium-emphasis"
                            >
                              <VIcon
                                size="12"
                                icon="mdi-clock-outline"
                                class="mr-1"
                              />
                              Restaurant's hours: {{ getSelectedDateHours(rh) }}
                            </span>
                            <div v-else-if="showAllHours">
                              <span
                                class="text-caption text-medium-emphasis d-block mb-1"
                              >Restaurant's hours:</span>
                              <span
                                v-for="day in rh.hours"
                                :key="day"
                                class="text-caption text-medium-emphasis d-block"
                              >{{ day }}</span>
                            </div>
                            <VBtn
                              density="compact"
                              size="small"
                              class="restaurant-hours-toggle"
                              variant="text"
                              color="secondary"
                              :icon="!showAllHours ? 'mdi-chevron-down' : 'mdi-chevron-up'"
                              @click.stop="showAllHours = !showAllHours"
                            />
                          </div>
                        </div>
                      </div>
                      <!-- Earliest / Latest time range -->
                      <div class="w-100 mx-3 mb-1">
                        <div
                          class="text-center mb-1"
                          style="font-size: 14px; color: rgba(0, 0, 0, 0.7);"
                        >
                          If preferred time is not available, what start times work?
                        </div>
                        <div class="d-flex datetime-picker-group">
                          <div
                            class="datetime-picker-field"
                            @click="checkEarliestTime(); scrollToSelected();"
                          >
                            <span class="datetime-label">{{ $t('reservation.labels.earliestStartTime') }}</span>
                            <VSelect
                              v-model="editingReservation.earliestTime"
                              :items="earliestTimeOptionsBasedOnReservationType"
                              no-data-text="No times available"
                              :rules="[requiredValidator]"
                              required
                              :disabled="!editingReservation.date"
                              hide-details
                            />
                          </div>
                          <div
                            class="datetime-picker-field"
                            @click="checkLatestTime(); scrollToSelected();"
                          >
                            <span class="datetime-label">{{ $t('reservation.labels.latestStartTime') }}</span>
                            <VSelect
                              v-model="editingReservation.latestTime"
                              :items="latestTimeOptionsBasedOnReservationType"
                              no-data-text="No times available"
                              :rules="[requiredValidator]"
                              required
                              :disabled="!editingReservation.date"
                              hide-details
                            />
                          </div>
                        </div>
                      </div>

                      <div class="w-100 mx-3 mb-1 text-center">
                        <span
                          class="text-medium-emphasis ml-2"
                          style="font-size: 14px;"
                        >
                          Wider time ranges <strong>increase acceptance</strong>
                        </span>
                      </div>
                      <VCol
                        v-if="editingReservation.reservationType != 'private' "
                        cols="12"
                        class="text-center mt-1"
                      >
                        <VExpandTransition>
                          <template v-if="!checkingGeneralAvailability">
                            <VBtn
                              class="mb-2"
                              :disabled="!editingReservation.date || !editingReservation.partySize || !editingReservation.preferredTime || !selectedRestaurantsObjectArray"
                              @click="checkingGeneralAvailability = true"
                            >
                              check general availability
                            </VBtn>
                          </template>
                        </VExpandTransition>

                        <VAlert
                          v-if="conflictingConfirmedRequest"
                          class="text-center mt-1"
                          type="warning"
                        >
                          <div>
                            Request conflicts with existing reservation at
                            {{ conflictingConfirmedRequest.restaurant }}, please revise preferred time. Must be 75
                            minutes before or after existing reservation start time.
                          </div>
                          <div class="mt-2">
                            questions? email <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
                          </div>
                        </VAlert>
                        <VAlert
                          v-if="conflictingPendingRequest"
                          class="text-center mt-1"
                          type="warning"
                        >
                          <div>
                            Request conflicts with pending reservation request for
                            {{ conflictingPendingRequest.restaurant }}, please revise preferred time
                            (needs to be 75 minutes before or after current request time range) or rescind pending reservation request
                            first.
                          </div>
                          <div class="mt-2">
                            questions? email <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
                          </div>
                        </VAlert>
                        <div class="text-center mt-1">
                          <VBtn
                            v-if="!selectedRestaurantsNotAcceptingReferrals"
                            color="primary-dark"
                            size="large"
                            block
                            class="step-next-btn mt-1"
                            :disabled="!myUser || (conflictingConfirmedRequest != false) || (conflictingPendingRequest != false) || !selectedRestaurantsObjectArray?.[0] || !!timeOutOfRangeMessage"
                            @click="showFavoriteRestaurantDialogIfNeeded"
                          >
                            Next
                          </VBtn>
                          <!-- Instant Booking Slots Section -->
                          <div
                            v-if="editingReservation.date && instantBookingSlotsByRestaurant.length > 0"
                            class="instant-booking-section mt-4"
                          >
                            <div class="instant-booking-header">
                              <span class="instant-booking-title">⚡ Instant Bookings - {{ moment(editingReservation.date).format('ddd M/D') }}</span>
                              <span class="instant-booking-subtitle">{{ $t('reservation.instantBookings.subtitle') }}</span>
                            </div>
                            <div class="instant-booking-restaurants">
                              <div
                                v-for="restaurantGroup in instantBookingSlotsByRestaurant"
                                :key="restaurantGroup.restaurantId"
                                class="instant-booking-restaurant"
                              >
                                <div class="restaurant-name-row">
                                  <span class="restaurant-name">{{ restaurantGroup.restaurantName }}</span>
                                  <span class="restaurant-market">{{ restaurantGroup.restaurantMarket }}</span>
                                </div>
                                <div class="instant-slots-row">
                                  <button
                                    v-for="slot in restaurantGroup.slots.slice(0, 4)"
                                    :key="slot._id"
                                    type="button"
                                    class="instant-slot-chip"
                                    @click="handleInstantBookingSlot(slot.seoId || slot._id)"
                                  >
                                    <span class="slot-time">{{ formatInstantBookingTime(slot.time) }}</span>
                                    <span class="slot-party">
                                      <VIcon
                                        icon="mdi-account-outline"
                                        size="12"
                                      />
                                      {{ slot.partySize }}
                                    </span>
                                  </button>
                                  <span
                                    v-if="restaurantGroup.slots.length > 4"
                                    class="more-slots"
                                  >
                                    +{{ restaurantGroup.slots.length - 4 }} more
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            v-else-if="editingReservation.date && loadingInstantBookingSlots"
                            class="instant-booking-loading mt-4"
                          >
                            <VProgressCircular
                              indeterminate
                              size="20"
                              width="2"
                              color="primary"
                            />
                            <span class="loading-text">{{ $t('reservation.instantBookings.checkingAvailability') }}</span>
                          </div>
                          <div
                            v-if="selectedRestaurantsNotAcceptingReferrals"
                            class="mt-1 text-caption"
                          >
                            🚫 {{ selectedRestaurantsNotAcceptingReferrals.join(", ") }} {{ $t('reservation.preferred.notAcceptingRequestsViaRM') }}<br>
                            {{ $t('reservation.preferred.tryAnotherFavorite') }}
                          </div>
                        </div>
                      </VCol>
                      <VCol
                        cols="12"
                      />
                    </VRow>
                  </VForm>
                </VCardText>
              </div>
            </VWindowItem>
            <VWindowItem value="4a">
              <div class="tab-content">
                <VCardText class="pa-2 pb-8">
                  <!-- 👉 Form -->
                  <VForm
                    ref="form"
                    v-model="valid"
                  >
                    <div class="d-flex flex-column">
                      <div
                        class="w-100 px-3 mb-4"
                        @click="changesMade = true; checkEarliestTime();"
                        @keyup="changesMade = true"
                      >
                        <VSelect
                          v-model="editingReservation.earliestTime"
                          label="Earliest Start Time"
                          :items="earliestTimeOptionsBasedOnReservationType"
                          no-data-text="No times available for this date and reservation type"
                          :rules="[requiredValidator]"
                          required
                          :disabled="!editingReservation.date"
                        />
                      </div>
                      <div
                        class="w-100 px-3 mb-4"
                        @click="changesMade = true; checkLatestTime();"
                        @keyup="changesMade = true"
                      >
                        <VSelect
                          v-model="editingReservation.latestTime"
                          label="Latest Start Time"
                          :items="latestTimeOptionsBasedOnReservationType"
                          no-data-text="No times available for this date and reservation type"
                          :rules="[requiredValidator]"
                          required
                          :disabled="!editingReservation.date"
                        />
                      </div>
                      <div class="w-100">
                        <div
                          v-if="editingReservation.earliestTime && editingReservation.latestTime"
                          class="mx-auto mb-4 text-center"
                        >
                          <span>
                            <b>Preferred Time:</b> {{ editingReservation.preferredTime }}<br>
                            <b>Start Time Range: </b>{{ editingReservation.earliestTime }} - {{ editingReservation.latestTime }}<br>
                          </span>
                        </div>
                      </div>
                      <div class="mx-auto text-center text-width-limiter">
                        <div class="text-center">
                          <div class="reservation-response-item-detail mt-2">
                            Request will be declined if start time range n/a
                          </div>
                          <VBtn
                            class="mt-6"
                            :disabled="!editingReservation.earliestTime || !editingReservation.latestTime || !!selectedRestaurantsNotAcceptingReferrals"
                            @click="tab='6b'"
                          >
                            Next
                          </VBtn>
                          <div
                            v-if="selectedRestaurantsNotAcceptingReferrals"
                            class="mt-1 text-caption"
                          >
                            {{ selectedRestaurantsNotAcceptingReferrals.join(", ") }} 🚫 {{ $t('reservation.preferred.notAcceptingReferrals') }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </VForm>
                </VCardText>
              </div>
            </VWindowItem>
            <!-- 5p: Spend & Payment (spend now; card + terms added next commit) -->
            <VWindowItem value="5p">
              <div class="tab-content occasion-step">
                <VForm
                  ref="form"
                  v-model="valid"
                >
                  <!-- Context anchor: restaurant + booking summary -->
                  <div class="occasion-context">
                    <div class="occasion-context-name">
                      {{ selectedRestaurantsObjectArray[0]?.name }}
                      <span
                        v-if="selectedRestaurantsObjectArray[0]?.market"
                        class="occasion-context-market"
                      >({{ selectedRestaurantsObjectArray[0].market.split(',')[0].trim() }})</span>
                    </div>
                    <div class="occasion-context-details">
                      {{ editingReservation.date ? moment(editingReservation.date).format('ddd, MMM D') : '' }}
                      <template v-if="editingReservation.preferredTime">
                        · {{ editingReservation.preferredTime }}
                      </template>
                      <template v-if="editingReservation.partySize">
                        · {{ editingReservation.partySize }} {{ editingReservation.partySize == 1 ? 'guest' : 'guests' }}
                      </template>
                    </div>
                  </div>

                  <!-- Spend section: visually distinct card -->
                  <div
                    class="occasion-spend-card"
                    @click="scrollToSelected(); changesMade = true"
                    @keyup="changesMade = true"
                  >
                    <!-- Preferred member note: spend is optional -->
                    <div
                      v-if="isPreferredAtSelectedRestaurant"
                      class="occasion-spend-hint"
                      style="color: var(--ds-brand-primary-darker); font-weight: 1500;"
                    >
                      <strong>You have Preferred Status at {{ addAndToString(preferredRestaurantNames.join(', ')) }} &amp; can submit a request without a guaranteed spend</strong>
                    </div>
                    <div v-if="!isPreferredAtSelectedRestaurant">
                      <div class="occasion-spend-card-header">
                        Guaranteed Food & Beverage Minimum
                      </div>
                      <div
                        class="occasion-spend-guidance"
                        style="font-size: 14px; color: #555; line-height: 1.45; margin-bottom: 8px; text-align: center;"
                      >
                        Required unless you have Preferred Status
                      </div>
                      <!-- Preferred member: opt-in copy shown when they've entered a spend value -->
                      <CmsText
                        v-if="isPreferredAtSelectedRestaurant && editingReservation.minSpendPerPerson"
                        k="reservation.preferred.optingIntoSpend"
                        tag="div"
                        class="occasion-spend-hint"
                        style="color: var(--ds-text-secondary, #666); font-size: 12px;"
                      />
                      <div
                        v-if="selectedRestaurantsObjectArray.length > 0 && selectedRestaurantsObjectArray[0]?.minSpend"
                        class="occasion-spend-hint"
                      >
                        ${{ selectedRestaurantsObjectArray[0].minSpend }}pp minimum for {{ selectedRestaurantsObjectArray[0].name }}
                      </div>
                      <div class="occasion-spend-card-body">
                        <VSelect
                          v-model="editingReservation.minSpendPerPerson"
                          class="min-spend-per-person-select occasion-spend-select"
                          :label="spendOptionsPlaceholder"
                          item-title="text"
                          item-value="value"
                          :items="minSpendOptions"
                          :rules="isPreferredAtSelectedRestaurant ? [] : [requiredValidator]"
                          :required="!isPreferredAtSelectedRestaurant"
                        />
                        <div
                          v-if="reservationOwner.isPlatinum && showMedianSpend(selectedRestaurantStats?.medianSpendPerPerson, minSpendOptions)"
                          class="occasion-spend-hint"
                          style="margin-top: 8px;"
                        >
                          Median via RM: ${{ selectedRestaurantStats.medianSpendPerPerson.toLocaleString() }}/person
                        </div>
                        <!-- RM-359 (Kapil 10/30): the guaranteed spend guidance, with the ask itself; "ask RM" opens Ask RM for feedback -->
                        <div
                          v-if="showsSpendGuidance(editingReservation)"
                          class="spend-guidance"
                        >
                          <template v-if="editingReservation?._id && myUser?.userLevel !== 'dummy'">
                            {{ SPEND_GUIDANCE_PARTS.before }}<a
                              href="#"
                              @click.prevent="spendAskRm?.open(SPEND_GUIDANCE_QUESTION)"
                            >{{ SPEND_GUIDANCE_PARTS.link }}</a>{{ SPEND_GUIDANCE_PARTS.after }}
                            <AskRmFeedback
                              ref="spendAskRm"
                              :reservation-id="editingReservation._id"
                              hide-button
                            />
                          </template>
                          <template v-else>
                            {{ SPEND_GUIDANCE }}
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- How guaranteed F&B spend works (card-hold restaurants only) — above Next so it's seen before advancing -->
                  <RequestPaymentInfo
                    v-if="!isPreferredAtSelectedRestaurant"
                    class="mt-6"
                    :pricing-breakdown="pricingBreakdown"
                    :first-restaurant-name="firstRestaurantName"
                  />

                  <!-- Next Button -->
                  <div class="text-center mt-4 mb-2">
                    <VBtn
                      :disabled="!!selectedRestaurantsNotAcceptingReferrals"
                      color="primary-dark"
                      size="large"
                      block
                      class="occasion-next-btn"
                      @click="handleSpendNext"
                    >
                      Next
                    </VBtn>
                  </div>
                </VForm>
              </div>
            </VWindowItem>

            <VWindowItem value="6a">
              <div class="tab-content occasion-step">
                <VForm
                  ref="form"
                  v-model="valid"
                >
                  <!-- Context anchor: restaurant + booking summary -->
                  <div class="occasion-context">
                    <div class="occasion-context-name">
                      {{ selectedRestaurantsObjectArray[0]?.name }}
                      <span
                        v-if="selectedRestaurantsObjectArray[0]?.market"
                        class="occasion-context-market"
                      >({{ selectedRestaurantsObjectArray[0].market.split(',')[0].trim() }})</span>
                    </div>
                    <div class="occasion-context-details">
                      {{ editingReservation.date ? moment(editingReservation.date).format('ddd, MMM D') : '' }}
                      <template v-if="editingReservation.preferredTime">
                        · {{ editingReservation.preferredTime }}
                      </template>
                      <template v-if="editingReservation.partySize">
                        · {{ editingReservation.partySize }} {{ editingReservation.partySize == 1 ? 'guest' : 'guests' }}
                      </template>
                    </div>
                  </div>

                  <!-- Occasion chips -->
                  <div
                    class="occasion-chips-section"
                    @click="changesMade = true"
                  >
                    <div class="occasion-chips-label">
                      What's the occasion?
                    </div>
                    <div class="occasion-chips-grid">
                      <button
                        v-for="occ in ['Anniversary','Birthday','Business','Date','General','Special Occasion']"
                        :key="occ"
                        type="button"
                        class="occasion-chip"
                        :class="{ 'occasion-chip--active': editingReservation.occasion === occ }"
                        @click="editingReservation.occasion = occ"
                      >
                        {{ occ }}
                      </button>
                    </div>
                    <!-- Validation-only select (control hidden, error messages visible) -->
                    <VSelect
                      v-model="editingReservation.occasion"
                      :items="['Anniversary','Birthday','Business','Date','General','Special Occasion']"
                      :rules="[requiredValidator]"
                      required
                      class="occasion-validation-select"
                    />
                  </div>

                  <!-- Note: collapsed link, expandable -->
                  <div class="occasion-notes-toggle">
                    <!-- RM-359 (Kapil 10/30): what can help get the table, on a regular request; the note stays optional -->
                    <div
                      v-if="showsNotesGuidance(editingReservation)"
                      class="notes-guidance"
                    >
                      <div class="notes-guidance__title">
                        {{ NOTES_GUIDANCE.title }}
                      </div>
                      <ol class="notes-guidance__list">
                        <li
                          v-for="(item, i) in NOTES_GUIDANCE.items"
                          :key="item"
                        >
                          {{ item }}<template v-if="i === 0 && notesMenuSite">
                            {{ NOTES_GUIDANCE.reviewHere }}
                            <a
                              :href="notesMenuSite.href"
                              target="_blank"
                              rel="noopener"
                            >{{ notesMenuSite.label }}</a>
                          </template>
                        </li>
                      </ol>
                      <div class="notes-guidance__seating">
                        {{ NOTES_GUIDANCE.seating }}
                      </div>
                    </div>
                    <button
                      v-if="!showNotesField && !reservationData.additionalNotes"
                      type="button"
                      class="occasion-add-note-btn"
                      @click="showNotesField = true"
                    >
                      <VIcon
                        size="16"
                        icon="mdi-plus"
                        class="mr-1"
                      />
                      Add a note for the restaurant
                    </button>
                    <VExpandTransition>
                      <div
                        v-if="showNotesField || reservationData.additionalNotes"
                        class="occasion-notes-field"
                        @click="changesMade = true"
                        @keyup="changesMade = true"
                      >
                        <VTextarea
                          v-model="reservationData.additionalNotes"
                          label="Note for restaurant (optional)"
                          rows="2"
                          auto-grow
                          :hint="showsNotesGuidance(editingReservation) ? '' : 'e.g. affiliation with restaurant, guest of note joining, special requests (dietary, seating)'"
                          :persistent-hint="!showsNotesGuidance(editingReservation)"
                          maxlength="150"
                        />
                      </div>
                    </VExpandTransition>
                  </div>

                  <!-- Spend section moved to its own "5p" Spend & Payment step (before 6a). -->

                  <!-- RM-162: request expiration choice — Preferred restaurants, Platinum members, reservation > 24h out -->
                  <div
                    v-if="showRequestExpirationChoice"
                    class="occasion-chips-section mt-4"
                    @click="changesMade = true"
                  >
                    <div class="occasion-chips-label">
                      When would you like the request to expire?
                    </div>
                    <div class="occasion-chips-grid">
                      <button
                        type="button"
                        class="occasion-chip"
                        :class="{ 'occasion-chip--active': expirationPicked && editingReservation.requestExpirationHours === 24 }"
                        @click="editingReservation.requestExpirationHours = 24; expirationPicked = true; expirationError = false"
                      >
                        In 24 hours
                      </button>
                      <button
                        type="button"
                        class="occasion-chip"
                        :class="{ 'occasion-chip--active': expirationPicked && editingReservation.requestExpirationHours === 48 }"
                        @click="editingReservation.requestExpirationHours = 48; expirationPicked = true; expirationError = false"
                      >
                        {{ requestExpirationLongOptionLabel }}
                      </button>
                    </div>
                    <div
                      v-if="expirationError"
                      class="mt-1 text-error text-sm text-center"
                    >
                      Please choose when the request should expire
                    </div>
                    <div
                      class="occasion-spend-hint"
                      style="color: var(--ds-text-secondary, #666); font-size: 14px;"
                    >
                      Preferred restaurants generally respond within 24 hours
                    </div>
                  </div>

                  <!--
                    RM-162: projected expiration — above Next, under the choice;
                    only once the member has picked a window (or no selector) 
                  -->
                  <div
                    v-if="projectedRequestExpirationLabel && (!showRequestExpirationChoice || expirationPicked)"
                    class="request-expiration-note mt-3"
                  >
                    <VIcon
                      icon="mdi-clock-outline"
                      size="16"
                      class="request-expiration-note-icon"
                    />
                    <span>
                      Expires <strong>{{ projectedRequestExpirationLabel }}</strong> if the restaurant doesn't respond. You can rescind anytime before then in the RM app or website.
                    </span>
                  </div>

                  <!-- Next Button -->
                  <div class="text-center mt-4 mb-2">
                    <VBtn
                      :disabled="!!selectedRestaurantsNotAcceptingReferrals"
                      color="primary-dark"
                      size="large"
                      block
                      class="occasion-next-btn"
                      @click="handleOccasionNext"
                    >
                      Next
                    </VBtn>
                    <div
                      v-if="selectedRestaurantsNotAcceptingReferrals"
                      class="mt-2 text-caption"
                    >
                      {{ selectedRestaurantsNotAcceptingReferrals.join(", ") }} 🚫 {{ $t('reservation.preferred.notAcceptingReferrals') }}
                    </div>
                  </div>
                </VForm>
              </div>
            </VWindowItem>
            <VWindowItem value="6b">
              <div class="tab-content">
                <VCardText>
                  <!-- 👉 Form -->
                  <VForm
                    ref="form"
                    v-model="valid"
                  >
                    <VRow>
                      <div
                        class="w-100 mx-3"
                        @click="changesMade = true"
                        @keyup="changesMade = true"
                      >
                        <VTextarea
                          v-model="reservationData.additionalNotes"
                          label="Notes / Requests"
                          rows="6"
                          auto-grow
                          hint="This will be shared with restaurant. Please provide helpful context only - e.g. guest of note, affiliation with restaurant team. Special requests (specific seating, etc.) can be harder to accommodate."
                          persistent-hint
                          maxlength="150"
                          class="mt-2"
                        />
                      </div>
                      <!-- 👉 Submit and Cancel -->
                      <VCol
                        cols="12"
                        class="d-flex flex-wrap justify-center gap-4 bottom-buttons"
                      >
                        <VSpacer />
                        <VBtn
                          color="primary-dark"
                          size="large"
                          block
                          class="step-next-btn"
                          :disabled="(editingReservation.hostAffiliated && !editingReservation.hostAffiliation) || (editingReservation.specialRequests && !editingReservation.specialRequestFlexibility) || saving || !!selectedRestaurantsNotAcceptingReferrals"
                          @click="reservationsStore.saveEditReservation(true); changesMade = false; tab='7a'"
                        >
                          Next
                        </VBtn>
                        <VSpacer />
                      </VCol>
                      <VCol
                        cols="12"
                        class="d-flex flex-wrap justify-center bottom-buttons pa-0 ma-0"
                      >
                        <div
                          v-if="selectedRestaurantsNotAcceptingReferrals"
                          class="mt-1 text-caption text-center"
                        >
                          {{ selectedRestaurantsNotAcceptingReferrals.join(", ") }} 🚫 {{ $t('reservation.preferred.notAcceptingReferrals') }}
                        </div>
                      </VCol>
                    </VRow>
                  </VForm>
                </VCardText>
              </div>
            </VWindowItem>
            <!-- 7a: Review (request summary) -->
            <VWindowItem value="7a">
              <EditReservationReviewStep
                section="review"
                :selected-restaurants="selectedRestaurantsObjectArray"
                :selected-restaurants-alphabetical="selectedRestaurantsObjectArrayAlphabetical"
                :selected-restaurants-not-preferred="selectedRestaurantsNotPreferred"
                :selected-restaurants-not-accepting-referrals="selectedRestaurantsNotAcceptingReferrals"
                :selected-restaurant-names="selectedRestaurantNames"
                :restaurants-for-selected-city="restaurantsForSelectedCity"
                :all-time-options="allTimeOptions"
                :pricing-breakdown="pricingBreakdown"
                :date-context-subtitle="dateContextSubtitle"
                :date-selected-past-platinum-membership-term="dateSelectedPastPlatinumMembershipTerm"
                :date-selected-past-preferred-membership-term="dateSelectedPastPreferredMembershipTerm"
                :is-trial-and-already-has-a-reservation="isTrialAndAlreadyHasAReservation"
                :is-not-host="isNotHost"
                :is-profile-incomplete="isProfileIncomplete"
                :profile-validation-message="profileValidationMessage"
                @upgrade="openUpgradeDialog"
              />
              <div class="text-center mx-4 mb-8">
                <VBtn
                  color="primary-dark"
                  size="large"
                  block
                  class="step-next-btn"
                  @click="tab = '7l'"
                >
                  Next
                </VBtn>
              </div>
            </VWindowItem>

            <!-- 7l (RM-359, Kapil 10/27): right after the request review: what the restaurant will receive, the Likelihood of Acceptance, Ask RM for feedback first -->
            <VWindowItem value="7l">
              <div class="before-submit-step mx-4 mb-8">
                <div class="before-submit-step__title">
                  Before you submit
                </div>
                <div
                  v-if="editingReservation?._id && myUser?.userLevel !== 'dummy'"
                  class="mb-4"
                >
                  <RequestPreviewCard
                    :label="requestPreviewLabel"
                    :loading="openingRequestPreview"
                    @open="showPreview"
                  />
                  <div
                    v-if="requestPreviewError"
                    class="text-caption text-error mt-1 text-center"
                  >
                    Couldn't open the preview. Try again
                  </div>
                </div>
                <!-- Members see it once RM turns it on (Kapil 10/29); the step keeps the preview and Ask RM either way -->
                <LikelihoodCard
                  :likelihood="likelihood"
                  :loading="placeholderWhile(likelihoodLoading)"
                />
                <!-- RM-359 Part B: RM's view before submitting; never submits -->
                <AskRmFeedback
                  v-if="editingReservation?._id && editingReservation?.status === 'Draft' && myUser?.userLevel !== 'dummy'"
                  :reservation-id="editingReservation._id"
                  class="mb-6"
                />
                <VBtn
                  color="primary-dark"
                  size="large"
                  block
                  class="step-next-btn"
                  @click="tab = '7b'"
                >
                  Next
                </VBtn>
              </div>
            </VWindowItem>

            <!-- 7b: Your Profile -->
            <VWindowItem value="7b">
              <EditReservationReviewStep
                section="profile"
                :selected-restaurants="selectedRestaurantsObjectArray"
                :selected-restaurants-alphabetical="selectedRestaurantsObjectArrayAlphabetical"
                :selected-restaurants-not-preferred="selectedRestaurantsNotPreferred"
                :selected-restaurants-not-accepting-referrals="selectedRestaurantsNotAcceptingReferrals"
                :selected-restaurant-names="selectedRestaurantNames"
                :restaurants-for-selected-city="restaurantsForSelectedCity"
                :all-time-options="allTimeOptions"
                :pricing-breakdown="pricingBreakdown"
                :date-context-subtitle="dateContextSubtitle"
                :date-selected-past-platinum-membership-term="dateSelectedPastPlatinumMembershipTerm"
                :date-selected-past-preferred-membership-term="dateSelectedPastPreferredMembershipTerm"
                :is-trial-and-already-has-a-reservation="isTrialAndAlreadyHasAReservation"
                :is-not-host="isNotHost"
                :is-profile-incomplete="isProfileIncomplete"
                :profile-validation-message="profileValidationMessage"
                @next="tab = '7c'"
                @upgrade="openUpgradeDialog"
              />
            </VWindowItem>

            <!-- 7c: Review & Submit -->
            <VWindowItem value="7c">
              <div
                class="text-center"
                style="padding: 12px 0 4px;"
              >
                <span style="font-size: 18px; font-weight: 700; color: #1a1a1a;">Review &amp; Submit</span>
              </div>
              <EditReservationPaymentStep
                v-if="reviewReady"
                ref="paymentStepRef"
                :is-within48-hours="isReservationWithin48Hours"
                :is-active="tab === '7c'"
                :selected-restaurants="selectedRestaurantsObjectArray"
                :selected-restaurants-not-preferred="selectedRestaurantsNotPreferred"
                :is-not-host="isNotHost"
                :is-preferred-at-selected-restaurant="isPreferredAtSelectedRestaurant"
                :selected-restaurants-min-spend="selectedRestaurantsMinSpend"
                :selected-restaurant-names="selectedRestaurantNames"
                :opened-via-agent-handoff="openedViaAgentHandoff"
                :pricing-breakdown="pricingBreakdown"
                :first-restaurant-name="firstRestaurantName"
              >
              </EditReservationPaymentStep>
            </VWindowItem>
            <VWindowItem value="4b">
              <EditReservationPrivateDining4bStep
                :selected-restaurant-names="selectedRestaurantNames"
                :selected-restaurants-min-spend="selectedRestaurantsMinSpend"
                :min-spend-options="minSpendOptions"
                :selected-restaurants-not-accepting-referrals="selectedRestaurantsNotAcceptingReferrals"
                :saving="!!saving"
                @next="tab = '3b'"
                @change="changesMade = true"
                @scroll-to-selected="scrollToSelected"
              />
            </VWindowItem>
            <VWindowItem value="3b">
              <EditReservationPrivateDining3bStep
                :selected-restaurant-names="selectedRestaurantNames"
                :selected-restaurants-not-accepting-referrals="selectedRestaurantsNotAcceptingReferrals"
                :room-preference-options="roomPreferenceOptions"
                :seating-preference-options="seatingPreferenceOptionsBasedOnReservationType"
                :saving="!!saving"
                @next="reservationsStore.saveEditReservation(true); changesMade = false; tab = '5b'"
                @change="changesMade = true"
              />
            </VWindowItem>
            <VWindowItem value="5b">
              <EditReservationPrivateDining5bStep
                :selected-restaurants="selectedRestaurantsObjectArray"
                :restaurants-for-selected-city="restaurantsForSelectedCity"
                :all-time-options="allTimeOptions"
                :selected-restaurants-not-accepting-referrals="selectedRestaurantsNotAcceptingReferrals"
                :date-context-subtitle="dateContextSubtitle"
                :date-selected-past-preferred-membership-term="dateSelectedPastPreferredMembershipTerm"
                :is-not-host="isNotHost"
                :is-profile-incomplete="isProfileIncomplete"
                :profile-validation-message="profileValidationMessage"
                :saving="!!saving"
                :preview-label="requestPreviewLabel"
                :opening-preview="openingRequestPreview"
                :preview-error="requestPreviewError"
                @submit="confirmEditingReservation"
                @upgrade="openUpgradeDialog"
                @preview="showPreview"
              />
            </VWindowItem>
          </VWindow>
        </VDefaultsProvider>
      </VCard>
    </VDialog>
    <PreferredRequestInfoDialog />
    <PreferredTabsDialog />
    <UpgradeProfileDialog
      v-model="showUpgradeDialog"
      :product-id="upgradeProductId"
      :price-dollars="upgradePriceDollars"
      :message="`purchased on ${moment().tz('America/Chicago').format('M/D/YY')}`"
      @complete="onUpgradeDialogComplete"
      @cancel="onUpgradeDialogCancel"
    />

    <!-- Instant Booking Checkout Dialog (rendered outside main dialog to avoid nesting issues) -->
    <InstantBookingCheckoutDialog
      v-model="showInstantBookingCheckout"
      :slot-id="selectedInstantBookingSlotId"
      @success="handleInstantBookingSuccess"
    />
  </div>
</template>


<style lang="scss" scoped>
.before-submit-step__title {
  padding: 12px 0 16px;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
}

.restaurant-hours-holder {
  cursor: pointer;
  display: flex;
  width: fit-content;
  border-radius: 0.4rem;
}

.restaurant-hours-toggle {
  transform: translateY(-0.25em);
  margin-left: 0.5rem;
}

.membership-details-list-item {
  margin-block-end: 0.7rem;
  line-height: 1.2;
}

.dialog-close-btn {
  position: fixed;
  right: 6px;
  top: 6px;
}

.v-label {
  white-space: unset;
}

.request-type-radio :deep(.v-selection-control) {
  align-items: flex-start;
}

.request-type-radio :deep(.v-label) {
  font-size: 14px;
  opacity: var(--v-high-emphasis-opacity);
  padding-left: 2px;
  padding-top: 4px;
}

.checkbox-align-start :deep(.v-selection-control) {
  align-items: flex-start;
}

.min-spend-per-person-select :deep(.v-field__field) {
  white-space: unset;
  box-sizing: content-box;
  padding-top: 6px;
}

:deep(.v-select .v-field__input) {
  padding-top: 22px;
}

.v-window {
  overflow: unset;
}

.confirm-title {
  font-family: var(--heading-font);
  font-size: 1rem;
  line-height: 1em;
}

.bottom-buttons {
  margin-top: 0em;
}

.referral-logo {
  width: 3rem;
}

.toolbar-header {
  position: sticky;
  top: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 44px;
  min-height: 44px;
  padding: 0 4px;
  background: var(--ds-brand-primary);
  border-bottom: none;
}

.toolbar-header-title {
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tab-content {
  position: relative;
}

.powered-by-google{
  height: 16px;
  position: absolute;
  bottom: 4px;
  right: 4px;
}

/* .user-info-content - removed empty ruleset */


.reservation-card {
  container-type: inline-size;
  overflow: hidden;
  border-radius: 0.4rem;
  border: thin solid var(--ds-border-light);
  width: 100%;
}

.reservation-card-title {
  font-size: 1rem;
  color: var(--ds-text-muted);
}


//.reservation-card:first-child {
//  margin-top: 0.5rem;
//}

.reservation-card-content {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
}

.reservation-card-meta-text-holder {
  display: none;
}

@container (min-width: 430px) {
  .reservation-card-content {
    flex-direction: row;
    padding: 1rem;
  }

  .reservation-card-meta-text-holder {
    display: flex;
  }

}


.mini-reservation-card {
  overflow: hidden;
  border-radius: 0.4rem;
  border: thin solid var(--ds-border-light);
  margin-bottom: 0.25rem;
  width: 100%;
}
//
//.mini-reservation-card-title {
//  font-size: 1rem;
//  color: var(--ds-text-muted);
//  padding-top: 0.25rem;
//}


.mini-reservation-card-content {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0rem;
  padding-bottom: 0.25rem;
}

@media screen and (min-width: 430px) {
  .mini-reservation-card-content {
    flex-direction: row;
    padding: 1rem;

  }
}


.restaurant-image-holder {
  width: 100%;
  aspect-ratio: 16/7;
  background: #efefef;
  border-radius: 0;
  overflow: hidden;
  //display: none;
}

@container (min-width: 430px) {
  .restaurant-image-holder {
    width: 8rem;
    height: 8rem;
    aspect-ratio: 1;
    border-radius: 0.6rem;
  }
}

.restaurant-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.action-buttons {
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: 100;
  background: #fff;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  border-top: thin solid var(--ds-border-default);
  padding: 1rem;
  box-shadow: 0px -3px 6px rgba(0,0,0,0.025);
}

.general-availability-holder {
  position: relative;
  border-radius: 0.4rem;
  text-align: left;
  padding: 0.1rem 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1rem;
}

.general-availability-title {
  width: max-content;
  position: absolute;
  left: 50%;
  top: 0;
  text-align: center;
  background: white;
  padding: 0 0.35rem;
  transform: translate(-50%, -50%);
}

.custom-title-style {
  font-weight: bold; /* Example: Make the text bold */
  font-size: 0.75rem !important;
}
//.test .v-list-item-title{
//  font-size: 0.75rem !important;
//}

.google-attribution-container {
  position: fixed;
  right: 7px;
  bottom: -18px;
}

.google-attribution {
  width: fit-content;
  margin-bottom: 1rem;
  margin-left: 0.4rem;
}

//:has(.v-input__details) ~ .bottom-buttons {
//  margin-top: -0.7rem;
//}

//.v-input:not(:has(.v-input__details)) {
//  margin-bottom: 1.6rem;
//}

.remove-restaurant-button{
  position: absolute;
  //display: flex;
  align-items: center;
  justify-content: center;
  top: 0.3rem;
  right: 0.3rem;
  width: 1.8rem;
  height: 1.8rem;
  background: rgba(0,0,0, 0.2);
  border-radius: 50%;
  transition: all 300ms;
}

.remove-restaurant-button:hover {
  background: rgba(0,0,0, 0.35);
}

.remove-restaurant-button-no-image {
  top: 0.3rem;
  right: 0.5rem;
}

/* Tab 1 specific styling for remove button - visible on white background */
.remove-restaurant-button-tab1 {
  top: -0.5rem;
  right: -0.5rem;
  background: rgba(80, 80, 80, 0.9) !important;
  color: white !important;
}

.remove-restaurant-button-tab1:hover {
  background: rgba(50, 50, 50, 1) !important;
}

/* Tab 1 image holder - shift left to make room for X button */
.mini-restaurant-image-holder-tab1 {
  margin-right: 0.5rem;
  position: relative;
  overflow: visible !important;
}

.restaurant-add-content {
  background: #fff;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height:100%;
}

.restaurant-add-content-text {
  margin-top: 0.5rem;
  max-width: 12ch;
}

.restaurant-image-holder {
  width: 100%;
  aspect-ratio: 16/7;
  background: #efefef;
  border-radius: 0;
  overflow: hidden;
  //display: none;
}

@container (min-width: 430px) {
  .restaurant-image-holder {
    width: 8rem;
    height: 8rem;
    aspect-ratio: 1;
    border-radius: 0.6rem;
  }
}

.mini-restaurant-card {
  overflow: hidden;
  border-radius: 0.4rem;
  border: thin solid var(--ds-border-light);
  margin-bottom: 0.25rem;
  width: 100%;
}

.single-restaurant-card {
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: flex-start; /* Align items to top like RestaurantGridCard */
  padding: 12px;

  .mini-restaurant-card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .mini-restaurant-image-holder {
    order: -1; /* thumbnail leads the row, matching the shared card */
    width: 52px;
    height: 52px;
    min-width: 52px;
    flex-shrink: 0;
    position: relative;
    border-radius: var(--ds-radius-md);
    overflow: hidden;

    :deep(.v-img) {
      width: 52px !important;
      height: 52px !important;
    }

    .remove-restaurant-button {
      position: absolute;
      top: 0;
      right: 0;
    }
  }
}

.mini-restaurant-card-content {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0rem;
  padding-bottom: 0.25rem;
}

.mini-restaurant-card-content-horizontal {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start; /* Align items to top like RestaurantGridCard */
  gap: 12px;
  padding: 12px;

  /* thumbnail leads the row, matching the shared card */
  .mini-restaurant-image-holder,
  .mini-restaurant-image-holder-right {
    order: -1;
  }
}

.mini-restaurant-image-holder {
  width: 52px;
  height: 52px;
  min-width: 52px;
  background: white; /* White background when no image */
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start; /* Align image to the top */
}

.mini-restaurant-image-holder-right {
  width: 52px;
  height: 52px;
  min-width: 52px;
  background: white; /* White background when no image */
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start; /* Align image to the top */
}

/* For private dining cards, use fixed dimensions matching RestaurantGridCard */
.mini-restaurant-card-content-horizontal .mini-restaurant-image-holder-right {
  width: 52px;
  height: 52px;
}

.mini-restaurant-card-content-horizontal .mini-restaurant-image-holder-right :deep(.v-img) {
  height: 52px;
  width: 52px;
}

/* Text column matching the shared card — plain stacked lines, no icon rail */
.profile-card-horizontal {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 0.125rem;
  padding: 0;
  align-items: start;
}

.profile-card-content {
  display: contents;
  font-size: 14px;
  line-height: 1.2;
}

.profile-card-content > * {
  min-width: 0;
}

.profile-card-subtitle {
  grid-row: 2;
}

.profile-card-title {
  display: flex;
  align-items: flex-start;
  gap: 2px;
  min-height: 1.4em; /* Ensure minimum height for single line */
  line-height: 1.2;
}

/* Names stay brand ink — the seal signals Preferred */
.text-preferred {
  color: var(--ds-brand-ink) !important;
}

.profile-card-subtitle {
  color: var(--ds-text-body);
  font-size: 13.5px;
}

.profile-card-location {
  color: var(--ds-text-body);
  font-size: 13.5px;
}

.profile-card-action {
  font-size: 0.75rem;
  margin-top: 0.125rem;
}

.profile-card-preferred-badge {
  display: inline-flex;
  align-items: center;
  background: var(--ds-brand-primary-dark);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 12px;
  margin-top: 3px;
  line-height: 1.4;
}

@media screen and (min-width: 430px) {
  .mini-restaurant-image-holder {
    width: 86px;
    height: 86px;
  }

  .mini-restaurant-image-holder-right {
    width: 86px;
    height: 86px;
  }
}

.add-restaurant-scroller {
  position: relative;
  overflow-x: auto;
  border-radius: 0.4rem;
  scroll-behavior: smooth;
}

.add-restaurant-scroller.vertical-scroller {
  overflow-x: visible;
  overflow-y: visible;
}

.add-restaurant-scroller.private-dining-with-restaurants {
  padding-top: 1rem;
}

.add-restaurant-scroll-button {
  display: none;
  position: absolute;
  top: 50%;
  transform: translateY(calc(-50% - 0.25rem));
  z-index: 100;
}

@media screen and (min-width: 430px) {
  .add-restaurant-scroll-button {
    display: block;
  }
}

.add-restaurant-scroll-button--left {
  left: 5px;
}

.add-restaurant-scroll-button--right {
  right: 0;
}

.add-restaurant-scroller::-webkit-scrollbar {
  display: none;
}

.add-restaurant-items-holder {
  display: flex;
  padding-bottom: 0.5rem;
  gap: 0.5rem;
}

.add-restaurant-items-holder.vertical-layout {
  flex-direction: column;

  .add-restaurant-item {
    width: 100%;
    flex-basis: auto;
    min-width: unset;
    min-height: auto;
  }

  .add-restaurant-button-holder {
    min-height: 100px;
    max-height: 100px;
  }

  .add-restaurant-button {
    gap: 0.25rem;
    padding: 0.5rem;
  }

  .add-restaurant-icon-wrapper {
    width: 2.5rem;
    height: 2.5rem;
  }

  .restaurant-add-content-text {
    margin-top: 0;
    font-size: 0.875rem;
  }
}

.add-restaurant-item {
  //word-break: break-all;
  min-width: 160px;
  flex-basis: 100%;
  border-radius: 0.4rem;
  overflow: hidden;
  border: thin solid var(--ds-border-light);
  cursor: pointer;
  transition: all 200ms;
}

/* Step-1 summary: a line row like every other restaurant card — no box,
   the remove X as the only control on the right */
.add-restaurant-item--summary {
  border: none;
  border-radius: 0;
  overflow: visible;

  .single-restaurant-card {
    padding: 12px 2px;
  }
}

.remove-restaurant-button-summary {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
}

/* For private dining cards, use fixed height matching RestaurantGridCard */
.add-restaurant-item .mini-restaurant-card-content-horizontal {
  height: auto;
}

/* Remove bottom padding from profile-card-horizontal in private dining cards */
.mini-restaurant-card-content-horizontal .profile-card-horizontal {
  padding-bottom: 0;
}

@media only screen and (min-width: 430px) {
  .add-restaurant-item:hover {
    box-shadow: 0 1px 7px -3px rgba(0,0,0,0.075), 0 5px 16px rgba(0,0,0,0.05);
  }

  .add-restaurant-item--summary:hover {
    box-shadow: none;
  }
}


.has-three .add-restaurant-item {
  min-width: unset;
  width: 33.3333%;
}

.add-restaurant-button-holder {
  pointer-events: none;
  position: relative;
  min-height: 135px;
  flex-basis: 100%;
}

.has-three .add-restaurant-button-holder {
  min-width: unset;
  width: 33.3333%;
}

.add-restaurant-button-holder--sticky-right {
  position: sticky;
  min-width: 160px;
  top: 0;
  right: 0;
  z-index: 100;
}

.add-restaurant-button {
  position: absolute;
  left: 50%;
  top: 50%;
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  border: thin solid var(--ds-border-light);
  border-radius: 0.4rem;
  padding: 1rem;
  pointer-events: all;
  cursor: pointer;
  background: #fff;
  transform-origin: center right;
  transform-style: preserve-3d;
  transform: translate3d(-50%, -50%, 0);

}

@media screen and (min-width: 430px) {
  .add-restaurant-button {
    transition: all 300ms;
  }

  .add-restaurant-button:hover {
    background: #f9fffe;
    box-shadow: 0 1px 7px -3px rgba(0,0,0,0.075), 0 5px 16px rgba(0,0,0,0.05);
  }
}

.scrolled-into-button .add-restaurant-button-holder {
  min-width: 160px;
}

.scrolled-into-button .add-restaurant-button {
  width: 50%;
  height: unset;
  border-right-color: transparent;
  border-radius: 60rem 0 0 60rem;
  transform: translate3d(0, -50%, 0);
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
}

@media screen and (min-width: 430px) {
  .scrolled-into-button .add-restaurant-button {
    width: 75%;
    height: unset;
    border-right-color: transparent;
    border-radius: 60rem 0 0 60rem;
    transform: translate3d(-25%, -50%, 0);
    box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  }
}


.scrolled-into-button .restaurant-add-content-text {
  display: none;
}

.add-restaurant-button:hover {
  background: #fefefe;
}

.add-restaurant-icon-wrapper {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: #eaf5f4;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

}

@media screen and (min-width: 430px) {
  .scrolled-into-button .add-restaurant-icon-wrapper {
    transform: translateX(-36%);
    transition: transform 300ms;
  }
}

.private-restaurant-selections {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  padding-bottom: 2.5rem;
}

@media screen and (min-width: 430px) {
  .private-restaurant-selections {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    padding-bottom: 2.5rem;
  }
}

.restaurant-terms-item {
  //word-break: break-all;
  flex-basis: 100%;
  border-radius: 0.4rem;
  overflow: hidden;
  border: thin solid var(--ds-border-light);
  transition: all 200ms;
}


.selector-overlay {
  position: absolute;
  width: 100vw;
  height: 100vh;
  z-index: 10000;
}

.text-width-limiter {
  max-width: 80ch;
}


:deep(.smaller-label .v-label){
  font-size: 16px !important;
}

:deep(.v-selection-control__input){
  justify-content: right;
}

:deep(.custom-font-size .v-input__control .v-field__input input ){
  font-size: 16px !important;
}

:deep(.v-field-label ){
  font-size: 16px !important;
}

:deep(.v-select__selection-text){
  font-size: 16px !important;
}
:deep(.smaller-input .v-field__input){
  font-size: 16px !important;
}


/* Hide the default title in the dropdown list */
:deep(.v-list-item__content .v-list-item-title) {
  display: none;
}

/* Ensure that your custom title is displayed */
:deep(.custom-title) {
  display: block !important;
}

:deep(.v-list-item__prepend ){
  width:16px !important;;
}

/* Search dropdown card styles */
.search-dropdown-card {
  display: flex;
  align-items: flex-start; /* Align items to top for better multi-line text */
  padding: 16px;
  gap: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  margin: 8px 16px;
  background: white;
  min-height: 92px; /* Minimum height, allows cards to grow for long names */
}

.search-dropdown-card:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.search-dropdown-add-btn {
  flex-shrink: 0;
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  padding: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.search-dropdown-add-btn:hover {
  opacity: 0.8;
}

.search-dropdown-content {
  flex: 1;
  min-width: 0;
  max-width: calc(100% - 80px); /* Ensures consistent wrapping: accounts for image (72px) + gap (8px) */
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden; /* Prevent content overflow */
}

.search-dropdown-title-with-heart {
  display: flex;
  align-items: flex-start;
  gap: 2px;
  min-height: 1.4em; /* Ensure minimum height for single line */
}

.search-dropdown-icon-placeholder {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin-top: 7.2px;
  min-width: 20px;
  width: 20px;
}

.search-dropdown-title-wrapper {
  flex: 1;
  min-width: 0;
  display: inline-block; /* Make wrapper inline-block so heart flows with text */
}

.search-dropdown-title {
  font-weight: 500;
  font-size: 15px;
  line-height: 1.4;
  color: #000;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  display: inline; /* Make title inline so heart appears right after text */
}

.text-preferred {
  color: var(--ds-brand-primary-dark) !important;
}

.search-dropdown-heart-btn {
  display: inline-flex;
  align-items: center;
  margin-left: 10px; /* Increased spacing to match home screen cards */
  vertical-align: middle;
  flex-shrink: 0;
}

.search-dropdown-subtitle-wrapper {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 22px; // icon width (20px) + gap (2px) - always align with restaurant name
}

.search-dropdown-subtitle {
  font-size: 13px;
  line-height: 1.5;
  color: #666;
  margin: 0;
}

.search-dropdown-image-container {
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  min-width: 72px; /* Always reserve space for image */
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 6px 6px 0; /* Top, right, bottom padding (left is 0) */
  box-sizing: border-box;
}

/* Loading state styles */
.search-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  min-height: 200px;
  gap: 16px;
}

.loading-text {
  font-size: 14px;
  font-weight: 400;
  color: var(--ds-text-muted);
  margin: 0;
}

/* Add favorite section styles */
.add-favorite-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  min-height: 150px;
}

.empty-state-text {
  font-size: 16px;
  font-weight: 500;
  color: var(--ds-text-muted);
  margin-bottom: 16px;
}

.add-restaurant-btn {
  text-transform: none;
  font-weight: 500;
}

.restaurant-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 600px) {
  .search-dropdown-card {
    padding: 12px;
    gap: 10px;
    min-height: 80px; /* Minimum height for mobile, allows cards to grow for long names */
    align-items: flex-start; /* Align items to top for better multi-line text */
  }

  .search-dropdown-content {
    max-width: calc(100% - 70px); /* Mobile: accounts for image (60px) + gap (10px) */
  }

  .search-dropdown-title {
    font-size: 14px;
  }

  .search-dropdown-subtitle {
    font-size: 12px;
  }

  .search-dropdown-image-container {
    width: 60px;
    height: 60px;
    min-width: 60px; /* Always reserve space for image on mobile */
    padding: 4px 4px 4px 0; /* Top, right, bottom padding (left is 0) */
  }
}

/* Private dining search icon adjustment */
:deep(.private-dining-search .v-field__prepend-inner) {
  transform: translateY(-1px);
}

/* Ensure placeholder text appears in search box even with chips */
:deep(.private-dining-search .v-field__input input::placeholder) {
  opacity: 1 !important;
  color: rgba(0, 0, 0, 0.6) !important;
}

:deep(.private-dining-search .v-field__input input) {
  opacity: 1 !important;
}

/* Datetime picker group - unified appearance */
.datetime-picker-group {
  gap: 8px;
  border-radius: 10px;
}

.datetime-picker-field {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 4px 0;
  overflow: hidden;
}

/* Field-specific flex values based on content type */
.datetime-field-date {
  flex: 1.1;
}

.datetime-field-date :deep(.v-field) {
  padding-inline-end: 12px;
}

/* Custom dropdown arrow for date picker to match VSelect */
.datetime-field-date::before {
  content: '';
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid rgba(var(--v-theme-on-surface), 0.54);
  pointer-events: none;
  z-index: 2;
}

.datetime-field-time {
  flex: 1.1;
}

.datetime-field-time-full {
  flex: 1;
}

.datetime-field-partysize {
  flex: 0.7;
}

.datetime-field-occasion {
  flex: 0.8;
}

.datetime-field-notes {
  flex: 1.2;
}

/* Time summary styling */
.time-summary-text {
  font-size: 12px;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.time-summary-divider {
  margin: 0 4px;
  opacity: 0.4;
}

.datetime-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  font-weight: 500;
  margin-bottom: 2px;
}

.datetime-picker-field :deep(.v-input__details) {
  display: none !important;
}

.datetime-picker-field :deep(.v-field__input) {
  font-size: 16px;
  padding-left: 12px;
  padding-right: 4px;
}

.datetime-picker-field :deep(.v-select__selection-text),
.datetime-picker-field :deep(.flatpickr-input) {
  font-size: 16px !important;
}

// Instant Booking Section Styles
.instant-booking-section {
  border: 0px solid #E2E8F0;
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
}

.instant-booking-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 2px;
}

.instant-booking-title {
  font-family: var(--ds-font-body);
  font-size: 14px;
  font-weight: 600;
  color: #1E293B;
}

.instant-booking-subtitle {
  font-family: var(--ds-font-body);
  font-size: 12px;
  color: #64748B;
}

.instant-booking-restaurants {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.instant-booking-restaurant {
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  padding: 10px 12px;
}

.restaurant-name-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.instant-booking-restaurant .restaurant-name {
  font-family: var(--ds-font-body);
  font-size: 13px;
  font-weight: 600;
  color: #1E293B;
}

.instant-booking-restaurant .restaurant-market {
  font-family: var(--ds-font-body);
  font-size: 11px;
  color: #64748B;
}

.instant-slots-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.instant-slot-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 12px;
  background: white;
  border: 1px solid var(--ds-brand-primary-dark);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 70px;

  &:hover {
    background: var(--ds-brand-primary-dark);
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(63, 153, 143, 0.3);

    .slot-time,
    .slot-party {
      color: white;
    }
  }
}

.instant-slot-chip .slot-time {
  font-family: var(--ds-font-body);
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-brand-primary-dark);
}

.instant-slot-chip .slot-party {
  display: flex;
  align-items: center;
  gap: 2px;
  font-family: var(--ds-font-body);
  font-size: 11px;
  color: #64748B;
}

.more-slots {
  font-family: var(--ds-font-body);
  font-size: 11px;
  color: #64748B;
  font-style: italic;
}

.instant-booking-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  background: #F8FAFC;
  border-radius: 8px;
}

.instant-booking-loading .loading-text {
  font-family: var(--ds-font-body);
  font-size: 12px;
  color: #64748B;
}

// Preferred Restaurants Section Styles (shown when selected restaurant is not preferred)
.preferred-restaurants-section {
  border: 0px solid #E2E8F0;
  border-radius: 12px;
  padding: 0px 16px;
  margin-top: 6px;
}

.preferred-restaurants-tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;

  .sort-toggle {
    border: 1px solid #E0E0E0;
    border-radius: 4px;

    :deep(.v-btn) {
      font-size: 12px;
      text-transform: none;
      letter-spacing: normal;
      min-width: 70px;
    }

    :deep(.v-btn--active) {
      background-color: rgb(var(--v-theme-primary));
      color: white;
      border-bottom: none;
    }
  }
}

.preferred-restaurants-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 4px;

  // Scrollbar styling
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;

    &:hover {
      background: #a1a1a1;
    }
  }
}

// Mobile responsiveness for preferred restaurants grid
@media (max-width: 600px) {
  .preferred-restaurants-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}

// Preferred Restaurants List Styles (new list format)
.preferred-restaurants-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 4px;
}

.preferred-restaurant-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 6px;
  border: 0px solid #E2E8F0;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background-color: #f8fafb;
    border-color: var(--ds-brand-primary-dark);
  }
}

.preferred-restaurant-add {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preferred-restaurant-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
}

.preferred-restaurant-name {
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
}

.preferred-restaurant-group {
  font-size: 12px;
  color: #666;
}

.preferred-restaurant-market {
  font-size: 12px;
  color: #888;
}

// Guaranteed Spend Section Styles
$primary-teal: var(--ds-brand-primary-dark);
$text-dark: var(--ds-text-dark);
$text-medium: var(--ds-text-medium);
$text-light: var(--ds-text-light);
$border-light: var(--ds-border-light);
$bg-white: var(--ds-surface-card);
$bg-light: var(--ds-bg-light);

// ─── Occasion + Spend step (tab 6a) ───

.occasion-step {
  padding: 0 20px 8px;
  max-width: 420px;
  margin: 0 auto;
}

// Context anchor
.occasion-context {
  text-align: center;
  padding: 16px 0 8px;
}

.occasion-context-name {
  font-size: 18px;
  font-weight: 700;
  color: $text-dark;
  line-height: 1.3;
}

.occasion-context-market {
  font-size: 14px;
  font-weight: 400;
  color: $text-medium;
}

.occasion-context-details {
  font-size: 14px;
  color: $text-medium;
  margin-top: 2px;
}

// Occasion chips
.occasion-chips-section {
  padding: 12px 0 4px;
}

.occasion-chips-label {
  font-size: 16px;
  font-weight: 600;
  color: $text-dark;
  margin-bottom: 10px;
}

.occasion-chips-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.occasion-chip {
  padding: 8px 16px;
  border-radius: 20px;
  border: 1.5px solid #e0e0e0;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  color: $text-dark;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;

  &:hover {
    border-color: #bbb;
  }

  &--active {
    border-color: var(--ds-brand-primary-dark);
    background: var(--ds-brand-primary-light);
    color: var(--ds-brand-primary-dark);
    font-weight: 600;
  }
}

// Validation-only select: hide the input, show only error messages
.occasion-validation-select {
  :deep(.v-input__control) {
    display: none;
  }

  :deep(.v-input__details) {
    padding-top: 0;
    min-height: 0;
  }

  // Only take space when there's an error
  &:not(.v-input--error) {
    display: none;
  }
}

// Notes toggle
.occasion-notes-toggle {
  padding: 8px 0 4px;
  margin-top: 18px;
}

.spend-guidance {
  margin-top: 12px;
  font-size: 14px;
  line-height: 1.5;
  color: $text-dark;
  text-align: center;

  a {
    color: var(--ds-brand-primary-dark);
    text-decoration: underline;
  }
}

.notes-guidance {
  font-size: 14px;
  line-height: 1.5;
  color: $text-dark;
  margin-bottom: 8px;
}

.notes-guidance__title {
  font-weight: 600;
  margin-bottom: 4px;
}

.notes-guidance__list {
  list-style: decimal;
  padding-left: 20px;
  margin: 0 0 8px;

  li {
    margin-bottom: 4px;
  }
}

.notes-guidance__seating {
  color: $text-light;
}

.notes-guidance__list a {
  color: var(--ds-brand-primary-dark);
  text-decoration: underline;
}

.occasion-add-note-btn {
  display: inline-flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  color: var(--ds-brand-primary-dark);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 0;

  &:hover {
    text-decoration: underline;
  }
}

.occasion-notes-field {
  padding-top: 8px;

  :deep(.v-field__input) {
    padding-top: 34px;
    padding-bottom: 6px;
    min-height: auto;
  }

  :deep(.v-field__input textarea) {
    margin-top: 2px;
  }
}

// Spend card
.occasion-spend-card {
  background: var(--ds-surface-card);
  border: 1px solid var(--ds-border-light);
  border-radius: 12px;
  padding: 20px;
  margin-top: 16px;
}

.occasion-spend-card-header {
  font-size: 16px;
  font-weight: 600;
  color: $text-dark;
  text-align: center;
  margin-bottom: 12px;
}

.occasion-spend-card-body {
  max-width: 300px;
  margin: 0 auto;
}

.occasion-spend-hint {
  font-size: 14px;
  color: $text-light;
  text-align: center;
  margin-bottom: 8px;
}

.request-expiration-note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.03);
  font-size: 14px;
  line-height: 1.4;
  color: $text-light;
}

.request-expiration-note-icon {
  flex: 0 0 auto;
  margin-top: 1px;
}

.occasion-spend-select {
  :deep(.v-field) {
    background: #fff !important;
    border: 1.5px solid #e0e0e0;
    border-radius: 12px;
  }

  :deep(.v-field:hover) {
    border-color: #bbb;
  }

  :deep(.v-field--focused) {
    border-color: var(--ds-brand-primary-dark);
  }
}

.occasion-spend-attribution {
  font-size: 14px;
  color: $text-medium;
  text-align: center;
  margin-top: 4px;
}

// Next button
.occasion-next-btn {
  max-width: 340px;
  margin: 0 auto;
  font-weight: 600;
  letter-spacing: 0.02em;
}

// How Payment Works section styles
.payment-info-section {
  background: linear-gradient(135deg, #f8fafa 0%, #f0f7f6 100%);
  border: 1px solid #e0ebe9;
  border-radius: 12px;
  padding: 20px;
}

.payment-info-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.payment-info-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: $text-dark;
}

.payment-info-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.payment-info-amount {
  font-size: 0.95rem;
  font-weight: 700;
  color: $primary-teal;
}

.payment-info-desc {
  font-size: 0.9rem;
  color: $text-medium;
}

.payment-info-note {
  font-size: 0.95rem;
  color: #555;
  line-height: 1.5;
  margin: 0 0 12px 0;

  strong {
    font-weight: 600;
  }
}

.payment-info-details {
  summary {
    font-size: 0.95rem;
    color: $primary-teal;
    cursor: pointer;
    font-weight: 500;
    list-style: none;
    display: flex;
    align-items: center;
    gap: 4px;

    &::-webkit-details-marker {
      display: none;
    }

    &::before {
      content: '▸';
      font-size: 0.7rem;
      transition: transform 0.2s ease;
    }

    &:hover {
      text-decoration: underline;
    }
  }

  &[open] summary::before {
    transform: rotate(90deg);
  }
}

.payment-info-details-content {
  margin-top: 10px;
  padding-left: 12px;
  border-left: 2px solid #e0ebe9;

  p {
    font-size: 0.95rem;
    color: $text-medium;
    margin: 0 0 6px 0;
  }

  ul {
    margin: 0 0 0 16px;
    padding: 0;
    font-size: 0.95rem;
    color: $text-medium;
    list-style-type: disc;

    li {
      margin-bottom: 4px;
      line-height: 1.4;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

// Policy Section Styles
.policy-section {
  background: $bg-white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid $border-light;
}

.policy-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.policy-icon {
  color: $text-medium;
}

.policy-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: $text-dark;
}

.policy-text {
  font-size: 0.9rem;
  color: $text-dark;
  margin: 0 0 4px 0;
  padding-left: 28px;
  line-height: 1.4;

  strong {
    font-weight: 600;
  }
}

.policy-penalty {
  font-size: 0.85rem;
  color: $text-light;
  margin: 0;
  padding-left: 28px;
  line-height: 1.4;
}

// Credit Card Hold Section Styles (step 6a)
.credit-card-hold-section {
  background: $bg-white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid $border-light;
}

.credit-card-hold-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.credit-card-hold-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: $text-dark;
}

.credit-card-hold-body {
  width: 100%;
}

.card-element-container {
  border: 1px solid $border-light;
  border-radius: 8px;
  padding: 14px;
  background: $bg-white;

  &:focus-within {
    border-color: $primary-teal;
    box-shadow: 0 0 0 1px $primary-teal;
  }
}

// Collapsible section arrow rotation
details.payment-info-section,
details.policy-section {
  summary::-webkit-details-marker {
    display: none;
  }

  &[open] summary span:last-child {
    transform: rotate(90deg);
    display: inline-block;
  }
}

// Guaranteed Spend Summary (step 7a)
.guaranteed-spend-summary {
  .spend-summary-header {
    margin-bottom: 4px;
  }

  .spend-summary-main {
    font-size: 0.95rem;
    font-weight: 500;
    color: $text-dark;
    margin-bottom: 2px;
  }

  .spend-summary-detail {
    font-size: 0.85rem;
    color: $text-medium;
    margin-bottom: 2px;
  }

  .spend-summary-total {
    font-size: 0.95rem;
    color: $text-dark;
    margin-bottom: 2px;
  }

  .spend-summary-attribution {
    font-size: 0.85rem;
    color: $text-light;
  }
}

.restaurant-key-stats {
  text-align: center;
  font-size: 13px;
  line-height: 1.6;
}

.preferred-restaurant-row {
  padding: 6px 0;
  border-bottom: 0px solid #eee;
}


.step1-content {
  padding-bottom: 0 !important;
}

.step1-flow {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step1-next-holder {
  display: flex;
  justify-content: center;
}

.reservation-type-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.reservation-type-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;

  &:active {
    transform: scale(0.98);
  }

  &--active {
    border-color: #3F998F;
    background: rgba(69, 181, 169, 0.04);
  }

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}

.reservation-type-card-radio {
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 50%;
  border: 2px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s ease;

  .reservation-type-card--active & {
    border-color: #3F998F;
  }
}

.reservation-type-radio-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: transparent;
  transition: background 0.2s ease;

  &--active {
    background: #3F998F;
  }
}

.reservation-type-card-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.reservation-type-card-label {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
}

.reservation-type-card-desc {
  font-size: 16px;
  color: #666;
  line-height: 1.3;
}

.step-next-btn {
  max-width: 340px;
  margin: 0 auto;
  font-weight: 600;
  letter-spacing: 0.02em;
}

// Review & Submit screen
.review-step-header {
  text-align: center;
  padding: 12px 0 4px;
}

.review-step-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
}

.review-step-subtitle {
  font-size: 14px;
  font-weight: 500;
  color: #666;
  display: block;
  margin-top: 4px;
}

.review-summary-card {
  background: #f8f8f9;
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
}

.review-restaurant-name {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 12px;
  line-height: 1.3;
}

.review-restaurant-market {
  font-size: 14px;
  font-weight: 400;
  color: #999;
  display: block;
  margin-top: 2px;
}

.review-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.review-detail-item {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.review-detail-label {
  font-size: 12px;
  color: #999;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.review-detail-value {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
}

.review-notes {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.review-notes-text {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

.review-spend-footer {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
  font-size: 12px;
  color: #999;
  line-height: 1.5;
}

// ─── Review Profile Card (referral-style) ───

.review-profile-card-wrapper {
  margin-top: 16px;
}

.review-profile-nudge {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: var(--ds-brand-primary-light);
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 8px;
}

.review-profile-nudge-text {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: var(--ds-brand-primary-dark);
}

.review-profile-nudge-btn {
  flex-shrink: 0;
}


.confirm-hold-card {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: $bg-white;
  border: 1px solid $border-light;
  border-radius: 12px;
  padding: 12px 20px;
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.confirm-hold-card--active {
  border-color: var(--ds-brand-primary-dark, $primary-teal);
}

.confirm-hold-checkbox {
  flex-shrink: 0;
}

.confirm-hold-checkbox :deep(.v-selection-control) {
  min-height: auto;
}

.confirm-hold-checkbox :deep(.v-selection-control__input i) {
  border-radius: 6px;
}

.confirm-hold-label {
  font-size: 14px;
  color: $text-dark;
  line-height: 1.4;
}

.submit-request-btn {
  font-weight: 700;
  letter-spacing: 0.02em;
  max-width: 320px;
  margin: 0 auto;
  display: block;
}

.submit-footer-text {
  margin-top: 12px;
  text-align: center;
  font-size: 12px;
  color: #999;
  line-height: 1.5;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
}

.preferred-restaurant-link {
  background: none;
  border: 0;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: 0;
  text-decoration: none;
}
</style>

<style lang="scss">
/* Unscoped styles to fix iOS Safari scrolling in v-combobox dropdown menu */
/* The v-menu is rendered outside the component, so scoped styles can't reach it */
.restaurant-search-menu {
  /* Use dvh (dynamic viewport height) when available - accounts for iOS keyboard */
  max-height: 50dvh !important;

  /* Fallback for browsers that don't support dvh */
  @supports not (max-height: 50dvh) {
    max-height: 50vh !important;
  }

  > .v-list {
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    overflow-y: auto !important;
    /* Ensure the list takes full available height for scrolling */
    max-height: inherit !important;
    /* Add padding at bottom of list to ensure last items are scrollable into view */
    padding-bottom: 100px !important;
  }
}

/* Ensure the add-favorite-section at the bottom is always reachable on iOS */
.restaurant-search-menu .add-favorite-section {
  /* Large bottom margin to ensure button can be scrolled fully into view */
  margin-bottom: 80px;
  padding-bottom: max(40px, env(safe-area-inset-bottom, 40px));
}

/* RM-359: RM's feedback on a request it sent back */
.approval-feedback-note {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  border-left: 3px solid var(--ds-brand-primary-dark);
  border-radius: 8px;
  background: var(--ds-brand-primary-light);
  font-size: 14px;
  line-height: 1.4;
}

.approval-feedback-note-label {
  color: var(--ds-brand-primary-dark);
  font-weight: 600;
}

.approval-feedback-note-text {
  color: #222;
  white-space: pre-line;
}
</style>
